import React, { useState, useEffect, useMemo, Component } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '@/lib/SessionContext';
import { getPageAccess } from '@/lib/AuthSession';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useClientPOs } from '@/hooks/useClientPOs';
import { useAsa } from '@/context/AsaContext';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-hot-toast';
import { formatTanggalIndo } from '@/lib/timeUtils';
import { Download, FileText, Search,
  ChevronDown, ChevronUp, Package, CreditCard, AlertTriangle, Wallet, CheckCircle, CheckCircle2,
  Database, Save, FileDown, ShieldCheck, Trash2, User, Sparkles, Edit3, Check, X, Truck, Calendar, FlaskConical, RotateCcw, Banknote, Clock
} from 'lucide-react';

let lpTimeout = null;

// ErrorBoundary to prevent accordion crashes from breaking the whole page
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Caught:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700">
          <p className="font-bold">Terjadi error saat menampilkan detail invoice.</p>
          <p className="text-[10px] mt-1 text-rose-500">{this.state.error?.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import AddOrderDialog from '@/components/production/AddOrderDialog';
import PicSelector from '@/components/shared/PicSelector';
import SuratJalanPrint from '@/components/production/SuratJalanPrint';
import { exportToPDF } from '@/utils/exportPDF';
import { generateSpkPdf } from '@/lib/spkGenerator';
import SpkPrintModal from '@/components/finance/SpkPrintModal';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Checkbox } from '@/components/ui/checkbox';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import SampleForm from '@/components/sample/SampleForm';
import { deductStockForPO, previewStockDeduction } from '@/lib/stockDeduction';
import AsaStockDeductionModal from '@/Components/shared/AsaStockDeductionModal';


const fmt = (n) => n != null ? new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : '—';
const fmtSmart = (n) => {
  if (n == null) return '—';
  const hasDecimals = Math.abs(n % 1) > 0.001;
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2
  }).format(n);
};

const terbilang = (n) => {
  const satuan = ['', 'Satu', 'Dua', 'Tiga', 'Empat', 'Lima', 'Enam', 'Tujuh', 'Delapan', 'Sembilan',
    'Sepuluh', 'Sebelas', 'Dua Belas', 'Tiga Belas', 'Empat Belas', 'Lima Belas', 'Enam Belas',
    'Tujuh Belas', 'Delapan Belas', 'Sembilan Belas'];
  const puluhan = ['', '', 'Dua Puluh', 'Tiga Puluh', 'Empat Puluh', 'Lima Puluh',
    'Enam Puluh', 'Tujuh Puluh', 'Delapan Puluh', 'Sembilan Puluh'];

  const convert = (num) => {
    if (num === 0) return '';
    if (num < 20) return satuan[num];
    if (num < 100) return puluhan[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + satuan[num % 10] : '');
    if (num < 200) return 'Seratus ' + convert(num - 100);
    if (num < 1000) return satuan[Math.floor(num / 100)] + ' Ratus ' + convert(num % 100);
    if (num < 2000) return 'Seribu ' + convert(num - 1000);
    if (num < 1000000) return convert(Math.floor(num / 1000)) + ' Ribu ' + convert(num % 1000);
    if (num < 1000000000) return convert(Math.floor(num / 1000000)) + ' Juta ' + convert(num % 1000000);
    return convert(Math.floor(num / 1000000000)) + ' Miliar ' + convert(num % 1000000000);
  };

  const result = convert(Math.round(n)).trim().replace(/\s+/g, ' ');
  return result ? result + ' Rupiah' : 'Nol Rupiah';
};

const SEED_POS = [
  {
    id: 'PO-2026-0515',
    varianLabel: 'Glacier Vanilla 100ml (2000 pcs)',
    date: '15 Mei 2026',
    statusProduksi: 'PROSES PRODUKSI',
    statusBayar: 'LUNAS',
    customerName: 'PT Satala Beauty / Asa',
    customerAddress: 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat 16610',
    phone: '08567215745',
    courier: 'JNE REG',
    resi: '0123082600169080',
    shippingCost: 150000,
    dpPercentage: 100,
    paymentMethod: 'Transfer BCA',
    deadline: '10 Jun 2026',
    items: [
      {
        nama: 'Glacier Vanilla 100ml',
        qty: 2000,
        hargaTotal: 25000000,
        hasImage: false
      }
    ],
    products: [
      {
        nama_product: 'Glacier Vanilla 100ml',
        qty: 2000,
        botol_nama: 'Botol Lotion Pump 100ml',
        botol_harga_beli: 4000,
        botol_harga: 7000,
        botol_id: 'botol-lotion-100',
        tutup_nama: null,
        tutup_harga_beli: 0,
        tutup_harga: 0,
        tutup_id: null,
        spray_nama: null,
        spray_harga_beli: 0,
        spray_harga: 0,
        spray_id: null,
        ukuran_digunakan: 'aktual',
        botol_ukuran_aktual: 100,
        botol_ukuran_label: 100,
        botol_dari_client: false,
        tutup_dari_client: false,
        spray_dari_client: false,
        racikan: [
          { kategori: 'Bibit', bahan_nama: 'Vanilla Extract Premium', persentase: 5, satuan: 'kg', harga_jual_satuan: 900000, hpp_bahan: 4500, volume_ml: 10000 },
          { kategori: 'Pelarut', bahan_nama: 'Lotion Base Standard', persentase: 95, satuan: 'kg', harga_jual_satuan: 30000, hpp_bahan: 2850, volume_ml: 190000 }
        ],
        komponen_tambahan: [
          { nama: 'Stiker Label Transparan', kategori: 'Stiker', harga: 300, harga_jual: 800 }
        ],
        jasa_nominal: 1250,
        harga_jual_per_pcs: 12500,
        total_harga_jual: 25000000,
        total_hpp_product: 20000000,
        hpp_per_pcs_digunakan: 10000
      }
    ],
    legalitas: [
      { nama: 'BPOM (Per Varian)', harga_beli: 500000, harga_jual: 2500000, qty_variant: 1 },
      { nama: 'HAKI (Hak Kekayaan Intelektual)', harga_beli: 2000000, harga_jual: 3000000, qty_variant: 1 }
    ],
    timeline: [
      { status: 'Pesanan Dibuat', date: '15 Mei 2026', desc: 'Pesanan Dibuat', done: true },
      { status: 'Pesanan Dibayar', date: '15 Mei 2026', desc: 'Pembayaran Lunas', done: true },
      { status: 'Menunggu Produksi', date: '15 Mei 2026', desc: 'Masuk Antrean', done: true },
      { status: 'Proses Produksi', date: '16 Mei 2026', desc: 'Sedang Diproduksi', done: true, active: true },
      { status: 'Pesanan Dikirim', date: '-', desc: '-', done: false },
      { status: 'Pesanan Selesai', date: '-', desc: '-', done: false }
    ],
    riwayatPembayaran: [
      { jumlah: 12500000, tanggal_bayar: '15 Mei 2026', catatan: 'Pembayaran DP 50% via Transfer BCA' },
      { jumlah: 12500000, tanggal_bayar: '18 Mei 2026', catatan: 'Pelunasan 50% via Transfer BCA' }
    ],
    resiTimeline: []
  },
  {
    id: 'PO-2026-0510',
    varianLabel: 'Sun Zest 20ml (1000 pcs)',
    date: '10 Mei 2026',
    statusProduksi: 'MENUNGGU PRODUKSI',
    statusBayar: 'DP 50%',
    customerName: 'PT Cielmora Beauty / Asa',
    customerAddress: 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat 16610',
    phone: '08567215745',
    courier: 'JNE REG',
    resi: 'MENUNGGU PICKUP',
    shippingCost: 80000,
    dpPercentage: 50,
    paymentMethod: 'Transfer BCA',
    deadline: '30 Mei 2026',
    items: [
      {
        nama: 'Sun Zest 20ml',
        qty: 1000,
        hargaTotal: 20000000,
        hasImage: false
      }
    ],
    products: [
      {
        nama_product: 'Sun Zest 20ml',
        qty: 1000,
        botol_nama: 'Botol Kaca Serum 20ml',
        botol_harga_beli: 1500,
        botol_harga: 3000,
        botol_id: 'botol-kaca-20',
        tutup_nama: 'Pipet Gold Serum',
        tutup_harga_beli: 1000,
        tutup_harga: 2000,
        tutup_id: 'tutup-pipet-gold',
        spray_nama: null,
        spray_harga_beli: 0,
        spray_harga: 0,
        spray_id: null,
        ukuran_digunakan: 'aktual',
        botol_ukuran_aktual: 20,
        botol_ukuran_label: 20,
        racikan: [
          { kategori: 'Bibit', bahan_nama: 'Orange Zest Fragrance', persentase: 15, satuan: 'kg', harga_jual_satuan: 1200000, hpp_bahan: 1800, volume_ml: 3000 },
          { kategori: 'Pelarut', bahan_nama: 'Aqua Des', persentase: 85, satuan: 'liter', harga_jual_satuan: 15000, hpp_bahan: 15, volume_ml: 17000 }
        ],
        komponen_tambahan: [
          { nama: 'Box Premium Gold Foil', kategori: 'Box Kemasan', harga: 1500, harga_jual: 3500 },
          { nama: 'Stiker Hologram Anti-Palsu', kategori: 'Stiker', harga: 200, harga_jual: 500 }
        ],
        jasa_nominal: 2000,
        harga_jual_per_pcs: 20000,
        total_harga_jual: 20000000,
        total_hpp_product: 12000000,
        hpp_per_pcs_digunakan: 12000
      }
    ],
    legalitas: [
      { nama: 'BPOM (Per Varian)', harga_beli: 500000, harga_jual: 2500000, qty_variant: 1 },
      { nama: 'HAKI (Hak Kekayaan Intelektual)', harga_beli: 2000000, harga_jual: 3000000, qty_variant: 1 }
    ],
    timeline: [
      { status: 'Pesanan Dibuat', date: '10 Mei 2026', desc: 'Pesanan Dibuat', done: true },
      { status: 'Pesanan Dibayar', date: '10 Mei 2026', desc: 'Pembayaran DP 50%', done: true, active: true },
      { status: 'Menunggu Produksi', date: '10 Mei 2026', desc: 'Masuk Antrean', done: true },
      { status: 'Proses Produksi', date: '-', desc: '-', done: false },
      { status: 'Pesanan Dikirim', date: '-', desc: '-', done: false },
      { status: 'Pesanan Selesai', date: '-', desc: '-', done: false }
    ],
    riwayatPembayaran: [
      { jumlah: 12830000, tanggal_bayar: '10 Mei 2026', catatan: 'Pembayaran DP 50% + Ongkir via Transfer BCA' }
    ],
    resiTimeline: []
  },
  {
    id: 'PO-2026-0512',
    varianLabel: 'Midnight Blue 50ml (500 pcs)',
    date: '12 Mei 2026',
    statusProduksi: 'MENUNGGU PEMBAYARAN',
    statusBayar: 'BELUM DIBAYAR',
    customerName: 'PT Cielmora Beauty / Satala',
    customerAddress: 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat 16610',
    phone: '08567215745',
    courier: 'JNE REG',
    resi: '-',
    shippingCost: 50000,
    dpPercentage: 0,
    paymentMethod: 'Transfer BCA',
    deadline: '05 Jun 2026',
    items: [
      {
        nama: 'Midnight Blue 50ml',
        qty: 500,
        hargaTotal: 15000000,
        hasImage: false
      }
    ],
    products: [
      {
        nama_product: 'Midnight Blue 50ml',
        qty: 500,
        botol_nama: 'Botol Kaca Bulat 50ml',
        botol_harga_beli: 3000,
        botol_harga: 5000,
        botol_id: 'botol-bulat-50',
        tutup_nama: 'Tutup Metalik Hitam',
        tutup_harga_beli: 1500,
        tutup_harga: 2500,
        tutup_id: 'tutup-metal-hitam',
        spray_nama: 'Spray Crimp Silver',
        spray_harga_beli: 1200,
        spray_harga: 2000,
        spray_id: 'spray-silver',
        ukuran_digunakan: 'aktual',
        botol_ukuran_aktual: 50,
        botol_ukuran_label: 50,
        racikan: [
          { kategori: 'Bibit', bahan_nama: 'Blue Ocean Fragrance', persentase: 20, satuan: 'kg', harga_jual_satuan: 1500000, hpp_bahan: 3000, volume_ml: 5000 },
          { kategori: 'Alkohol', bahan_nama: 'Ethanol 96%', persentase: 80, satuan: 'liter', harga_jual_satuan: 40000, hpp_bahan: 32, volume_ml: 20000 }
        ],
        komponen_tambahan: [
          { nama: 'Box Hardcover Exclusive', kategori: 'Box Kemasan', harga: 2500, harga_jual: 5500 }
        ],
        jasa_nominal: 3000,
        harga_jual_per_pcs: 30000,
        total_harga_jual: 15000000,
        total_hpp_product: 9000000,
        hpp_per_pcs_digunakan: 18000
      }
    ],
    legalitas: [
      { nama: 'BPOM (Per Varian)', harga_beli: 500000, harga_jual: 2500000, qty_variant: 1 }
    ],
    timeline: [
      { status: 'Pesanan Dibuat', date: '12 Mei 2026', desc: 'Pesanan Dibuat', done: true, active: true },
      { status: 'Pesanan Dibayar', date: '-', desc: '-', done: false },
      { status: 'Menunggu Produksi', date: '-', desc: '-', done: false },
      { status: 'Proses Produksi', date: '-', desc: '-', done: false },
      { status: 'Pesanan Dikirim', date: '-', desc: '-', done: false },
      { status: 'Pesanan Selesai', date: '-', desc: '-', done: false }
    ],
    riwayatPembayaran: [],
    resiTimeline: []
  }
];

import { useAsaPersonality } from '@/hooks/useAsaPersonality';
import { useAsaMessageGenerator } from '@/hooks/useAsaMessageGenerator';
import CustomDatePicker from '@/components/shared/CustomDatePicker';
import { Loader2 } from 'lucide-react';

export default function FinanceInvoices() {
  const navigate = useNavigate();
  const location = useLocation();
  const { member: currentMember, companySettings: globalCompanySettings } = useSession();
  const isMarketing = location.pathname.includes('/marketing');
  
  // Use centralized ASA personality hook
  const { sapaan: asaNickname, gaya: asaGaya } = useAsaPersonality();
  
  const queryClient = useQueryClient();
  const { accountsMap: bankAccounts } = useBankAccounts();
  const params = new URLSearchParams(location.search);
  const initialSearch = params.get('search') || '';
  const initialExpand = params.get('expand') || null;
  const [searchQuery, setSearchQuery] = useState(initialSearch);

  const [statusFilter, setStatusFilter] = useState('ALL');
  const [prosesFilter, setProsesFilter] = useState('ALL');
  const [brandFilter, setBrandFilter] = useState('ALL');
  const [picFilter, setPicFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [asaDialog, setAsaDialog] = useState(null);
  const [pendingAsaPoUpdate, setPendingAsaPoUpdate] = useState(null);
  const [expandedId, setExpandedId] = useState(initialExpand);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showSpkModal, setShowSpkModal] = useState(false);
  const [spkSelectedInvoice, setSpkSelectedInvoice] = useState(null);
  const { clientPOs = [], isLoading: loadingPOs, updatePO, deletePO } = useClientPOs();
  
  // AI Message Generator Hook
  const getFallbackText = () => {
    if (!asaDialog) return '';
    if (asaDialog.type === 'stock_deduction') {
      return `Halo ${asaNickname}! Ada pembayaran masuk Rp ${fmt(asaDialog.tambahan)}. Apakah kamu ingin memotong stok ${asaDialog.itemToDeduct} sebanyak ${asaDialog.qty} pcs secara otomatis?`;
    }
    if (asaDialog.type === 'duplicate_variant') {
      const list = asaDialog.duplicateVarian.join(', ');
      return `Halo ${asaNickname}! Varian [${list}] rupanya sudah ada di database untuk brand ${asaDialog.brandName}. Apakah Anda ingin menggantinya (overwrite) dengan yang baru dari invoice ini?`;
    }
    return '';
  };
  const picName = asaDialog?.safeInv?.rawRecord?.pic || asaDialog?.inv?.rawRecord?.pic || null;
  const { aiMessage, isAiLoading } = useAsaMessageGenerator(asaDialog, getFallbackText, picName);
  const { member } = useSession();
  const { openAsa } = useAsa();
  const [longPressedId, setLongPressedId] = useState(null);

  const { data: itemTambahanList = [] } = useQuery({
    queryKey: ['dbBahan', 'item_tambahan'],
    queryFn: () => base44.entities.ItemTambahan.list()
  });

  const { data: productionOrders = [] } = useQuery({
    queryKey: ['production-orders'],
    queryFn: () => base44.entities.ProductionOrder.list(),
  });
  const { data: fullsetList = [] } = useQuery({
    queryKey: ['fullset'],
    queryFn: () => base44.entities.Fullset.list(),
  });
  const { data: bahanList = [] } = useQuery({
    queryKey: ['bahan-cair'],
    queryFn: () => base44.entities.BahanCair.list(),
  });
  const { data: botolList = [] } = useQuery({
    queryKey: ['botol'],
    queryFn: () => base44.entities.Botol.list(),
  });
  const { data: tutupList = [] } = useQuery({
    queryKey: ['tutup'],
    queryFn: () => base44.entities.Tutup.list(),
  });
  const { data: sprayList = [] } = useQuery({
    queryKey: ['spray'],
    queryFn: () => base44.entities.Spray.list(),
  });
  // States for dynamic inline payment inputs
  const [editBayarId, setEditBayarId] = useState(null);
  const [inputBayar, setInputBayar] = useState('');
  const [catatanBayar, setCatatanBayar] = useState('');
  const [bankBayar, setBankBayar] = useState('');
  const [inputTanggalBayar, setInputTanggalBayar] = useState('');
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  
  // State Komisi
  const [showKomisiModal, setShowKomisiModal] = useState(false);
  const [komisiForm, setKomisiForm] = useState({ invId: '', invName: '', picName: '', bankAsal: 'bca', bankTujuan: '', atasNama: '', nominal: '', catatan: '', rawRecord: null, source: '' });
  const [isSavingKomisi, setIsSavingKomisi] = useState(false);
  
  const [isAdding, setIsAdding] = useState(false);
  const [showSuratJalanFor, setShowSuratJalanFor] = useState(null);
  const [showSuratJalanShipment, setShowSuratJalanShipment] = useState(null);
  const [pendingAsaSuratJalan, setPendingAsaSuratJalan] = useState(null);
  const [editPicId, setEditPicId] = useState(null);
  const [inputPic, setInputPic] = useState('');
  const [editInvoiceNameId, setEditInvoiceNameId] = useState(null);
  const [inputInvoiceName, setInputInvoiceName] = useState('');

  const [simpanDbRecord, setSimpanDbRecord] = useState(null);
  const [sampleFormData, setSampleFormData] = useState(null);
  const [tempoModal, setTempoModal] = useState(null);
  const [asaPrintModal, setAsaPrintModal] = useState(null);
  const [asaPrintNote, setAsaPrintNote] = useState('');
  const [asaPrintDp, setAsaPrintDp] = useState('');
  const [asaStockModal, setAsaStockModal] = useState(null);

  const combinedInventory = useMemo(() => [
    ...botolList.map(i => ({ id: `botol-${i.id}`, _stokId: i.id, _entity: 'Botol', total_stock: i.stok ?? 0, item_name: i.nama })),
    ...tutupList.map(i => ({ id: `tutup-${i.id}`, _stokId: i.id, _entity: 'Tutup', total_stock: i.stok ?? 0, item_name: i.nama })),
    ...sprayList.map(i => ({ id: `spray-${i.id}`, _stokId: i.id, _entity: 'Spray', total_stock: i.stok ?? 0, item_name: i.nama })),
    ...bahanList.map(i => ({ id: `bahan-${i.id}`, _stokId: i.id, _entity: 'BahanCair', total_stock: i.stok ?? 0, item_name: i.nama })),
    ...fullsetList.map(i => ({ id: `fullset-${i.id}`, _stokId: i.id, _entity: 'Fullset', total_stock: i.stok ?? 0, item_name: i.nama })),
  ], [botolList, tutupList, sprayList, bahanList, fullsetList]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Search State for Brand Filter
  const [brandSearchQuery, setBrandSearchQuery] = useState('');
  
  // Scroll to Top
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection,
    selectedCount,
    totalCount
  } = useBatchSelection(clientPOs, po => po.id);

  const batchDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      for (const id of ids) {
        await base44.entities.ClientPO.delete(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-pos'] });
      toast.success(`${selectedCount} invoice berhasil dihapus`);
      clearSelection();
    }
  });

  const updatePOMutation = useMutation({
    mutationFn: async (data) => {
      let idToUpdate = data.id;
      if (!idToUpdate) {
        // Try to find by order_number if id is not in data
        const po = productionOrders?.find(p => p.order_number === data.order_number);
        if (po) idToUpdate = po.id;
      }
      if (!idToUpdate) throw new Error('Data ProductionOrder dengan ID undefined tidak ditemukan di database.');
      return await base44.entities.ProductionOrder.update(idToUpdate, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['client-pos'] });
    }
  });

  const { data: companySettingsList } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => base44.entities.CompanySettings.list(),
  });
  const localSettings = companySettingsList?.[0] || {};
  let companySettings = (localSettings.logo_url || localSettings.stempel_url || localSettings.ttd_url) ? { ...localSettings } : { ...globalCompanySettings, ...localSettings };
  companySettings.logo_url = companySettings.logo_url || '/logo-invoice.png';
  companySettings.stempel_url = companySettings.stempel_url || '/stempel-invoice.png';
  companySettings.ttd_url = companySettings.ttd_url || '/ttd-invoice.png';

  // Mutation for saving to Brand DB
  const simpanKeBrandDbMut = useMutation({
    mutationFn: async (inv) => {
      const brandName = inv.parsedBrandName || (inv.client || 'Satala').split(' / ')[0].trim();
      
      const promises = (inv.products || []).map(async p => {
        const varianName = p.nama_product || 'Varian Default';
        
        const ukuranKonsisten = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || 30;
        
        // Handle overwrite deletion if user agreed
        if (inv._duplicateAction === 'overwrite') {
          if (p.racikan && p.racikan.length > 0) {
            const oldRacikanList = await base44.entities.RacikanDatabase.filter({ nama_brand: brandName, nama_varian: varianName, ukuran_ml: ukuranKonsisten });
            for (const old of oldRacikanList) await base44.entities.RacikanDatabase.hardDelete(old.id);
          }
          
          const oldBahanList = await base44.entities.DatabaseBahan.filter({ nama_brand: brandName, nama_varian: varianName, botol_ukuran_label_ml: ukuranKonsisten });
          for (const old of oldBahanList) await base44.entities.DatabaseBahan.hardDelete(old.id);
        }

        const tasks = [];
        
        // Simpan Racikan — gunakan ukuran label agar konsisten dengan kemasan
        if (p.racikan && p.racikan.length > 0) {
          const racikanPayload = {
            nama_brand: brandName,
            nama_varian: varianName,
            ukuran_ml: ukuranKonsisten,
            catatan: `Diambil otomatis dari invoice ${inv.id}`,
            racikan: p.racikan.map(r => {
              const bMatch = bahanList.find(x => (x.nama || '').trim().toLowerCase() === (r.bahan_nama || r.nama_bahan || '').trim().toLowerCase());
              return {
                kategori_bahan: r.kategori || r.kategori_bahan || 'Bibit',
                nama_bahan: r.bahan_nama || r.nama_bahan || r.nama || '',
                vendor: r.vendor || bMatch?.vendor || (r.dari_client ? 'Client' : ''),
                grade: r.grade || bMatch?.grade || '',
                persentase: r.persentase || 0,
                volume_ml: r.volume_ml || parseFloat((((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * ukuranKonsisten).toFixed(2))
              };
            })
          };
          tasks.push(base44.entities.RacikanDatabase.create(racikanPayload));
        }

        // Simpan Bahan Kemasan
        const bahanPayload = {
          nama_brand: brandName,
          nama_varian: varianName,
          botol_nama: p.botol_nama || (p.botol_dari_client ? 'Botol Dari Client' : ''),
          tutup_nama: p.tutup_nama || (p.tutup_dari_client ? 'Tutup Dari Client' : ''),
          spray_nama: p.spray_nama || (p.spray_dari_client ? 'Spray Dari Client' : ''),
          botol_ukuran_label_ml: ukuranKonsisten,
          botol_ukuran_aktual_ml: p.botol_ukuran_aktual || p.botol_ukuran_aktual_ml || p.ukuran_botol_ml || ukuranKonsisten,
          is_fullset: p.is_fullset || false,
          fullset_nama: p.fullset_nama || (p.is_fullset ? 'Kemasan Fullset' : ''),
          custom_jasa: p.jasa_nominal || p.final_jasa_nominal || null,
          custom_harga_jual: p.harga_jual_per_pcs || p.final_harga_jual_per_pcs || (p.total_harga_jual ? p.total_harga_jual / (p.qty || 1) : null) || null,
          status: 'aktif',
          catatan: `Diambil otomatis dari invoice ${inv.id}`,
          item_tambahan: p.komponen_tambahan || []
        };
        tasks.push(base44.entities.DatabaseBahan.create(bahanPayload));
        
        // Handle Discovery Set items properly
        if ((p.is_discovery_set || p.nama_product === 'Discovery Set') && p.items && p.items.length > 0) {
          for (const it of p.items) {
            const itemVarianName = `${varianName} || ${it.nama_variant || 'Unnamed'}`;
            const regexMatch = it.nama_variant?.match(/\((\d+)ml\)/);
            const fallbackMl = regexMatch ? parseFloat(regexMatch[1]) : 0;
            const itemUkuran = it.custom_ukuran_aktual_ml !== undefined ? it.custom_ukuran_aktual_ml : (it.botol_ukuran_aktual || it.botol_ukuran_label || it.botol_ukuran_client || it.ukuran_botol_ml || fallbackMl);

            if (inv._duplicateAction === 'overwrite') {
              if (it.racikan && it.racikan.length > 0) {
                const oldRacikanList = await base44.entities.RacikanDatabase.filter({ nama_brand: brandName, nama_varian: itemVarianName, ukuran_ml: itemUkuran });
                for (const old of oldRacikanList) await base44.entities.RacikanDatabase.hardDelete(old.id);
              }
              const oldBahanList = await base44.entities.DatabaseBahan.filter({ nama_brand: brandName, nama_varian: itemVarianName, botol_ukuran_label_ml: itemUkuran });
              for (const old of oldBahanList) await base44.entities.DatabaseBahan.hardDelete(old.id);
            }

            if (it.racikan && it.racikan.length > 0) {
              const itemRacikanPayload = {
                nama_brand: brandName,
                nama_varian: itemVarianName,
                ukuran_ml: itemUkuran,
                catatan: `Diambil otomatis dari invoice ${inv.id} (Sub-variant Discovery Set)`,
                racikan: it.racikan.map(r => {
                  const bMatch = bahanList.find(x => (x.nama || '').trim().toLowerCase() === (r.bahan_nama || r.nama_bahan || '').trim().toLowerCase());
                  return {
                    kategori_bahan: r.kategori || r.kategori_bahan || 'Bibit',
                    nama_bahan: r.bahan_nama || r.nama_bahan || r.nama || '',
                    vendor: r.vendor || bMatch?.vendor || (r.dari_client ? 'Client' : ''),
                    grade: r.grade || bMatch?.grade || '',
                    persentase: r.persentase || 0,
                    volume_ml: r.volume_ml || parseFloat((((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * itemUkuran).toFixed(2))
                  };
                })
              };
              tasks.push(base44.entities.RacikanDatabase.create(itemRacikanPayload));
            }

            const itemBahanPayload = {
              nama_brand: brandName,
              nama_varian: itemVarianName,
              botol_nama: it.botol_nama || (it.botol_dari_client ? 'Botol Dari Client' : ''),
              tutup_nama: it.tutup_nama || (it.tutup_dari_client ? 'Tutup Dari Client' : ''),
              spray_nama: it.spray_nama || (it.spray_dari_client ? 'Spray Dari Client' : ''),
              botol_ukuran_label_ml: itemUkuran,
              botol_ukuran_aktual_ml: itemUkuran,
              is_fullset: it.is_fullset || false,
              fullset_nama: it.fullset_nama || (it.is_fullset ? 'Kemasan Fullset' : ''),
              status: 'aktif',
              catatan: `Diambil otomatis dari invoice ${inv.id} (Sub-variant Discovery Set)`,
              item_tambahan: []
            };
            tasks.push(base44.entities.DatabaseBahan.create(itemBahanPayload));
          }
        }

        return Promise.all(tasks);
      });
      return Promise.all(promises);
    },
    onSuccess: (res, inv) => {
      // Apabila PIC diisi di invoice, simpan ke Database Brand juga
      if (inv.rawRecord?.pic) {
        const brandName = inv.parsedBrandName || (inv.client || 'Satala').split(' / ')[0].trim();
        const exist = companySettingsList?.[0] || {};
        // Opsional: integrasi PIC ke tabel Database Brand
      }
      queryClient.invalidateQueries({ queryKey: ['racikan-database'] });
      queryClient.invalidateQueries({ queryKey: ['database-bahan'] });
      
      const savedCount = (inv.products || []).length;
      const skippedMsg = inv._duplicateAction === 'skip' ? ' (beberapa varian duplikat dilewati)' : '';
      const overwriteMsg = inv._duplicateAction === 'overwrite' ? ' (varian lama ditimpa)' : '';
      
      toast.success(`${savedCount} varian Formula dan Kemasan berhasil diajukan ke Database Brand!${skippedMsg}${overwriteMsg}`);
    },
    onError: (err) => {
      toast.error('Gagal menyimpan ke Database Brand: ' + err.message);
    }
  });

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm(`Yakin ingin menghapus invoice ${id}? Ini akan membatalkan PO terkait.`)) return;
    try {
      await deletePO(id);
      toast.success(`Invoice ${id} berhasil dihapus dari database.`);
    } catch(e) {
      toast.error('Gagal menghapus invoice.');
    }
  };

  useEffect(() => {
    if (!currentMember) return;
    const accessKey = isMarketing ? 'marketing_invoices' : 'invoice_po';
    const access = getPageAccess(currentMember, accessKey);
    if (access === 'none') {
      navigate('/office');
    }
  }, [currentMember, navigate, isMarketing]);



  // Load HPP logs
  const { data: riwayatList = [], refetch: refetchRiwayat } = useQuery({
    queryKey: ['riwayat-produksi'],
    queryFn: () => base44.entities.RiwayatProduksi.list('-created_date'),
  });

  const updateHppMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.RiwayatProduksi.update(id, data),
    onSuccess: () => {
      refetchRiwayat();
      toast.success('Pembayaran HPP berhasil dicatat!');
    }
  });

  const createPOMutation = useMutation({
    mutationFn: (data) => base44.entities.ProductionOrder.create({ ...data, status: data.status || 'pending' }),
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      // Update PO status to pending in Supabase
      const poId = variables.order_number;
      if (poId) {
        const poToUpdate = clientPOs.find(o => o.id === poId);
        if (poToUpdate) {
          const updatedTimeline = [...(poToUpdate.timeline || [])];
          const tIdx = updatedTimeline.findIndex(t => t.status === 'Proses Produksi' || t.status === 'PO Diterima');
          if (tIdx !== -1) {
            updatedTimeline[tIdx].done = true;
            updatedTimeline[tIdx].date = new Date().toLocaleDateString('id-ID');
            updatedTimeline[tIdx].active = true;
          }
          updatePO(poId, { statusProduksi: 'PENDING', timeline: updatedTimeline });
        }
      }
      toast.success('PO berhasil dibuat dan masuk ke Antrian Produksi (Pending)!');
    },
    onError: (err) => {
      toast.error('Gagal membuat PO Produksi: ' + err.message);
    }
  });

  // Helper to clean and deduplicate variant names from strings like "Cuddle (3 pcs), Cuddle (10 pcs)"
  const getUniqueProduk = (po) => {
    let rawLabel = '';
    if (po.products && po.products.length > 0) {
      rawLabel = po.products.map(p => p.nama_product || 'Varian').join(', ');
    } else if (po.items && po.items.length > 0) {
      rawLabel = po.items.map(i => i.nama || i.nama_product || 'Varian').join(', ');
    } else if (po.varianLabel) {
      rawLabel = po.varianLabel;
    } else {
      return '-';
    }

    const stripped = rawLabel.replace(/\s*\(\d+\s*pcs\)/gi, '');
    const parts = stripped.split(',').map(s => s.trim()).filter(Boolean);
    return Array.from(new Set(parts)).join(', ');
  };

  // Merge client POs + HPP logs
  const invoices = useMemo(() => {
    const list = [];

    // Client POs
    clientPOs.forEach(po => {
      if ((po.poMode === 'sph' || po.isSPH) && po.statusBayar !== 'TEMPO' && !po.acc_date) return;
      // Allow all invoices to appear, including unpaid ones (e.g. manually created by Finance)
      const shipping = po.shippingCost || 0;
      const prodJual = (po.products || po.items || []).reduce((s, p) => s + (p.total_harga_jual || p.subtotal || 0), 0);
      const legJual = (po.legalitas || []).reduce((s, l) => s + (l.harga_jual * (l.qty_variant || 1)), 0);
      const nominalPPN = po.ppn_mode === 'exclude' ? ((prodJual + legJual) * (parseFloat(po.ppn_percentage) || 0) / 100) : 0;
      const totalJual = prodJual + legJual + shipping; // Termasuk biaya tambahan/ongkir
      const totalTagihan = totalJual + nominalPPN; // Tagihan ke klien

      const prodHpp = (po.products || []).reduce((s, p) => {
        if (p.total_hpp_product !== undefined && Number(p.total_hpp_product) > 0) {
          return s + Number(p.total_hpp_product);
        }
        const qty = p.qty || 1;
        if (p.hpp_satuan !== undefined) {
          return s + (Number(p.hpp_satuan) * qty);
        }
        const modalPackaging = (p.botol_harga_beli || 0) + (p.tutup_harga_beli || 0) + (p.spray_harga_beli || 0);
        const volume = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || p.volume_ml || 0;
        const modalRacikan = (p.racikan || []).reduce((rs, r) => {
          const costPerUnit = r.hpp_bahan || r.harga_per_satuan || 0;
          const volPerPcs = volume * ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100);
          return rs + volPerPcs * (costPerUnit / 1000);
        }, 0);
        const modalKomponen = (p.komponen_tambahan || []).reduce((ks, k) => ks + (k.harga || 0), 0);
        const jasaNominal = p.jasa_nominal || p.final_jasa_nominal || 0;
        const customHargaBeli = p.hargaBeli || p.harga_beli || 0;
        const hppPerPcs = modalPackaging + modalRacikan + modalKomponen + jasaNominal + customHargaBeli;
        return s + (hppPerPcs * qty);
      }, 0);
      const legHpp = (po.legalitas || []).reduce((s, l) => s + ((l.harga_beli || 0) * (l.qty_variant || 1)), 0);
      const komisiTotal = (po.komisiList || []).reduce((s, k) => s + (Number(k.nominal) || 0), 0);
      const totalHpp = prodHpp + legHpp + komisiTotal;
      const validPayments = (po.riwayatPembayaran || []).filter(r => r.status_konfirmasi !== 'Menunggu Konfirmasi Finance');
      const pendingPayments = (po.riwayatPembayaran || []).filter(r => r.status_konfirmasi === 'Menunggu Konfirmasi Finance');
      const totalBayar = validPayments.reduce((s, r) => s + (r.jumlah || 0), 0);
      
      const normalizeCode = (str) => String(str || '').replace(/^(PO-|INV-|SPH-)/i, '').trim().toLowerCase();

      const realPO = productionOrders.find(p => 
        p.order_number === po.id || 
        (p.notes && p.notes.includes(po.id)) ||
        (p.order_number && po.id && String(p.order_number).trim() === String(po.id).trim()) ||
        (po.id && p.order_number && normalizeCode(p.order_number) === normalizeCode(po.id))
      );

      // Map ProductionOrder.status to user-friendly display label
      const mapProdStatus = (rawStatus) => {
        const map = {
          'pending': 'MENUNGGU PRODUKSI',
          'sedang_po_item': 'SEDANG PO ITEM',
          'antrian_produksi': 'ANTRIAN PRODUKSI',
          'ready_produksi': 'READY PRODUKSI',
          'diracik': 'PROSES PRODUKSI - MIXING',
          'menunggu_qc_mixing': 'MENUNGGU VALIDASI QC - MIXING',
          'maserasi': 'PROSES PRODUKSI - MASERASI',
          'menunggu_filling': 'MENUNGGU FILLING',
          'filling': 'PROSES PRODUKSI - FILLING',
          'menunggu_qc_filling': 'MENUNGGU VALIDASI QC - FILLING',
          'menunggu_packing': 'MENUNGGU PENGEMASAN',
          'packing': 'PROSES PRODUKSI - PENGEMASAN',
          'waiting_filling': 'PENGEMASAN SELESAI (MENUNGGU FILLING)',
          'menunggu_qc_packing': 'MENUNGGU VALIDASI QC - PENGEMASAN',
          'siap_kirim': 'SIAP KIRIM',
          'dalam_pengiriman': 'SEDANG DALAM PENGIRIMAN',
          'shipping': 'SEDANG DALAM PENGIRIMAN',
          'done': 'PESANAN SELESAI',
          'in_progress': 'PROSES PRODUKSI',
          'ready': 'READY PRODUKSI',
        };
        return map[rawStatus] || rawStatus.replace(/_/g, ' ').toUpperCase();
      };

      let dynamicStatusProduksi = po.statusProduksi || 'BELUM NAIK PRODUKSI';
      if (dynamicStatusProduksi === 'MENUNGGU PRODUKSI' || dynamicStatusProduksi === 'MENUNGGU PEMBAYARAN') {
        dynamicStatusProduksi = 'BELUM NAIK PRODUKSI';
      }
      
      if (realPO) {
        // PO exists in production — sync the real status
        dynamicStatusProduksi = mapProdStatus(realPO.status);
      }

      const isLunas = (totalTagihan > 0 && totalBayar >= totalTagihan - 100) || 
                      (totalJual > 0 && totalBayar >= totalJual - 100) ||
                      po.statusBayar === 'LUNAS' || 
                      po.statusBayar === 'Sudah Lunas' || 
                      (totalTagihan > 0 && Math.round(totalTagihan - totalBayar) <= 0);

      let dynamicStatusBayar = 'BELUM BAYAR';
      if (pendingPayments.length > 0 && !isLunas) {
        dynamicStatusBayar = 'MENUNGGU KONFIRMASI FINANCE';
      } else if (isLunas) {
        dynamicStatusBayar = 'LUNAS';
      } else if (totalTagihan > 0 && totalBayar > 0 && totalBayar < totalTagihan) {
        dynamicStatusBayar = 'BELUM LUNAS';
      } else if (totalTagihan > 0 && totalBayar <= 0) {
        dynamicStatusBayar = 'BELUM BAYAR';
      } else if (po.statusBayar) {
        dynamicStatusBayar = po.statusBayar;
      } else {
        dynamicStatusBayar = 'BELUM BAYAR';
      }

      const effectiveId = (po.id && String(po.id).toUpperCase().startsWith('SPH-') && (po.poMode !== 'sph' || po.acc_date || !po.isSPH))
        ? po.id.replace(/^SPH-/i, 'INV-')
        : po.id;

      list.push({
        id: effectiveId,
        source: 'client_po',
        client: po.customerName || 'Client Kustom',
        tanggal: po.date || '-',
        status: dynamicStatusBayar,
        statusProduksi: dynamicStatusProduksi,
        produk: getUniqueProduk(po),
        totalHpp,
        totalJual,
        totalTagihan,
        nominalPPN,
        jumlahDibayar: totalBayar,
        products: po.products || [],
        legalitas: po.legalitas || [],
        riwayatPembayaran: po.riwayatPembayaran || [],
        shippingCost: shipping,
        shippingCostLabel: po.shippingCostLabel || 'Biaya Pengiriman',
        deadline: po.deadline || '',
        rawRecord: po,
        isSample: po.poMode === 'sample' || po.isSample === true,
        has_pending_po_update: po.has_pending_po_update || false,
        productionOrder: realPO
      });
    });

    // HPP records
    riwayatList.forEach(r => {
      const komisiTotal = (r.komisiList || []).reduce((s, k) => s + (Number(k.nominal) || 0), 0);
      const prodJual = r.total_harga_jual_semua || 0;
      const nominalPPN = r.ppn_mode === 'exclude' ? (prodJual * (parseFloat(r.ppn_percentage) || 0) / 100) : 0;
      const totalJual = prodJual; // Murni Omset
      const totalTagihan = totalJual + nominalPPN;

      const totalHpp = (r.total_hpp_semua || 0) + komisiTotal;
      const validPayments = (r.riwayat_pembayaran || []).filter(p => p.status_konfirmasi !== 'Menunggu Konfirmasi Finance');
      const pendingPayments = (r.riwayat_pembayaran || []).filter(p => p.status_konfirmasi === 'Menunggu Konfirmasi Finance');
      const jumlahDibayar = validPayments.reduce((s, p) => s + (p.jumlah || 0), 0);

      const isLunasHpp = (totalTagihan > 0 && jumlahDibayar >= totalTagihan - 100) || 
                         (totalJual > 0 && jumlahDibayar >= totalJual - 100) ||
                         r.status_bayar === 'Sudah Lunas' || 
                         r.status_bayar === 'LUNAS' || 
                         (totalTagihan > 0 && Math.round(totalTagihan - jumlahDibayar) <= 0);

      let statusBayar = 'BELUM BAYAR';
      if (pendingPayments.length > 0 && !isLunasHpp) {
        statusBayar = 'MENUNGGU KONFIRMASI FINANCE';
      } else if (isLunasHpp) {
        statusBayar = 'LUNAS';
      } else if (totalTagihan > 0 && jumlahDibayar > 0 && jumlahDibayar < totalTagihan) {
        statusBayar = 'BELUM LUNAS';
      } else if (totalTagihan > 0 && jumlahDibayar <= 0) {
        statusBayar = 'BELUM BAYAR';
      } else if (r.status_bayar) {
        statusBayar = r.status_bayar;
      } else {
        statusBayar = 'BELUM BAYAR';
      }

      list.push({
        id: `HPP-${r.id}`,
        source: 'hpp',
        client: r.nama_brand || '-',
        tanggal: r.tanggal || '-',
        status: statusBayar === 'Sudah Lunas' ? 'LUNAS' : statusBayar === 'Belum Lunas' ? 'BELUM LUNAS' : 'BELUM BAYAR',
        produk: getUniqueProduk(r),
        totalHpp,
        totalJual,
        totalTagihan,
        nominalPPN,
        jumlahDibayar: jumlahDibayar,
        products: r.products || [],
        legalitas: r.legalitas || [],
        riwayatPembayaran: r.riwayat_pembayaran || [],
        rawRecord: r,
        isSample: r.isSample || false
      });
    });

    // Filter out Custom Invoices and Sample Invoices from the main PO page
    const filteredList = list.filter(inv => {
      const isCustom = inv.rawRecord?.poMode === 'custom_finance' || inv.rawRecord?.is_custom_finance;
      const isSample = inv.isSample;
      const isSph = inv.rawRecord?.poMode === 'sph';
      return !isCustom && !isSample && !isSph;
    });

    return filteredList;
  }, [clientPOs, riwayatList, productionOrders]);

  // Filtered invoices (pre-brand filter)
  const preFiltered = useMemo(() => {
    return invoices.filter(inv => {
      const qLower = searchQuery.toLowerCase().trim();
      const qInvVariant = qLower.replace(/^po-/i, 'inv-');
      const qPoVariant = qLower.replace(/^inv-/i, 'po-');
      const isCustomSearch = qLower === 'custom' && inv.rawRecord?.is_custom_finance;

      let bankName = '';
      const pm = (inv.rawRecord?.paymentMethod || '').toLowerCase();
      if (pm.includes('bca') || pm.includes('apa saja bisa')) bankName = 'asb';
      else if (pm.includes('mandiri') || pm.includes('cuan jalan terus')) bankName = 'cjt';
      else if (pm.includes('bni') || pm.includes('essential')) bankName = 'sde';

      const matchSearch = isCustomSearch || 
        String(inv.client || '').toLowerCase().includes(qLower) ||
        String(inv.produk || '').toLowerCase().includes(qLower) ||
        String(inv.tanggal || '').toLowerCase().includes(qLower) ||
        String(inv.id || '').toLowerCase().includes(qLower) ||
        String(inv.id || '').toLowerCase().includes(qInvVariant) ||
        String(inv.id || '').toLowerCase().includes(qPoVariant) ||
        String(inv.rawRecord?.id || '').toLowerCase().includes(qLower) ||
        String(inv.rawRecord?.id || '').toLowerCase().includes(qInvVariant) ||
        String(inv.rawRecord?.order_number || '').toLowerCase().includes(qLower) ||
        String(inv.rawRecord?.po_number || '').toLowerCase().includes(qLower) ||
        bankName.includes(qLower);
      
      const matchStatus = statusFilter === 'ALL' || 
        inv.status === statusFilter || 
        (statusFilter === 'BELUM BAYAR' && inv.status === 'BELUM DIBAYAR') ||
        (statusFilter === 'BELUM LUNAS' && inv.status !== 'LUNAS' && inv.status !== 'BELUM DIBAYAR' && inv.jumlahDibayar > 0) ||
        (statusFilter === 'TEMPO' && (inv.rawRecord?.paymentTerms === 'tempo' || inv.rawRecord?.is_tempo || inv.statusBayar === 'TEMPO'));

      let matchProses = true;
      if (prosesFilter !== 'ALL') {
        if (prosesFilter === 'BELUM_NAIK') {
          matchProses = ['BELUM NAIK PRODUKSI', 'MENUNGGU PRODUKSI', 'SEDANG PO ITEM', 'ANTRIAN PRODUKSI'].includes(inv.statusProduksi);
        } else if (prosesFilter === 'PROSES') {
          matchProses = ['PROSES PRODUKSI', 'SIAP PRODUKSI', 'PROSES PRODUKSI - MASERASI', 'PROSES PRODUKSI - FILLING', 'MENUNGGU PENGEMASAN', 'PROSES PENGEMASAN', 'QC'].includes(inv.statusProduksi);
        } else if (prosesFilter === 'DIKIRIM') {
          matchProses = ['SIAP KIRIM', 'DALAM PENGIRIMAN', 'MENUNGGU PICKUP'].includes(inv.statusProduksi);
        } else if (prosesFilter === 'SELESAI') {
          matchProses = ['PESANAN SELESAI', 'SELESAI'].includes(inv.statusProduksi);
        }
      }

      // Date range filter
      let matchDate = true;
      if (dateFrom || dateTo) {
        const poMatch = productionOrders.find(po => String(po.order_number) === String(inv.id));
        const dStr = inv.rawRecord?.surat_jalan_date || poMatch?.shipping_date || poMatch?.shipped_at || inv.tanggal || inv.rawRecord?.date || inv.rawRecord?.tanggal || inv.rawRecord?.created_date || inv.rawRecord?.created_at || '';
        let invDate = new Date(dStr);
        if (isNaN(invDate.getTime()) && typeof dStr === 'string') {
          const str = dStr.toLowerCase();
          const months = { 'jan':0,'feb':1,'mar':2,'apr':3,'mei':4,'may':4,'jun':5,'jul':6,'agu':7,'aug':7,'sep':8,'okt':9,'oct':9,'nov':10,'des':11,'dec':11 };
          const parts = str.split(' ');
          if (parts.length >= 3) {
            const day = parseInt(parts[0]);
            const monthStr = Object.keys(months).find(m => parts[1].includes(m));
            const month = monthStr ? months[monthStr] : 0;
            const year = parseInt(parts[2]);
            invDate = new Date(year, month, day);
          }
        }
        if (!isNaN(invDate.getTime())) {
          const invDateOnly = new Date(invDate.getFullYear(), invDate.getMonth(), invDate.getDate());
          if (dateFrom) {
            const from = new Date(dateFrom);
            if (invDateOnly < from) matchDate = false;
          }
          if (dateTo) {
            const to = new Date(dateTo);
            if (invDateOnly > to) matchDate = false;
          }
        } else {
          matchDate = false; // Can't parse date, hide it when date filter is active
        }
      }

      let matchPortal = true;
      if (isMarketing) {
        // Marketing: hilangkan HANYA JIKA produksinya sudah SELESAI DAN pembayarannya sudah LUNAS
        if (inv.statusProduksi === 'PESANAN SELESAI' && inv.status === 'LUNAS') matchPortal = false;
        
        // Marketing: HANYA tampilkan invoice milik PIC tersebut (kecuali Direktur/Developer/Admin dll bisa lihat semua)
        const role = (currentMember?.role || '').toLowerCase();
        const position = (currentMember?.position || currentMember?.jabatan || '').toLowerCase();
        const isOwner = ['developer', 'owner', 'admin', 'manager', 'finance', 'direktur'].some(r => role.includes(r) || position.includes(r));
        if (!isOwner) {
          const picName = (inv.rawRecord?.pic || '').toLowerCase();
          const memberName = (currentMember?.nama || currentMember?.full_name || '').toLowerCase();
          if (memberName && !picName.includes(memberName) && !memberName.includes(picName)) {
            matchPortal = false;
          }
        }
      } else {
        // Finance Portal: tampilkan semua invoice
      }

      return matchSearch && matchStatus && matchProses && matchDate && matchPortal;
    }).sort((a, b) => {
      const getCreationTime = (inv) => {
        const cStr = inv.rawRecord?.created_at || inv.rawRecord?.created_date || inv.tanggal || inv.rawRecord?.date || inv.rawRecord?.tanggal || '';
        let d = new Date(cStr);
        if (isNaN(d.getTime()) && typeof cStr === 'string') {
          const str = cStr.toLowerCase();
          const months = { 'jan':0,'feb':1,'mar':2,'apr':3,'mei':4,'may':4,'jun':5,'jul':6,'agu':7,'aug':7,'sep':8,'okt':9,'oct':9,'nov':10,'des':11,'dec':11 };
          const parts = str.split(' ');
          if (parts.length >= 3) {
             const day = parseInt(parts[0]);
             const monthStr = Object.keys(months).find(m => parts[1].includes(m));
             const month = monthStr ? months[monthStr] : 0;
             const year = parseInt(parts[2]);
             d = new Date(year, month, day);
          }
        }
        return isNaN(d.getTime()) ? 0 : d.getTime();
      };
      
      const creationDiff = getCreationTime(b) - getCreationTime(a);
      if (creationDiff !== 0) return creationDiff;
      
      // Jika tanggal dan waktu pembuatan sama persis, urutkan berdasarkan ID (Descending)
      const idA = String(a.id || '');
      const idB = String(b.id || '');
      return idB.localeCompare(idA);
    });
  }, [invoices, searchQuery, statusFilter, prosesFilter, dateFrom, dateTo, isMarketing, productionOrders]);

  const availableBrands = useMemo(() => {
    const brands = new Set();
    preFiltered.forEach(inv => {
      if (picFilter !== 'ALL' && inv.rawRecord?.pic !== picFilter) return;
      const clientStr = inv.client || 'Client Kustom';
      const cleanBrand = clientStr.split(' / ')[0].trim().toUpperCase();
      brands.add(cleanBrand);
    });
    return Array.from(brands).sort();
  }, [preFiltered, picFilter]);

  const availablePics = useMemo(() => {
    const pics = new Set();
    preFiltered.forEach(inv => {
      if (brandFilter !== 'ALL') {
        const clientStr = inv.client || 'Client Kustom';
        const cleanBrand = clientStr.split(' / ')[0].trim().toUpperCase();
        if (cleanBrand !== brandFilter) return;
      }
      const pic = inv.rawRecord?.pic;
      if (pic) pics.add(pic);
    });
    return Array.from(pics).sort();
  }, [preFiltered, brandFilter]);

  const filtered = useMemo(() => {
    let result = preFiltered;
    
    if (brandFilter !== 'ALL') {
      result = result.filter(inv => {
        const clientStr = inv.client || 'Client Kustom';
        const cleanBrand = clientStr.split(' / ')[0].trim().toUpperCase();
        return cleanBrand === brandFilter;
      });
    }

    if (picFilter !== 'ALL') {
      result = result.filter(inv => inv.rawRecord?.pic === picFilter);
    }
    
    return result;
  }, [preFiltered, brandFilter, picFilter]);

  // Summary figures
  const totalOmset = filtered.reduce((s, inv) => s + (inv.totalJual || 0), 0);
  const totalModal = filtered.reduce((s, inv) => {
    const totalJasa = (inv.products || []).reduce((sum, p) => sum + ((p.jasa_nominal || p.final_jasa_nominal || 0) * (p.qty || 1)), 0);
    return s + (inv.totalHpp - totalJasa);
  }, 0);
  const totalMargin = totalOmset - totalModal;
  const marginPct = totalOmset > 0 ? ((totalMargin / totalOmset) * 100).toFixed(1) : 0;
  const totalPiutang = filtered.reduce((s, inv) => {
    const sisa = (inv.totalTagihan || 0) - (inv.jumlahDibayar || 0);
    return s + (sisa > 0 ? sisa : 0);
  }, 0);
  const lunas = filtered.filter(i => i.status === 'LUNAS').length;
  const belumLunas = filtered.filter(i => i.status !== 'LUNAS').length;

  const getStatusBadge = (status) => {
    if (status === 'LUNAS') return <span className="px-2.5 py-1 bg-green-100 text-green-800 text-[10px] font-black uppercase rounded-lg border border-green-200 shadow-sm">LUNAS</span>;
    if (status === 'MENUNGGU KONFIRMASI FINANCE' || status === 'MENUNGGU VERIFIKASI' || status === 'MENUNGGU KONFIRMASI') {
      return <span className="px-2.5 py-1 bg-amber-100 text-amber-900 text-[10px] font-black uppercase rounded-lg border border-amber-300 shadow-sm flex items-center gap-1">⏳ MENUNGGU KONFIRMASI FINANCE</span>;
    }
    if (status?.includes('DP') || status?.includes('CICILAN')) return <span className="px-2.5 py-1 bg-amber-100 text-amber-800 text-[10px] font-black uppercase rounded-lg border border-amber-200 shadow-sm">{status}</span>;
    return <span className="px-2.5 py-1 bg-rose-100 text-rose-800 text-[10px] font-black uppercase rounded-lg border border-rose-200 shadow-sm">{status || 'BELUM BAYAR'}</span>;
  };

  const isLegalitasOnly = (inv) => {
    if (!inv) return false;
    const hasNoValidProducts = !inv.products || inv.products.length === 0 || inv.products.every(p => !p.nama_product);
    const hasLegalitas = (inv.legalitas && inv.legalitas.length > 0) || (inv.rawRecord?.legalitas && inv.rawRecord.legalitas.length > 0);
    const isStatusLegal = String(inv.statusProduksi || '').toUpperCase() === 'LEGALITAS SAJA' || String(inv.statusProduksi || '').toUpperCase() === 'NON PRODUKSI';
    return (hasNoValidProducts && hasLegalitas) || isStatusLegal;
  };

  const getProduksiBadge = (statusProduksi, inv) => {
    if (inv && isLegalitasOnly(inv)) {
      return <span className="px-2.5 py-1 bg-amber-100 text-amber-900 text-[10px] font-black uppercase rounded-lg border border-amber-300 shadow-sm flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-amber-700" />LEGALITAS SAJA</span>;
    }
    if (!statusProduksi) return null;
    const s = String(statusProduksi).toUpperCase();
    if (s === 'LEGALITAS SAJA' || s === 'NON PRODUKSI') {
      return <span className="px-2.5 py-1 bg-amber-100 text-amber-900 text-[10px] font-black uppercase rounded-lg border border-amber-300 shadow-sm flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-amber-700" />LEGALITAS SAJA</span>;
    }
    if (s === 'PESANAN SELESAI' || s === 'SELESAI') {
      return <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase rounded-lg border border-emerald-200 shadow-sm flex items-center gap-1"><CheckCircle className="w-3 h-3" />{statusProduksi}</span>;
    }
    if (s === 'BELUM NAIK PRODUKSI') {
      return <span className="px-2.5 py-1 bg-rose-100 text-rose-800 text-[10px] font-black uppercase rounded-lg border border-rose-200 shadow-sm">{statusProduksi}</span>;
    }
    if (s.includes('PENGIRIMAN') || s.includes('SIAP KIRIM')) {
      return <span className="px-2.5 py-1 bg-teal-100 text-teal-800 text-[10px] font-black uppercase rounded-lg border border-teal-200 shadow-sm flex items-center gap-1"><Truck className="w-3 h-3" />{statusProduksi}</span>;
    }
    if (s.includes('VALIDASI QC') || s.includes('QC')) {
      return <span className="px-2.5 py-1 bg-purple-100 text-purple-800 text-[10px] font-black uppercase rounded-lg border border-purple-200 shadow-sm flex items-center gap-1"><Clock className="w-3 h-3" />{statusProduksi}</span>;
    }
    if (s.includes('MENUNGGU FILLING') || s.includes('MENUNGGU PENGEMASAN')) {
      return <span className="px-2.5 py-1 bg-amber-100 text-amber-800 text-[10px] font-black uppercase rounded-lg border border-amber-200 shadow-sm flex items-center gap-1"><Clock className="w-3 h-3" />{statusProduksi}</span>;
    }
    if (s.includes('PROSES PRODUKSI') || s.includes('MIXING') || s.includes('FILLING') || s.includes('PENGEMASAN') || s.includes('MASERASI')) {
      return <span className="px-2.5 py-1 bg-blue-100 text-blue-800 text-[10px] font-black uppercase rounded-lg border border-blue-200 shadow-sm flex items-center gap-1"><FlaskConical className="w-3 h-3" />{statusProduksi}</span>;
    }
    if (s === 'READY PRODUKSI' || s === 'SIAP PRODUKSI') {
      return <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase rounded-lg border border-emerald-200 shadow-sm flex items-center gap-1"><CheckCircle className="w-3 h-3" />READY PRODUKSI</span>;
    }
    return <span className="px-2.5 py-1 bg-indigo-100 text-indigo-800 text-[10px] font-black uppercase rounded-lg border border-indigo-200 shadow-sm flex items-center gap-1"><Package className="w-3 h-3" />{statusProduksi}</span>;
  };

  const getInitialPOData = (inv) => {
    let rawProducts = inv.products || [];
    if (!Array.isArray(rawProducts)) {
      try { rawProducts = JSON.parse(rawProducts); } catch(e) { rawProducts = []; }
      if (!Array.isArray(rawProducts)) rawProducts = [];
    }
    const products = rawProducts;

    const maxQty = products.length > 0 ? Math.max(...products.map(p => p.qty || 1)) : 1;
    const hasBox = products.some(p => {
      let kt = p.komponen_tambahan || [];
      if (!Array.isArray(kt)) {
        try { kt = JSON.parse(kt); } catch(e) { kt = []; }
        if (!Array.isArray(kt)) kt = [];
      }
      return kt.some(k => 
        (k.kategori?.toLowerCase() || '').includes('box') ||
        (k.kategori?.toLowerCase() || '').includes('sablon') ||
        (k.kategori?.toLowerCase() || '').includes('hot print')
      );
    });
    
    let estimasiHariProduksi = 2;
    if (maxQty > 2000) estimasiHariProduksi = 7;
    else if (maxQty >= 1000) estimasiHariProduksi = 5;
    else if (maxQty >= 500) estimasiHariProduksi = 3;
    if (hasBox) estimasiHariProduksi = 15;
    
    let deadlineDate = new Date();
    let daysAdded = 0;
    while (daysAdded < estimasiHariProduksi) {
      deadlineDate.setDate(deadlineDate.getDate() + 1);
      const day = deadlineDate.getDay();
      if (day !== 0 && day !== 6) daysAdded++; 
    }
    let parsedDeadline = deadlineDate.toISOString().split('T')[0];
    if (inv.deadline) {
      try {
        const d = new Date(inv.deadline);
        if (!isNaN(d.getTime())) {
          parsedDeadline = d.toISOString().split('T')[0];
        }
      } catch (e) {}
    }
    
    const rawClient = inv.client || 'Satala';
    const cleanBrand = rawClient.split('/')[0].trim().replace(/^(PT|CV|UD)\s+/i, '').replace(/\s*(Beauty|Cosmetic|Indonesia)\s*/gi, '').trim();

    const normalizeCode = (str) => String(str || '').replace(/^(PO-|INV-|SPH-)/i, '').trim().toLowerCase();
    const realPO = productionOrders.find(p => 
      p.id === inv.id ||
      p.order_number === inv.id || 
      (p.notes && p.notes.includes(inv.id)) || 
      (p.order_number && String(p.order_number).trim() === String(inv.id).trim()) ||
      (p.order_number && inv.id && normalizeCode(p.order_number) === normalizeCode(inv.id))
    );
    const existingOrderNumber = realPO?.order_number || (inv.id ? (inv.id.startsWith('PO-') ? inv.id : `PO-${inv.id.replace(/^INV-|^SPH-/i, '')}`) : '');

    return {
      id: realPO?.id,
      form: {
        order_number: existingOrderNumber,
        brand_name: cleanBrand || rawClient,
        deadline: parsedDeadline,
        notes: '',
        pic_marketing: inv.rawRecord?.pic || '',
        customerAddress: inv.rawRecord?.customerAddress || inv.customerAddress || '',
        phone: inv.rawRecord?.phone || inv.phone || '',
        customerName: inv.rawRecord?.customerName || inv.client || '',
      },
      products: products.map((p, idx) => {
        let racikan = p.racikan || [];
        if (!Array.isArray(racikan)) {
          try { racikan = JSON.parse(racikan); } catch(e) { racikan = []; }
          if (!Array.isArray(racikan)) racikan = [];
        }

        const bibitComps = racikan
          .filter(r => (r.kategori || r.kategori_bahan) === 'Bibit')
          .map(r => ({
            category: 'bibit',
            inventory_item_id: r.bahan_id ? `bahan-${r.bahan_id}` : '',
            item_name: r.bahan_nama || r.nama_bahan || r.nama || '',
            vendor: r.vendor || '',
            unit: 'L',
            status: r.dari_client ? 'pending_client' : 'pending',
            item_notes: '',
            is_integrated: true,
          }));
          
        if (bibitComps.length === 0) {
          bibitComps.push({
            category: 'bibit',
            inventory_item_id: '',
            item_name: '',
            vendor: '',
            unit: 'L',
            status: 'pending',
            item_notes: '',
            is_integrated: true,
          });
        }

        const bahanCairComps = racikan
          .filter(r => {
            const cat = (r.kategori || r.kategori_bahan || '').toLowerCase();
            return cat !== 'bibit';
          })
          .map(r => ({
            category: 'bahan_cair',
            inventory_item_id: r.bahan_id ? `bahan-${r.bahan_id}` : '',
            item_name: r.bahan_nama || r.nama_bahan || r.nama || '',
            vendor: r.vendor || '',
            unit: 'L',
            status: r.dari_client ? 'pending_client' : 'pending',
            item_notes: '',
            is_integrated: true,
          }));

        let botolItemId = p.botol_id ? `botol-${p.botol_id}` : '';
        let botolName = p.botol_nama || '';
        let botolDariClient = p.botol_dari_client || false;
        
        let tutupComps = [{ category: 'tutup', inventory_item_id: p.tutup_id ? `tutup-${p.tutup_id}` : '', item_name: p.tutup_nama || (p.tutup_dari_client ? 'Tutup Dari Client' : ''), status: p.tutup_dari_client ? 'pending_client' : 'pending' }];
        let sprayComps = [{ category: 'spray', inventory_item_id: p.spray_id ? `spray-${p.spray_id}` : '', item_name: p.spray_nama || (p.spray_dari_client ? 'Spray Dari Client' : ''), status: p.spray_dari_client ? 'pending_client' : 'pending' }];

        if (p.is_fullset) {
          botolItemId = p.fullset_id ? `fullset-${p.fullset_id}` : '';
          botolName = p.fullset_nama || 'Kemasan Fullset';
          tutupComps = [];
          sprayComps = [];
        }

        const coreComps = [
          [{ category: 'botol', inventory_item_id: botolItemId, item_name: botolName || (botolDariClient ? 'Botol Dari Client' : ''), status: botolDariClient ? 'pending_client' : 'pending' }],
          bibitComps,
          tutupComps,
          sprayComps,
          bahanCairComps,
          [] 
        ];
        return {
          _id: Date.now() + idx,
          product_name: p.nama_product,
          target_qty: p.qty,
          ukuran_botol_ml: p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || 30,
          is_dynamic_qty: false,
          racikanList: racikan.map(r => ({
            nama_bahan: r.bahan_nama || r.nama_bahan || r.nama || '',
            kategori_bahan: r.kategori || r.kategori_bahan || 'Bibit',
            persentase: r.persentase || 0,
            vendor: r.vendor || '',
            dari_client: r.dari_client,
            _source: 'invoice',
          })),
          coreComponents: coreComps
        };
      })
    };
  };

  const parseNominal = (input, total) => {
    const clean = String(input).trim();
    if (clean.includes('%')) {
      const cleanPct = clean.replace('%', '').trim().replace(/\./g, '').replace(',', '.');
      const pct = parseFloat(cleanPct);
      if (!isNaN(pct)) return Math.round((pct / 100) * total);
    }
    return parseFloat(clean.replace(/\./g, '').replace(',', '.')) || 0;
  };

  const handleInputBayarChange = (e, inv) => {
    const val = e.target.value;
    
    // Auto convert percentage to nominal
    if (val.endsWith('%')) {
      const cleanVal = val.replace('%', '').trim().replace(/\./g, '').replace(',', '.');
      const pct = parseFloat(cleanVal);
      if (!isNaN(pct)) {
        const nominal = Math.round((pct / 100) * inv.totalTagihan);
        setInputBayar(fmt(nominal));
        if (!catatanBayar) setCatatanBayar(`Pembayaran ${pct}%`);
        return;
      }
    }
    
    if (val.includes('%')) {
      setInputBayar(val); // Let user type %
    } else {
      const digits = val.replace(/\D/g, '');
      if (digits) {
        const num = parseInt(digits, 10);
        setInputBayar(new Intl.NumberFormat('id-ID').format(num));
      } else {
        setInputBayar('');
      }
    }
  };

  const verifikasiBayar = (inv) => {
    const aktual = parseNominal(inputBayar, inv.totalTagihan);
    let pct = 0;
    
    if (aktual === inv.totalTagihan) pct = 100;
    else if (aktual === Math.round(inv.totalTagihan * 0.5)) pct = 50;;
    const expected = inv.rawRecord?.riwayatPembayaran?.[0]?.jumlah || 0;

    if (aktual !== expected) {
      toast.error('jumlah tf tidak sesuai dengan payment');
      
      const newRiwayat = [...(inv.rawRecord?.riwayatPembayaran || [])];
      if (newRiwayat.length > 0) {
        newRiwayat[0].catatan += ` (jumlah tf tidak sesuai: Rp ${fmt(aktual)})`;
        updatePO(inv.id, { riwayatPembayaran: newRiwayat });
      }
      return;
    }

    const confirmPush = window.confirm("Invoice valid! Apakah Anda mau memasukan invoice ini ke list PO production?");
    
    pct = inv.rawRecord.dpPercentage || pct;
    const statusBayar = pct === 100 ? 'LUNAS' : `DP ${pct}%`;
    const newCatatan = `Pembayaran ${pct}% Verified via BCA`;
    
    const newRiwayat = [...(inv.rawRecord.riwayatPembayaran || [])];
    if (newRiwayat.length > 0) {
        newRiwayat[0].catatan = newCatatan;
        newRiwayat[0].status_konfirmasi = 'Dikonfirmasi';
    }

    const updates = {
      statusBayar,
      riwayatPembayaran: newRiwayat
    };

    if (confirmPush) {
      updates.statusProduksi = 'MENUNGGU PRODUKSI';
      toast.success('Pembayaran diverifikasi dan dimasukkan ke list PO Production.');
    } else {
      toast.success('Pembayaran diverifikasi.');
    }

    // Push 1x ke Mutasi Rekening (Event-Driven Architecture)
    const entry = inv.rawRecord?.riwayatPembayaran?.[0];
    if (entry) {
      const refId = `po-${inv.id}-pay-0-verified`;
      const parsedDate = new Date().toISOString().split('T')[0];
      base44.entities.AccountMutation.create({
        date: parsedDate,
        type: 'masuk',
        account: 'BCA', // Assume verified via BCA as hardcoded in newCatatan
        amount: entry.jumlah,
        description: `[Pembayaran Invoice] Verified ${newCatatan} PO #${inv.id} (${inv.customerName || 'Mitra'})`,
        reference_id: refId
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
      }).catch(err => console.error('Gagal mencatat mutasi:', err));
    }

    updatePO(inv.id, updates);
  };

  const saveBayar = async (inv) => {
    if (isSavingPayment) return;
    const tambahan = parseNominal(inputBayar, inv.totalTagihan);
    if (tambahan <= 0) { setEditBayarId(null); setInputBayar(''); setCatatanBayar(''); return; }

    const finalTanggalBayar = inputTanggalBayar 
      ? (typeof inputTanggalBayar === 'string' ? inputTanggalBayar : formatTanggalIndo(inputTanggalBayar))
      : formatTanggalIndo(new Date());

    setIsSavingPayment(true);
    try {
      const selectedBankName = bankAccounts?.[bankBayar]?.name || bankBayar || 'BCA';
      const entry = {
        jumlah: tambahan,
        tanggal_bayar: finalTanggalBayar,
        catatan: catatanBayar.trim(),
        diinput_oleh: currentMember?.full_name || 'System',
        bank_tujuan: selectedBankName,
        status_konfirmasi: 'Dikonfirmasi Finance'
      };

      const pendingId = `pending-pay-${Date.now()}`;
      
      if (inv.source === 'hpp') {
        const riwayatLama = inv.rawRecord.riwayat_pembayaran || [];
        const newRiwayat = [...riwayatLama, entry];
        const totalBaru = newRiwayat.reduce((s, r) => s + (r.jumlah || 0), 0);
        const isNowLunas = totalBaru >= (inv.totalTagihan || 0);
        const updatedStatusBayar = isNowLunas ? 'Sudah Lunas' : (totalBaru > 0 ? 'Belum Lunas' : 'Belum Bayar');

        await updateHppMutation.mutateAsync({
          id: inv.rawRecord.id,
          data: {
            shadow_prevent: true,
            riwayat_pembayaran: newRiwayat,
            status_bayar: updatedStatusBayar
          }
        });
        
        await base44.entities.AccountMutation.create({
          account: selectedBankName,
          type: 'masuk',
          amount: tambahan,
          date: finalTanggalBayar,
          description: `[Pembayaran Invoice] Pembayaran HPP: ${inv.rawRecord.nama_produk || 'Produk'}${catatanBayar ? ` - ${catatanBayar.trim()}` : ''}`,
          reference_id: inv.rawRecord.id,
          status: 'verified',
          // Fields for payment record
          invoice_id: inv.rawRecord.id,
          invoice_type: 'hpp',
          invoice_name: inv.rawRecord.nama_produk || 'Produk HPP',
          jumlah: tambahan,
          tanggal_bayar: finalTanggalBayar,
          catatan: catatanBayar.trim(),
          diinput_oleh: entry.diinput_oleh,
          bank_tujuan: selectedBankName,
          bank_key: bankBayar || 'bca',
          coa_allocations: [
            {
              account_id: '4-40001',
              amount: tambahan,
              notes: 'Penerimaan Pembayaran HPP'
            }
          ]
        });
        
        toast.success('Pembayaran HPP berhasil dicatat dan masuk ke Mutasi Rekening!');
        queryClient.invalidateQueries({ queryKey: ['hpp-invoices'] });
        queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
      } else {
        const riwayatLama = inv.rawRecord.riwayatPembayaran || [];
        const newRiwayat = [...riwayatLama, entry];
        const totalBaru = newRiwayat.reduce((s, r) => s + (r.jumlah || 0), 0);
        const isNowLunas = totalBaru >= (inv.totalTagihan || 0);
        const updatedStatusBayar = isNowLunas ? 'LUNAS' : (totalBaru > 0 ? 'DP' : 'BELUM DIBAYAR');
        
        await updatePO(inv.id, { 
          riwayatPembayaran: newRiwayat, 
          paymentMethod: bankBayar || 'bca',
          statusBayar: updatedStatusBayar
        });

        await base44.entities.AccountMutation.create({
          account: selectedBankName,
          type: 'masuk',
          amount: tambahan,
          date: finalTanggalBayar,
          description: `[Pembayaran Invoice] Invoice: ${inv.customerName || inv.client || inv.id}${catatanBayar ? ` - ${catatanBayar.trim()}` : ''}`,
          reference_id: inv.id,
          status: 'verified',
          // Fields for payment record
          invoice_id: inv.id,
          invoice_type: 'client_po',
          invoice_name: inv.customerName || inv.client || inv.id,
          jumlah: tambahan,
          tanggal_bayar: finalTanggalBayar,
          catatan: catatanBayar.trim(),
          diinput_oleh: entry.diinput_oleh,
          bank_tujuan: selectedBankName,
          bank_key: bankBayar || 'bca',
          coa_allocations: [
            {
              account_id: '4-40001',
              amount: tambahan,
              notes: 'Penerimaan Pembayaran Invoice'
            }
          ]
        });

        toast.success('Pembayaran Invoice berhasil dicatat dan masuk ke Mutasi Rekening!');
        queryClient.invalidateQueries({ queryKey: ['client-pos'] });
        queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
      }

      setEditBayarId(null);
      setInputBayar('');
      setCatatanBayar('');
      setBankBayar('');
      setInputTanggalBayar('');
    } catch (e) {
      toast.error('Gagal mencatat pembayaran: ' + (e.message || 'Error'));
    } finally {
      setIsSavingPayment(false);
    }
  };
  const cancelPayment = async (inv, index, e) => {
    e.stopPropagation();
    try {
      if (!window.confirm('Yakin ingin menghapus riwayat pembayaran ini? Kas yang terkait juga akan dihapus.')) return;
      if (inv.source === 'hpp') {
         const newRiwayat = [...(inv.rawRecord.riwayat_pembayaran || [])];
         const deletedEntry = newRiwayat.splice(index, 1)[0];
         await updateHppMutation.mutateAsync({
           id: inv.rawRecord.id,
           data: {
             shadow_prevent: true,
             riwayat_pembayaran: newRiwayat
           }
         });
         
         // Delete from AccountMutation
         const { data: muts } = await base44.entities.AccountMutation.filter({ invoice_id: inv.rawRecord.id, jumlah: deletedEntry.jumlah });
         if (muts && muts.length > 0) {
           await base44.entities.AccountMutation.hardDelete(muts[0].id);
         }
         
         toast.success('Riwayat pembayaran berhasil dihapus.');
         queryClient.invalidateQueries({ queryKey: ['hpp-invoices'] });
      } else {
         const newRiwayat = [...(inv.rawRecord.riwayatPembayaran || [])];
         const deletedEntry = newRiwayat.splice(index, 1)[0];
         await updatePO(inv.id, { riwayatPembayaran: newRiwayat });
         
         // Delete from AccountMutation
         const muts = await base44.entities.AccountMutation.filter({ invoice_id: inv.id, jumlah: deletedEntry.jumlah });
         if (muts && muts.length > 0) {
           await base44.entities.AccountMutation.hardDelete(muts[0].id);
         }
         
         toast.success('Riwayat pembayaran berhasil dihapus.');
         queryClient.invalidateQueries({ queryKey: ['client-pos'] });
      }
    } catch (err) {
      toast.error('Gagal membatalkan pembayaran: ' + (err.message || 'Error'));
    }
  };


  const handleSimpanPic = (inv, picName) => {
    if (inv.source === 'client_po') {
      updatePO(inv.id, { pic: picName });
      toast.success('PIC berhasil disimpan!');
    }
    setEditPicId(null);
    setInputPic('');
  };

  const saveKomisi = async (e) => {
    e.preventDefault();
    if (isSavingKomisi) return;
    const nominal = Number(komisiForm.nominal);
    if (!nominal || nominal <= 0) {
      toast.error('Nominal komisi tidak valid');
      return;
    }
    
    setIsSavingKomisi(true);
    try {
      const entry = {
        tanggal: new Date().toISOString().split('T')[0],
        bank_asal: komisiForm.bankAsal,
        bank_tujuan: komisiForm.bankTujuan,
        atas_nama: komisiForm.atasNama,
        nominal: nominal,
        catatan: komisiForm.catatan,
        diinput_oleh: currentMember?.full_name || 'System'
      };

      if (komisiForm.source === 'hpp') {
        const riwayatLama = komisiForm.rawRecord.komisiList || [];
        await updateHppMutation.mutateAsync({
          id: komisiForm.rawRecord.id,
          data: { shadow_prevent: true, komisiList: [...riwayatLama, entry] }
        });
      } else {
        const riwayatLama = komisiForm.rawRecord.komisiList || [];
        await updatePO(komisiForm.invId, { komisiList: [...riwayatLama, entry] });
      }

      const refId = `komisi-${komisiForm.invId}-${Date.now()}`;
      await base44.entities.AccountMutation.create({
        date: entry.tanggal,
        type: 'keluar',
        account: entry.bank_asal || 'BCA',
        amount: entry.nominal,
        description: `[Komisi Invoice] ${entry.catatan || 'Pembayaran Komisi'} - ${komisiForm.invName} a.n ${entry.atas_nama} (${entry.bank_tujuan})`,
        reference_id: refId,
        kategori: 'Komisi Invoice'
      });

      toast.success('Komisi berhasil disimpan & masuk Mutasi Keluar!');
      queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
      setShowKomisiModal(false);
      setKomisiForm({ invId: '', invName: '', picName: '', bankAsal: 'bca', bankTujuan: '', atasNama: '', nominal: '', catatan: '', rawRecord: null, source: '' });
    } catch (err) {
      toast.error('Gagal mencatat komisi: ' + (err.message || 'Error'));
    } finally {
      setIsSavingKomisi(false);
    }
  };

  const handleCetakLaporan = () => {
    const columns = [
      { key: 'no', label: 'No' },
      { key: 'tgl', label: 'Tgl' },
      { key: 'id', label: 'Nomor Invoice' },
      { key: 'client', label: 'Client / Produk' },
      { key: 'total_pcs', label: 'Total Pcs' },
      { key: 'margin', label: 'Margin Bersih' }
    ];

    let totalAllPcs = 0;

    const rows = filtered.map((inv, idx) => {
      const totalJasa = (inv.products || []).reduce((s, p) => s + ((p.jasa_nominal || p.final_jasa_nominal || 0) * (p.qty || 1)), 0);
      const totalKomisi = (inv.rawRecord?.komisiList || []).reduce((sum, k) => sum + (Number(k.nominal) || 0), 0);
      const hppTanpaJasa = (inv.totalHpp || 0) - totalJasa + totalKomisi;
      const margin = (inv.totalJual || 0) - hppTanpaJasa;
      const clientStr = inv.client || '';
      let prodTotalPcs = 0;
      
      const productsList = (inv.products || []).map(p => {
        const qty = Number(p.qty || p.pcs || p.jumlah_order || 0);
        prodTotalPcs += qty;
        const vol = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || p.volume_ml || p.ukuran || p.ukuran_ml || '-';
        return `- ${p.item_name || p.nama_product || p.name || 'Produk'} (${vol} ml, ${fmt(qty)} pcs)`;
      }).join('\n');
      
      totalAllPcs += prodTotalPcs;
      
      return {
        no: String(idx + 1),
        tgl: inv.tanggal || '-',
        id: inv.id,
        client: `${clientStr}\n${productsList}`,
        total_pcs: `${fmt(prodTotalPcs)} Pcs`,
        margin: `Rp ${fmt(margin)}`
      };
    });

    const totalBrands = new Set(filtered.map(i => (i.client || '').split(' / ')[0].trim())).size;

    const summaryStr = `Omset : Rp ${fmt(totalOmset)} | HPP : Rp ${fmt(totalModal)} | Margin : Rp ${fmt(totalMargin)} | ${fmt(totalAllPcs)} Pcs | ${fmt(filtered.length)} Transaksi | ${fmt(totalBrands)} Brand`;

    exportToPDF({
      title: 'Laporan Invoice & Riwayat Keuangan',
      subtitle: summaryStr,
      filename: 'Laporan_Finance',
      columns,
      rows,
      orientation: 'landscape'
    });
  };

  return (
    <div className="space-y-6 pb-20 print:pb-0 print:space-y-0 text-[#302621]">
      <BatchActionBar
        isSelectionMode={isSelectionMode}
        totalCount={filtered.length}
        selectedCount={selectedCount}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={() => {
          if (confirm(`Yakin ingin menghapus ${selectedCount} invoice terpilih?`)) {
            batchDeleteMutation.mutate(Array.from(selectedIds));
          }
        }}
        isDeleting={batchDeleteMutation.isPending}
      />
      
      <div className="space-y-6 print:hidden">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
          <h1 className="text-xl md:text-3xl font-black text-[#302621] tracking-tight">Invoice & Riwayat Keuangan</h1>
          <p className="text-[9px] md:text-xs text-[#7A695B] font-medium mt-0.5 md:mt-1">Laporan semua tagihan, riwayat pembayaran (DP/Lunas), dan rekap profit bersih per order — terintegrasi dengan PO (Pesanan Maklon/OEM).</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="text-xs gap-1.5 h-9 rounded-xl border-slate-200 hover:bg-slate-50" onClick={handleCetakLaporan}>
            <Download className="w-3.5 h-3.5" />
            Cetak Laporan
          </Button>
        </div>
      </div>

      {/* Summary Cards - Exact 2-Row Configuration Requested */}
      <div className="space-y-3.5 print:hidden">
        {/* Baris 1: Omset & Total Modal (2 Kartu) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          <Card className="border-white/60 shadow-sm rounded-[1.75rem] bg-white/80  hover:shadow-xl transition-all duration-300 overflow-hidden">
            <CardContent className="p-4 md:p-5">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Omset (Harga Jual)</p>
              <p className="text-xl md:text-2xl lg:text-3xl font-black text-slate-800 mt-1 tracking-tight truncate" title={`Rp ${fmt(totalOmset)}`}>
                Rp {fmtSmart(totalOmset)}
              </p>
              <p className="text-[10px] text-slate-400 mt-1">{filtered.length} order aktif</p>
            </CardContent>
          </Card>

          <Card className="border-white/60 shadow-sm rounded-[1.75rem] bg-white/80  hover:shadow-xl transition-all duration-300 overflow-hidden">
            <CardContent className="p-4 md:p-5">
              <p className="text-[10px] font-bold text-rose-500 uppercase tracking-wider">Total Modal (HPP)</p>
              <p className="text-xl md:text-2xl lg:text-3xl font-black text-rose-600 mt-1 tracking-tight truncate" title={`Rp ${fmt(totalModal)}`}>
                Rp {fmtSmart(totalModal)}
              </p>
              <p className="text-[10px] text-slate-400 mt-1">Biaya Packaging & Bahan Cair</p>
            </CardContent>
          </Card>
        </div>

        {/* Baris 2: Margin Profit, Total Piutang, & Metrik Pembayaran (3 Kartu) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-700 border-0 p-4 md:p-5 rounded-[1.75rem] shadow-xl text-white overflow-hidden flex flex-col justify-between min-h-[105px]">
            <div>
              <p className="text-[10px] font-bold text-emerald-100 uppercase tracking-wider">Margin Profit Bersih</p>
              <p className="text-xl md:text-2xl font-black mt-1 tracking-tight truncate" title={`Rp ${fmt(totalMargin)}`}>
                Rp {fmtSmart(totalMargin)}
              </p>
            </div>
            <p className="text-[10px] text-emerald-100 mt-1 leading-tight">
              <span className="bg-white/20 px-1.5 py-0.5 rounded font-black text-[9px] mr-1">+{marginPct}%</span> dari Jual − Modal
            </p>
          </Card>

          <Card className="border-white/60 shadow-sm rounded-[1.75rem] bg-white/80  hover:shadow-xl transition-all duration-300 overflow-hidden">
            <CardContent className="p-4 md:p-5">
              <p className="text-[10px] font-bold text-orange-500 uppercase tracking-wider">Total Piutang</p>
              <p className="text-xl md:text-2xl font-black text-orange-600 mt-1 tracking-tight truncate" title={`Rp ${fmt(totalPiutang)}`}>
                Rp {fmtSmart(totalPiutang)}
              </p>
              <p className="text-[10px] text-slate-400 mt-1">Sisa tagihan belum lunas</p>
            </CardContent>
          </Card>

          <Card className="border-white/60 shadow-sm rounded-[1.75rem] bg-white/80  hover:shadow-xl transition-all duration-300 overflow-hidden">
            <CardContent className="p-4 md:p-5">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Metrik Pembayaran</p>
              <div className="flex items-center gap-4 mt-2">
                <div>
                  <p className="text-lg md:text-xl font-black text-green-600">{lunas}</p>
                  <p className="text-[8px] md:text-[9px] text-slate-400 font-bold uppercase tracking-wider">Lunas</p>
                </div>
                <div className="w-px h-8 bg-slate-200" />
                <div>
                  <p className="text-lg md:text-xl font-black text-amber-600">{belumLunas}</p>
                  <p className="text-[8px] md:text-[9px] text-slate-400 font-bold uppercase tracking-wider">Tergantung DP</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-4 flex flex-col sm:flex-row gap-3 print:hidden">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-[#8C6208]/30 focus:border-[#8C6208]"
            placeholder="Cari invoice, brand client, produk, tanggal..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex items-center gap-1.5">
            <CustomDatePicker
              value={dateFrom}
              onChange={setDateFrom}
              placeholder="Mulai..."
              className="w-[125px] rounded-xl bg-slate-50 border-slate-200 shadow-sm h-8 text-[10px]"
            />
            <span className="text-[10px] text-slate-400 font-bold">→</span>
            <CustomDatePicker
              value={dateTo}
              onChange={setDateTo}
              placeholder="Akhir..."
              className="w-[125px] rounded-xl bg-slate-50 border-slate-200 shadow-sm h-8 text-[10px]"
            />
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="p-1.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors shadow-sm ml-0.5"
                title="Reset filter tanggal"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          <div className="w-[140px]">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-[10px] font-bold text-slate-600 bg-slate-50 border-slate-200 rounded-xl truncate focus:ring-[#8C6208]/30">
                <SelectValue placeholder="Semua Status" />
              </SelectTrigger>
              <SelectContent>
                {['ALL', 'MENUNGGU VERIFIKASI', 'LUNAS', 'BELUM LUNAS', 'BELUM BAYAR', 'TEMPO'].map(s => (
                  <SelectItem key={s} value={s} className="text-[10px] font-bold uppercase tracking-wider">{s === 'ALL' ? 'Semua Status' : s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-[140px]">
            <Select value={prosesFilter} onValueChange={setProsesFilter}>
              <SelectTrigger className="h-8 text-[10px] font-bold text-slate-600 bg-slate-50 border-slate-200 rounded-xl truncate focus:ring-[#8C6208]/30">
                <SelectValue placeholder="Semua Proses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL" className="text-[10px] font-bold uppercase tracking-wider">Semua Proses</SelectItem>
                <SelectItem value="BELUM_NAIK" className="text-[10px] font-bold uppercase tracking-wider">Belum Naik Produksi</SelectItem>
                <SelectItem value="PROSES" className="text-[10px] font-bold uppercase tracking-wider">Proses Produksi</SelectItem>
                <SelectItem value="DIKIRIM" className="text-[10px] font-bold uppercase tracking-wider">Sedang Dikirim</SelectItem>
                <SelectItem value="SELESAI" className="text-[10px] font-bold uppercase tracking-wider">Selesai</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(!isMarketing || currentMember?.role === 'owner' || currentMember?.role === 'developer') && (
            <div className="w-[120px]">
              <Select value={picFilter} onValueChange={setPicFilter}>
                <SelectTrigger className="h-8 text-[10px] font-bold text-slate-600 bg-slate-50 border-slate-200 rounded-xl truncate focus:ring-[#8C6208]/30">
                  <SelectValue placeholder="Semua PIC" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL" className="text-[10px] font-bold">Semua PIC</SelectItem>
                  {availablePics.map(p => (
                    <SelectItem key={p} value={p} className="text-[10px] font-bold">{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="w-[140px]">
            <Select value={brandFilter} onValueChange={setBrandFilter}>
              <SelectTrigger className="h-8 text-[10px] font-bold text-slate-600 bg-slate-50 border-slate-200 rounded-xl truncate focus:ring-[#8C6208]/30">
                <SelectValue placeholder="Semua Brand" />
              </SelectTrigger>
              <SelectContent>
                <div className="p-2 sticky top-0 bg-white z-10 border-b border-slate-100">
                  <div className="relative">
                    <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input 
                      placeholder="Cari brand..." 
                      className="w-full text-[10px] pl-6 pr-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-[#8C6208] focus:ring-1 focus:ring-[#8C6208]/50"
                      value={brandSearchQuery}
                      onChange={(e) => setBrandSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                <SelectItem value="ALL" className="text-[10px] font-bold">Semua Brand</SelectItem>
                {availableBrands.filter(b => b.toLowerCase().includes(brandSearchQuery.toLowerCase())).map(b => (
                  <SelectItem key={b} value={b} className="text-[10px] font-bold">{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Invoice Table */}
      <div className="bg-white border-white/60 rounded-[2rem] shadow-sm overflow-hidden  bg-white/80 relative">
        <div className="p-4 md:p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 print:hidden">
          <h3 className="text-xs md:text-sm font-black text-slate-800">Riwayat Pesanan & Laporan Keuntungan</h3>
          <span className="text-[9px] md:text-[10px] text-slate-400 bg-white px-2.5 py-1 rounded-lg border border-slate-100 shadow-sm font-semibold">{filtered.length} transaksi</span>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 bg-slate-50/30">
            <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-500">Tidak ada Invoice yang cocok.</p>
            <p className="text-xs text-slate-400 mt-1">Gunakan Kalkulator HPP untuk mencatat data baru.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((inv) => {
              const isExpanded = expandedId === inv.id;
              // Jasa adalah profit murni, jadi kita keluarkan dari HPP untuk hitung margin yang benar
              const totalJasaHeader = (inv.products || []).reduce((s, p) => s + ((p.jasa_nominal || p.final_jasa_nominal || 0) * (p.qty || 1)), 0);
              const totalKomisiHeader = (inv.rawRecord?.komisiList || []).reduce((sum, k) => sum + (Number(k.nominal) || 0), 0);
              const hppTanpaJasa = inv.totalHpp - totalJasaHeader + totalKomisiHeader;
              const margin = inv.totalJual - hppTanpaJasa;
              const marginPercentage = inv.totalJual > 0 ? ((margin / inv.totalJual) * 100).toFixed(1) : 0;
              const sisaPiutang = (inv.totalTagihan || 0) - (inv.jumlahDibayar || 0);
              const displayPiutang = sisaPiutang > 0 ? sisaPiutang : 0;

              let bgClass = '';
              if (isSelectionMode && selectedIds.has(inv.id)) {
                bgClass = 'bg-rose-50/20';
              } else if ((inv.statusProduksi === 'PESANAN SELESAI' || inv.statusProduksi === 'SELESAI') && inv.status === 'LUNAS') {
                bgClass = 'bg-emerald-50/30 hover:bg-emerald-50/50';
              } else if (inv.rawRecord?.paymentTerms === 'tempo' || inv.rawRecord?.is_tempo || inv.statusBayar === 'TEMPO' || inv.status === 'BELUM BAYAR' || inv.status === 'BELUM DIBAYAR') {
                bgClass = 'bg-rose-50 hover:bg-rose-100/50';
              } else if (inv.statusProduksi === 'BELUM NAIK PRODUKSI' || inv.statusProduksi === 'MENUNGGU PRODUKSI') {
                bgClass = 'bg-white hover:bg-slate-50/40';
              } else if (inv.statusProduksi === 'PROSES PRODUKSI' || inv.statusProduKSI === 'SIAP PRODUKSI' || inv.statusProduksi === 'PROSES PENGEMASAN' || inv.statusProduksi === 'QC') {
                bgClass = 'bg-blue-50/30 hover:bg-blue-50/50';
              } else if (['SIAP KIRIM', 'DALAM PENGIRIMAN', 'MENUNGGU PICKUP'].includes(inv.statusProduksi)) {
                bgClass = 'bg-purple-50/30 hover:bg-purple-50/50';
              } else {
                bgClass = 'hover:bg-slate-50/40';
              }

              return (
                  <div 
                    key={inv.id} 
                    className={`border-b last:border-b-0 border-slate-100 transition-all ${bgClass}`}
                  >
                  <div 
                    onClick={() => isSelectionMode ? toggleSelection(inv.id) : setExpandedId(isExpanded ? null : inv.id)}
                    onMouseDown={(e) => {
                      if (e.button !== 0) return;
                      lpTimeout = setTimeout(() => {
                        setLongPressedId(inv.id);
                      }, 2000);
                    }}
                    onTouchStart={() => {
                      lpTimeout = setTimeout(() => {
                        setLongPressedId(inv.id);
                      }, 2000);
                    }}
                    onMouseUp={() => clearTimeout(lpTimeout)}
                    onMouseLeave={() => clearTimeout(lpTimeout)}
                    onTouchEnd={() => clearTimeout(lpTimeout)}
                    onTouchCancel={() => clearTimeout(lpTimeout)}
                    className="flex items-center gap-4 px-6 py-4 transition-all duration-300 cursor-pointer active:scale-[0.97] active:bg-slate-200/50 active:shadow-inner select-none relative"
                  >
                    
                    {/* Floating Tanya Asa Button */}
                    {longPressedId === inv.id && (
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#302621] text-[#E6D5B8] px-4 py-2.5 rounded-full shadow-2xl z-20 flex items-center gap-3 cursor-pointer animate-in zoom-in-75 duration-200 border border-[#E6D5B8]/20"
                           onClick={(e) => { 
                             e.stopPropagation(); 
                             openAsa('Invoice & Laporan Keuangan', inv); 
                             setLongPressedId(null); 
                           }}
                      >
                        <Sparkles className="w-4 h-4 text-emerald-400 animate-pulse" />
                        <span className="font-bold text-sm tracking-wide">Tanya Asa</span>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setLongPressedId(null); }} 
                          className="ml-2 bg-rose-500 text-white rounded-full p-0.5 hover:bg-rose-600 hover:scale-110 transition-transform"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {isSelectionMode && (
                      <Checkbox 
                        checked={selectedIds.has(inv.id)} 
                        onCheckedChange={() => toggleSelection(inv.id)} 
                      />
                    )}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${
                      inv.source === 'client_po' ? 'bg-amber-100 text-[#8C6208]' : 'bg-indigo-100 text-indigo-700'
                    }`}>
                      {inv.source === 'client_po' ? <Package className="w-4.5 h-4.5" /> : <FileText className="w-4.5 h-4.5" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {editInvoiceNameId === inv.id ? (
                          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded px-1.5 py-0.5">
                            <input
                              autoFocus
                              value={inputInvoiceName}
                              onChange={(e) => setInputInvoiceName(e.target.value)}
                              className="text-xs font-extrabold w-36 outline-none bg-transparent"
                            />
                            <button
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (inputInvoiceName.trim() && inputInvoiceName !== inv.id) {
                                  try {
                                    await updatePO(inv.id, { id: inputInvoiceName.trim() });
                                    toast.success('Nama invoice berhasil diubah.');
                                    setEditInvoiceNameId(null);
                                  } catch (error) {
                                    toast.error('Gagal ubah nama: Mungkin nama sudah pernah terpakai atau ada di Keranjang Sampah.');
                                  }
                                } else {
                                  setEditInvoiceNameId(null);
                                }
                              }}
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button
                              className="p-1 text-slate-400 hover:bg-slate-100 rounded"
                              onClick={(e) => { e.stopPropagation(); setEditInvoiceNameId(null); }}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 group">
                            <span className="font-extrabold text-sm text-slate-800">{inv.id}</span>
                            {inv.source === 'client_po' && (
                              <button 
                                className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-indigo-600 rounded transition-all"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setEditInvoiceNameId(inv.id); 
                                  setInputInvoiceName(inv.id); 
                                }}
                                title="Edit Nama Invoice"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                        {(() => {
                          const validPayments = (inv.riwayatPembayaran || inv.rawRecord?.riwayatPembayaran || inv.rawRecord?.riwayat_pembayaran || []).filter(r => r.status_konfirmasi !== 'Menunggu Konfirmasi Finance');
                          const pendingPayments = (inv.riwayatPembayaran || inv.rawRecord?.riwayatPembayaran || inv.rawRecord?.riwayat_pembayaran || []).filter(r => r.status_konfirmasi === 'Menunggu Konfirmasi Finance');
                          const totalValidBayar = validPayments.reduce((s, r) => s + (r.jumlah || 0), 0);
                          const isFullyPaid = (inv.totalTagihan > 0 && totalValidBayar >= inv.totalTagihan - 100) || 
                                              (inv.totalJual > 0 && totalValidBayar >= inv.totalJual - 100) ||
                                              inv.status === 'LUNAS' || 
                                              inv.rawRecord?.statusBayar === 'LUNAS' || 
                                              inv.rawRecord?.status_bayar === 'LUNAS' || 
                                              (inv.totalTagihan > 0 && Math.round(inv.totalTagihan - totalValidBayar) <= 0);

                          if (pendingPayments.length > 0 && !isFullyPaid) {
                            return getStatusBadge('MENUNGGU KONFIRMASI FINANCE');
                          }
                          if (isFullyPaid) {
                            return getStatusBadge('LUNAS');
                          }
                          if (inv.totalTagihan > 0 && totalValidBayar > 0) {
                            const realPct = Math.min(99, Math.floor((totalValidBayar / inv.totalTagihan) * 100));
                            const badgeText = validPayments.length > 1 ? `CICILAN ${realPct}%` : `DP ${realPct}%`;
                            return <span className="px-2.5 py-1 bg-amber-100 text-amber-800 text-[10px] font-black uppercase rounded-lg border border-amber-200 shadow-sm">{badgeText}</span>;
                          }
                          if (inv.totalTagihan > 0 && totalValidBayar <= 0) {
                            return getStatusBadge('BELUM BAYAR');
                          }
                          return getStatusBadge(inv.status);
                        })()}
                        {getProduksiBadge(inv.statusProduksi, inv)}
                        {(inv.rawRecord?.paymentTerms === 'tempo' || inv.rawRecord?.is_tempo || inv.statusBayar === 'TEMPO') && (
                          <span className="text-[9px] font-bold bg-rose-100 text-rose-800 px-2 py-0.5 rounded border border-rose-200 uppercase tracking-wider">
                            TEMPO {inv.rawRecord?.tempoDate ? `(${inv.rawRecord.tempoDate})` : ''}
                          </span>
                        )}
                        {(inv.rawRecord?.paymentTerms === 'cash' && !inv.rawRecord?.is_tempo && inv.statusBayar !== 'TEMPO') && (
                          <span className="text-[9px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded border border-emerald-200 uppercase tracking-wider">
                            CASH
                          </span>
                        )}
                        {inv.rawRecord?.komisiList?.length > 0 && (
                          <span className="text-[9px] font-bold bg-amber-50 text-amber-700 px-2 py-0.5 rounded border border-amber-200 uppercase tracking-wider flex items-center gap-1">
                            <Banknote className="w-3 h-3" /> KOMISI
                          </span>
                        )}
                        {inv.totalTagihan > 0 && inv.status !== 'LUNAS' && inv.jumlahDibayar > 0 && (() => {
                          const percent = Math.round((inv.jumlahDibayar / inv.totalTagihan) * 100);
                          if (percent >= 100) return null;
                          let colorClass = 'bg-red-50 text-red-700 border-red-200';
                          if (percent > 70) colorClass = 'bg-green-50 text-green-700 border-green-200';
                          else if (percent >= 40) colorClass = 'bg-orange-50 text-orange-700 border-orange-200';
                          return (
                            <span className={`text-[9px] font-bold px-2 py-0.5 rounded border ${colorClass}`}>
                              {percent}% Menuju Lunas
                            </span>
                          );
                        })()}
                        <span className="text-[9px] font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200">
                          {inv.rawRecord?.is_custom_finance ? 'Custom' : inv.rawRecord?.poMode === 'database' ? 'Repeat Order' : inv.rawRecord?.poMode === 'custom' ? 'Varian Baru' : inv.rawRecord?.poMode === 'sample' || inv.isSample ? 'Sample' : (inv.source === 'client_po' ? 'Client PO' : 'HPP Record')}
                        </span>
                        {inv.rawRecord?.ppn_mode === 'exclude' && (
                          <span className="text-[9px] font-bold bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-200 uppercase tracking-wider flex items-center gap-1">
                            + PPN {inv.rawRecord?.ppn_percentage || 0}%
                          </span>
                        )}
                        {inv.rawRecord?.is_custom_finance && (
                          <span className="text-[9px] font-bold bg-cyan-50 text-cyan-700 px-2 py-0.5 rounded border border-cyan-200 tracking-wider">
                            CUSTOM
                          </span>
                        )}
                        {inv.isSample && (
                          <span className="text-[9px] font-bold bg-purple-50 text-purple-700 px-2 py-0.5 rounded border border-purple-200 tracking-wider">
                            SAMPLE
                          </span>
                        )}
                        {inv.rawRecord?.deleted_by_client && (
                          <span className="text-[9px] font-bold bg-rose-50 text-rose-700 px-2 py-0.5 rounded border border-rose-200 uppercase flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Dihapus Klien
                          </span>
                        )}
                        {inv.rawRecord?.pic && (
                          <span className="text-[9px] font-bold bg-[#F9F5EC] text-[#8C6208] px-2 py-0.5 rounded border border-[#E6D5B8] flex items-center gap-1">
                            <User className="w-3 h-3" /> PIC: {inv.rawRecord.pic}
                          </span>
                        )}
                        {(() => {
                           const pm = (inv.rawRecord?.paymentMethod || '').toLowerCase();
                           if (!pm) return null;
                           let bankName = '';
                           if (pm.includes('bca') || pm.includes('apa saja bisa')) bankName = 'ASB';
                           else if (pm.includes('mandiri') || pm.includes('cuan jalan terus')) bankName = 'CJT';
                           else if (pm.includes('bni') || pm.includes('essential')) bankName = 'SDE';
                           
                           if (!bankName) return null;
                           return (
                             <span className="text-[9px] font-bold bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200 uppercase tracking-wider flex items-center gap-1">
                               <Wallet className="w-3 h-3" /> {bankName}
                             </span>
                           );
                        })()}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs font-bold text-slate-700 truncate">{inv.client}</p>
                        <span className="text-[9px] font-medium bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200">
                          {inv.tanggal}
                        </span>
                        {inv.statusProduksi === 'PESANAN SELESAI' && (
                          <span className="text-[9px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200">
                            Selesai: {inv.rawRecord?.updated_at ? formatTanggalIndo(inv.rawRecord.updated_at) : inv.tanggal}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 truncate mt-0.5">{inv.produk}</p>
                    </div>

                    <div className="text-right hidden md:block">
                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">HPP (Modal Murni)</p>
                      <p className="font-bold text-xs text-rose-600">Rp {fmt(hppTanpaJasa)}</p>
                    </div>

                    <div className="text-right hidden lg:block">
                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Harga Jual</p>
                      <p className="font-bold text-xs text-slate-700">Rp {fmt(inv.totalTagihan)}</p>
                    </div>

                    <div className="text-right hidden sm:block">
                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Piutang (Sisa)</p>
                      <p className={`font-bold text-xs ${displayPiutang > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                        Rp {fmt(displayPiutang)}
                      </p>
                    </div>

                    <div className="text-right min-w-[120px]">
                      <p className={`font-black text-xs ${margin >= 0 ? 'text-green-700' : 'text-rose-600'}`}>
                        {margin >= 0 ? '+' : ''}Rp {fmt(margin)}
                      </p>
                      <span className={`text-[9px] font-black inline-block px-1.5 py-0.5 rounded border mt-0.5 ${
                        margin >= 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-rose-50 text-rose-700 border-rose-200'
                      }`}>
                        {marginPercentage}% Margin
                      </span>
                    </div>

                    {!isSelectionMode && (
                      <div className="flex items-center gap-1">
                        <button className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Accordion Content */}
                  {isExpanded && !isSelectionMode && (
                    <div className="px-6 pb-6 pt-2 bg-slate-50/40 border-t border-slate-100 space-y-5">
                      <ErrorBoundary>
                      {/* === CORE ACTIONS === */}
                      <div className="flex items-center gap-2 flex-wrap bg-white p-3 rounded-xl border border-slate-200/80 shadow-inner justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                          {inv.source === 'client_po' && inv.isSample ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs h-8 text-purple-700 gap-1 hover:bg-purple-50 border border-purple-200 bg-white font-bold"
                              onClick={() => {
                                const rawProducts = Array.isArray(inv.products) ? inv.products : [];
                                const brandName = (inv.client || '').includes(' / ') ? inv.client.split(' / ')[0].trim() : inv.client;
                                
                                const nameCounts = {};
                                const products = rawProducts.map((p, idx) => {
                                  const volume = 20; // Default SPL size
                                  const prodName = p.nama_product || '';
                                  nameCounts[prodName] = nameCounts[prodName] || 0;
                                  const labelStr = ['A','B','C','D','E','F'][nameCounts[prodName] % 6] || 'A';
                                  nameCounts[prodName]++;
                                  
                                  const racikan = (p.racikan || []).map(r => ({
                                    kategori_bahan: r.kategori || 'Bibit',
                                    vendor: r.vendor || '',
                                    nama_bahan: r.bahan_nama || r.nama_bahan || '',
                                    persentase: r.persentase || 0,
                                    volume_ml: parseFloat(((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100 * volume).toFixed(2)),
                                    dari_client: r.dari_client || false
                                  }));
                                  
                                  return {
                                    _id: Date.now() + idx,
                                    nama_produk: prodName,
                                    ukuran_ml: volume,
                                    label: labelStr,
                                    racikan,
                                    status: 'pending',
                                    catatan_review: ''
                                  };
                                });

                                setSampleFormData({
                                  nama_brand: brandName,
                                  products: products.length > 0 ? products : [{
                                    _id: Date.now(),
                                    nama_produk: '',
                                    ukuran_ml: 0,
                                    label: 'A',
                                    racikan: [],
                                    status: 'pending',
                                    catatan_review: ''
                                  }],
                                  pic_brand: inv.rawRecord?.pic || '',
                                  kategori: 'SPL',
                                  pic_pembuat: '',
                                  catatan: ''
                                });
                              }}
                            >
                              <Package className="w-3.5 h-3.5" />
                              <span>Buat PO Sample</span>
                            </Button>
                          ) : (inv.source === 'client_po' && !isLegalitasOnly(inv)) && (
                            <>
                              {(() => {
                                const isExistingPO = Boolean(productionOrders.find(p => 
                                  p.order_number === inv.id || 
                                  (p.notes && p.notes.includes(inv.id)) ||
                                  (inv.id && p.order_number && String(p.order_number).replace(/^(PO-|INV-|SPH-)/i, '').trim().toLowerCase() === String(inv.id).replace(/^(PO-|INV-|SPH-)/i, '').trim().toLowerCase())
                                ));
                                const isPendingUpdate = Boolean(inv.has_pending_po_update || inv.statusProduksi === 'UPDATE PO');
                                const isBelumNaik = inv.statusProduksi === 'BELUM NAIK PRODUKSI';
                                
                                return (
                                  <AddOrderDialog
                                    initialData={getInitialPOData(inv)}
                                    onSave={async (data) => {
                                      if (isPendingUpdate || isExistingPO) {
                                        setPendingAsaPoUpdate({
                                          invoice: inv,
                                          poData: data
                                        });
                                        return Promise.resolve();
                                      } else {
                                        return createPOMutation.mutateAsync(data);
                                      }
                                    }}
                                    trigger={
                                      <Button
                                        variant={isPendingUpdate ? "default" : isExistingPO ? "outline" : "ghost"}
                                        size="sm"
                                        className={`text-xs h-8 gap-1 font-bold flex-1 md:flex-none transition-all shadow-sm ${
                                          isPendingUpdate 
                                            ? 'bg-amber-600 hover:bg-amber-700 text-white rounded-lg border-transparent' 
                                            : isExistingPO
                                            ? 'text-amber-800 hover:bg-amber-50 border border-amber-300 bg-amber-50/40 rounded-md'
                                            : 'text-[#8C6208] hover:bg-[#F9F5EC] border border-[#E6D5B8] bg-white rounded-md'
                                        }`}
                                        disabled={inv.statusProduksi === 'PESANAN SELESAI' || inv.statusProduksi === 'SELESAI'}
                                        title={isBelumNaik ? "Buat PO Produksi" : "Update / Sinkronkan data ke PO Produksi"}
                                      >
                                        <Package className="w-3.5 h-3.5" />
                                        <span>
                                          {isBelumNaik ? 'Buat PO Produksi' : 'Update PO Produksi'}
                                        </span>
                                      </Button>
                                    }
                                  />
                                );
                              })()}
                              {(inv.statusProduksi !== 'PESANAN SELESAI' && inv.statusProduksi !== 'SELESAI') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs h-8 text-emerald-700 gap-1 hover:bg-emerald-50 border border-emerald-200 bg-white font-bold"
                                  onClick={async () => {
                                      const normalizeCode = (str) => String(str || '').replace(/^(PO-|INV-|SPH-)/i, '').trim().toLowerCase();
                                      const realPO = productionOrders.find(p => 
                                        p.id === inv.id || 
                                        p.order_number === inv.id || 
                                        p.order_number === inv.invoice_number ||
                                        p.invoice_id === inv.id ||
                                        p.client_po_id === inv.id ||
                                        (p.notes && p.notes.includes(inv.id)) ||
                                        (inv.id && p.order_number && normalizeCode(p.order_number) === normalizeCode(inv.id))
                                      );

                                      const tid = toast.loading('Menyelesaikan pesanan...');
                                      try {
                                        if (realPO) {
                                          await base44.entities.ProductionOrder.update(realPO.id, {
                                            status: 'done',
                                            is_shipped: true,
                                            diselesaikan_langsung: true,
                                            completion_type: 'direct',
                                          });
                                          try {
                                            const tasksForPO = await base44.entities.ProductionTask.filter({ po_id: realPO.id });
                                            for (const t of (tasksForPO || [])) {
                                              if (t.status !== 'completed' && t.status !== 'cancelled') {
                                                await base44.entities.ProductionTask.update(t.id, {
                                                  status: 'completed',
                                                  end_time: new Date().toISOString()
                                                });
                                              }
                                            }
                                          } catch (e) {}
                                        }
                                        const targetInvId = inv.rawRecord?.id || inv.id;
                                        await base44.entities.ClientPO.update(targetInvId, { statusProduksi: 'PESANAN SELESAI' });
                                        queryClient.invalidateQueries({ queryKey: ['production-orders'] });
                                        queryClient.invalidateQueries({ queryKey: ['production-tasks'] });
                                        queryClient.invalidateQueries({ queryKey: ['clientPOs'] });
                                        toast.success('Pesanan & Invoice berhasil diselesaikan!', { id: tid });
                                      } catch (err) {
                                        toast.error('Gagal menyelesaikan: ' + err.message, { id: tid });
                                      }
                                  }}
                                  title={inv.statusProduksi === 'BELUM NAIK PRODUKSI' ? "Gunakan ini untuk memasukkan data tagihan masa lalu tanpa perlu naik ke tim Produksi lagi." : "Tandai pesanan ini selesai."}
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  <span>Selesaikan Langsung</span>
                                </Button>
                              )}
                            </>
                          )}
                          {!isLegalitasOnly(inv) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-8 text-blue-700 gap-1 hover:bg-blue-50 border border-blue-200 bg-white font-bold"
                            onClick={async () => {
                              const rawBrandName = inv.client || 'Satala';
                              const brandName = rawBrandName.includes(' / ') ? rawBrandName.split(' / ')[0].trim() : rawBrandName.trim();
                              
                              const existingRacikan = await base44.entities.RacikanDatabase.filter({ nama_brand: brandName });
                              
                              const duplicateVarian = [];
                              const newProducts = [];
                              
                              (inv.products || []).forEach(p => {
                                const varianName = p.nama_product || 'Varian Default';
                                const ukuranKonsisten = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || 30;
                                const isDuplicate = existingRacikan.some(r => r.nama_varian === varianName && Number(r.ukuran_ml) === Number(ukuranKonsisten));
                                
                                if (isDuplicate) {
                                  duplicateVarian.push(`${varianName} (${ukuranKonsisten}ml)`);
                                } else {
                                  newProducts.push(p);
                                }
                              });
                              
                              const safeInv = { ...inv, parsedBrandName: brandName };

                              if (duplicateVarian.length > 0) {
                                setAsaDialog({
                                  show: true,
                                  duplicateVarian,
                                  brandName,
                                  safeInv,
                                  newProducts
                                });
                                return;
                              }

                              simpanKeBrandDbMut.mutate(safeInv);
                            }}
                          >
                            <Database className="w-3.5 h-3.5" />
                            <span>Simpan ke DB Brand</span>
                          </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-8 text-green-700 gap-1 hover:bg-green-50 border border-green-200 bg-white font-bold"
                            onClick={() => {
                              setEditBayarId(inv.id);
                              setInputBayar('');
                              setCatatanBayar('');
                              // Default ke rekening sesuai PO atau BCA
                              const defaultBank = inv.rawRecord?.paymentMethod || 'bca';
                              setBankBayar(defaultBank);
                            }}
                          >
                            <Wallet className="w-3.5 h-3.5" />
                            <span>Input Pembayaran</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-8 text-rose-700 gap-1 hover:bg-rose-50 border border-rose-200 bg-white font-bold"
                            onClick={() => {
                              setShowKomisiModal(true);
                              setKomisiForm({
                                invId: inv.id,
                                invName: inv.id,
                                picName: inv.pic || inv.rawRecord?.pic || '',
                                bankAsal: 'bca',
                                bankTujuan: '',
                                atasNama: '',
                                nominal: '',
                                catatan: '',
                                rawRecord: inv.rawRecord,
                                source: inv.source
                              });
                            }}
                          >
                            <Banknote className="w-3.5 h-3.5" />
                            <span>Input Komisi</span>
                          </Button>
                          {inv.source === 'client_po' && inv.rawRecord?.poMode === 'custom' && !inv.isSample && (
                            <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-8 text-indigo-700 gap-1 hover:bg-indigo-50 border border-indigo-200 bg-white font-bold"
                            onClick={async () => {
                              toast.loading('Memyiapkan pengajuan Sample...', { id: 'input-sample' });
                              try {
                                const brandName = (inv.client || '').includes(' / ') ? inv.client.split(' / ')[0].trim() : inv.client;
                                const volume = 20; // Default SPL size
                                
                                const nameCounts = {};
                                const products = [];
                                for (let idx = 0; idx < (inv.products || []).length; idx++) {
                                  const p = inv.products[idx];
                                  const prodName = p.nama_product || '';
                                  nameCounts[prodName] = nameCounts[prodName] || 0;
                                  const labelStr = ['A','B','C','D','E','F'][nameCounts[prodName] % 6] || 'A';
                                  nameCounts[prodName]++;
                                  
                                  // Preserve existing bibit/formula if any, but racikan is optional for Marketing
                                  const racikan = [];
                                  for (const r of (p.racikan || [])) {
                                    const bName = r.bahan_nama || r.nama_bahan || '';
                                    racikan.push({
                                      kategori_bahan: r.kategori || 'Bibit',
                                      vendor: r.vendor || '',
                                      nama_bahan: bName,
                                      persentase: r.persentase || 0,
                                      volume_ml: parseFloat(((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100 * volume).toFixed(2)),
                                    });
                                  }
                                  
                                  products.push({
                                    _id: Date.now() + idx,
                                    nama_produk: prodName,
                                    ukuran_ml: volume,
                                    label: labelStr,
                                    racikan: racikan,
                                    status: 'pending',
                                    catatan_review: ''
                                  });
                                }

                                setSampleFormData({
                                  nama_brand: brandName,
                                  products: products.length > 0 ? products : [{
                                    _id: Date.now(),
                                    nama_produk: '',
                                    ukuran_ml: volume,
                                    label: 'A',
                                    racikan: [],
                                    status: 'pending',
                                    catatan_review: ''
                                  }],
                                  pic_brand: inv.rawRecord?.pic || inv.client || '',
                                  kategori: 'SPL',
                                  pic_pembuat: '',
                                  catatan: '',
                                  source_invoice_id: inv.id || inv.rawRecord?.id,
                                  source_invoice_number: inv.invoice_number || inv.no_po || inv.id,
                                  source_invoice_client: inv.client || '',
                                  created_by_role: 'marketing'
                                });
                                toast.dismiss('input-sample');
                              } catch (e) {
                                toast.error('Gagal menyiapkan pengajuan Sample');
                                toast.dismiss('input-sample');
                              }
                            }}
                          >
                            <FlaskConical className="w-3.5 h-3.5" />
                            <span>Sample</span>
                          </Button>
                          )}

                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-8 text-[#8C6208] gap-1 hover:bg-[#F9F5EC] border border-[#E6D5B8] bg-white font-bold"
                            onClick={() => {
                              setAsaPrintModal(inv);
                              setAsaPrintNote('');
                              setAsaPrintDp('');
                            }}
                          >
                            <FileDown className="w-3.5 h-3.5" />
                            <span>Cetak Invoice</span>
                          </Button>

                          {!isLegalitasOnly(inv) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-8 text-blue-700 gap-1 hover:bg-blue-50 border border-blue-200 bg-white font-bold"
                            onClick={() => {
                              const settings = companySettings || {};
                              if (inv.products && inv.products.length > 0) {
                                setSpkSelectedInvoice(inv);
                                setShowSpkModal(true);
                              } else {
                                toast.error('Tidak ada produk di invoice ini untuk dicetak SPK-nya.');
                              }
                            }}
                          >
                            <FileText className="w-3.5 h-3.5" />
                            <span>Cetak SPK</span>
                          </Button>
                          )}
                          {(() => {
                            const normalizeCode = (str) => String(str || '').replace(/^(PO-|INV-|SPH-)/i, '').trim().toLowerCase();
                            const realPO = inv.productionOrder || productionOrders.find(p => 
                              p.id === inv.id || 
                              p.order_number === inv.id || 
                              p.order_number === inv.invoice_number ||
                              p.invoice_id === inv.id ||
                              p.client_po_id === inv.id ||
                              (inv.rawRecord?.production_order_id && p.id === inv.rawRecord.production_order_id) ||
                              (p.notes && p.notes.includes(inv.id)) ||
                              (inv.id && p.order_number && normalizeCode(p.order_number) === normalizeCode(inv.id)) ||
                              (inv.rawRecord?.id && p.order_number && normalizeCode(p.order_number) === normalizeCode(inv.rawRecord.id))
                            );

                            const sUpper = String(inv.statusProduksi || '').toUpperCase();
                            const hasSuratJalan = Boolean(
                              (realPO && (
                                realPO.is_shipped || 
                                (realPO.shipments && realPO.shipments.length > 0) || 
                                realPO.delivery_info || 
                                realPO.surat_jalan
                              )) ||
                              ['SEDANG DALAM PENGIRIMAN', 'DALAM PENGIRIMAN', 'SHIPPING', 'SUDAH SAMPAI', 'SELESAI', 'PESANAN SELESAI', 'SIAP KIRIM'].includes(sUpper) ||
                              sUpper.includes('PENGIRIMAN') ||
                              sUpper.includes('SELESAI') ||
                              sUpper.includes('KIRIM')
                            );

                            if (!hasSuratJalan) return null;

                            return (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs h-8 text-emerald-700 gap-1 hover:bg-emerald-50 border border-emerald-200 bg-white font-bold"
                                onClick={() => {
                                  if (realPO) {
                                    if (realPO.shipments && realPO.shipments.length > 1) {
                                      setPendingAsaSuratJalan(realPO);
                                    } else {
                                      setShowSuratJalanFor(realPO);
                                      if (realPO.shipments && realPO.shipments.length === 1) {
                                        setShowSuratJalanShipment(realPO.shipments[0]);
                                      } else {
                                        setShowSuratJalanShipment(null);
                                      }
                                    }
                                  } else {
                                    const mockPO = {
                                      id: inv.id,
                                      order_number: inv.id,
                                      brand_name: inv.client,
                                      shipped_at: new Date().toISOString(),
                                      products: (inv.products || []).map(p => ({
                                        product_name: p.nama_product || p.item_name || 'Produk',
                                        target_qty: p.qty || 1,
                                        ukuran_botol_ml: p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || 0
                                      })),
                                      shipping_via: '',
                                      ongkir_amount: 0,
                                      tracking_number: '',
                                      shipping_address: '',
                                    };
                                    setShowSuratJalanFor(mockPO);
                                    setShowSuratJalanShipment(null);
                                  }
                                }}
                              >
                                <Truck className="w-3.5 h-3.5" />
                                <span>Surat Jalan</span>
                              </Button>
                            );
                          })()}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-8 text-blue-700 gap-1 hover:bg-blue-50 border border-blue-200 bg-white font-bold"
                            onClick={() => {
                              navigate(isMarketing ? `/office/marketing/calculator?edit=${inv.id}` : `/office/finance/calculator?edit=${inv.id}`);
                            }}
                          >
                            <FileText className="w-3.5 h-3.5" />
                            <span>Edit Invoice</span>
                          </Button>
                          <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs h-8 text-red-700 gap-1 hover:bg-red-50 border border-red-200 bg-white font-bold"
                              onClick={(e) => {
                                e.stopPropagation();
                                startSelection(inv.id);
                              }}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Hapus</span>
                            </Button>
                        </div>
                        <div className="text-xs text-slate-400 font-semibold italic flex items-center gap-2">
                          <span>Deadline: {inv.deadline ? formatTanggalIndo(inv.deadline) : '—'}</span>
                          {inv.shippingCost > 0 && <span> | Ekspedisi ({inv.rawRecord?.courier || 'Kurir'}): Rp {fmt(inv.shippingCost)}</span>}
                          {inv.rawRecord?.updated_at && (
                            <span className="text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                              (Edited: {formatTanggalIndo(inv.rawRecord.updated_at)})
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Payment inputs */}
                      {editBayarId === inv.id && inv.status !== 'MENUNGGU VERIFIKASI' && (() => {
                        const sisaTagihan = inv.totalTagihan - inv.jumlahDibayar;
                        const sudahBayarPct = inv.totalTagihan > 0 ? Math.round((inv.jumlahDibayar / inv.totalTagihan) * 100) : 0;
                        const sisaPct = 100 - sudahBayarPct;
                        const sudahPernah = inv.jumlahDibayar > 0;

                        return (
                        <Card className="border border-green-200 bg-green-50/40 p-4 rounded-xl space-y-3">
                          <p className="text-xs font-bold text-green-800 uppercase tracking-wider">Input Kas Masuk (Cicilan/DP/Lunas)</p>

                          {/* ASA Info — sisa tagihan */}
                          {sudahPernah && (
                            <div className="relative overflow-hidden rounded-2xl border border-[#E6D5B8]/60 bg-gradient-to-br from-[#FFFDF9] via-[#FFF8EE] to-[#FFF3E0] p-4 shadow-sm">
                              {/* Decorative corner */}
                              <div className="absolute -top-6 -right-6 w-20 h-20 bg-[#E6D5B8]/15 rounded-full blur-xl" />
                              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-emerald-200/10 rounded-full blur-lg" />
                              
                              <div className="relative flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C9A96E] to-[#8C6208] flex items-center justify-center shadow-md shrink-0 mt-0.5">
                                  <span className="text-white text-xs font-black">A</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-[11px] font-extrabold text-[#5C4A2A] tracking-wide">ASA</span>
                                    <span className="text-[9px] bg-[#C9A96E]/15 text-[#8C6208] px-2 py-0.5 rounded-full font-bold border border-[#C9A96E]/20">💡 Info Pembayaran</span>
                                  </div>
                                  <p className="text-[11px] text-[#5C4A2A]/90 leading-relaxed">
                                    Haii kak~ invoice ini udah masuk pembayaran <strong className="text-[#5C4A2A]">Rp {fmt(inv.jumlahDibayar)}</strong> ({sudahBayarPct}%).{' '}
                                    {sisaPct > 0 ? (
                                      <>Sisa tagihannya tinggal <strong className="text-rose-600">Rp {fmt(sisaTagihan)}</strong> ({sisaPct}%) lagi ya! Semangaatt! 💪✨</>
                                    ) : (
                                      <>Waaah udah <strong className="text-emerald-600">LUNAS</strong> dong! 🎉🥳</>
                                    )}
                                  </p>

                                  {/* Progress bar */}
                                  <div className="mt-3 space-y-1.5">
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 bg-[#E6D5B8]/30 rounded-full overflow-hidden h-3 border border-[#E6D5B8]/40 shadow-inner">
                                        <div
                                          className="h-full rounded-full transition-all duration-700 ease-out shadow-sm"
                                          style={{
                                            width: `${Math.min(sudahBayarPct, 100)}%`,
                                            background: sudahBayarPct >= 100
                                              ? 'linear-gradient(90deg, #10b981, #059669)'
                                              : sudahBayarPct >= 50
                                                ? 'linear-gradient(90deg, #C9A96E, #8C6208)'
                                                : 'linear-gradient(90deg, #f59e0b, #d97706)'
                                          }}
                                        />
                                      </div>
                                      <span className="text-[11px] font-black text-[#5C4A2A] min-w-[36px] text-right">{sudahBayarPct}%</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <div className="flex items-center gap-1.5">
                                        <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-sm" />
                                        <span className="text-[10px] font-bold text-emerald-700">Terbayar Rp {fmt(inv.jumlahDibayar)}</span>
                                      </div>
                                      {sisaPct > 0 && (
                                        <div className="flex items-center gap-1.5">
                                          <div className="w-2 h-2 rounded-full bg-rose-400 shadow-sm" />
                                          <span className="text-[10px] font-bold text-rose-600">Sisa Rp {fmt(sisaTagihan)}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex items-center gap-1.5 flex-1 min-w-[200px]">
                              <span className="text-xs font-semibold text-slate-500">Rp</span>
                              <Input
                                autoFocus
                                value={inputBayar}
                                onChange={e => handleInputBayarChange(e, inv)}
                                placeholder={sudahPernah ? `Sisa Rp ${fmt(sisaTagihan)} — ketik nominal / %` : "Ketik nominal / persenan (cth: 50%)"}
                                className="h-9 text-xs bg-white"
                              />
                            </div>
                            <div className="flex-1 min-w-[160px]">
                              <Select 
                                value={bankBayar}
                                onValueChange={setBankBayar}
                              >
                                <SelectTrigger className="h-9 w-full text-xs bg-white border-slate-200 focus:ring-green-400">
                                  <SelectValue placeholder="Pilih rekening bank" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                  {Object.keys(bankAccounts).map(key => (
                                    <SelectItem key={key} value={key} className="text-xs">
                                      {bankAccounts[key]?.name || key.toUpperCase()} ({(bankAccounts[key]?.nama_bank || key).toUpperCase()})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex-1 min-w-[150px]">
                              <CustomDatePicker
                                value={inputTanggalBayar}
                                onChange={setInputTanggalBayar}
                                className="h-9 text-xs bg-white w-full"
                              />
                            </div>
                            <Input
                              value={catatanBayar}
                              onChange={(e) => setCatatanBayar(e.target.value)}
                              placeholder="Catatan (misal: Pelunasan sisa 50%)"
                              className="h-9 text-xs bg-white flex-1 min-w-[200px]"
                            />
                            <Button disabled={isSavingPayment} size="sm" className="h-9 px-3 gap-1.5 bg-green-700 hover:bg-green-800" onClick={() => saveBayar(inv)}>
                              <Save className="w-3.5 h-3.5" />
                              <span>{isSavingPayment ? 'Menyimpan...' : 'Simpan'}</span>
                            </Button>
                            <Button disabled={isSavingPayment} variant="ghost" size="sm" className="h-9 px-3 text-slate-500 hover:text-slate-800" onClick={() => { setEditBayarId(null); setInputBayar(''); setCatatanBayar(''); setBankBayar(''); setInputTanggalBayar(''); }}>
                              <span>Batal</span>
                            </Button>
                          </div>
                          <p className="text-[10px] text-slate-500 font-medium">*Bisa pakai persen, contoh: ketik "50%" otomatis menghitung Rp {fmt(inv.totalTagihan / 2)}</p>
                        </Card>
                        );
                      })()}

                      {/* PIC inputs — Searchable & terintegrasi Manajemen Tim */}
                      {editPicId === inv.id && (
                        <Card className="border border-indigo-200 bg-indigo-50/40 p-4 rounded-xl">
                          <p className="text-xs font-bold text-indigo-800 uppercase tracking-wider mb-2">Assign Penanggung Jawab (PIC)</p>
                          <div className="flex items-end gap-3 flex-wrap">
                            <div className="flex-1 min-w-[240px]">
                              <PicSelector
                                value={inputPic}
                                onChange={(name) => setInputPic(name)}
                                label=""
                                placeholder="Cari nama tim..."
                                requiredPageAccessKey="marketing_invoices"
                                roleLabelMap={{
                                  admin: 'Admin',
                                  supervisor_office: 'Spv Office',
                                  supervisor: 'Spv Produksi',
                                  operator_office: 'Operator Office',
                                  operator_produksi: 'Operator Produksi',
                                  marketing: 'Marketing',
                                }}
                              />
                            </div>
                            <Button size="sm" className="h-9 px-3 gap-1.5 bg-indigo-700 hover:bg-indigo-800" onClick={() => handleSimpanPic(inv, inputPic)}>
                              <Save className="w-3.5 h-3.5" />
                              <span>Simpan</span>
                            </Button>
                            <Button variant="ghost" size="sm" className="h-9 px-3 text-slate-500 hover:text-slate-800" onClick={() => { setEditPicId(null); setInputPic(''); }}>
                              <span>Batal</span>
                            </Button>
                          </div>
                        </Card>
                      )}

                      {/* Verification Panel */}
                      {inv.status === 'MENUNGGU VERIFIKASI' && (
                        <Card className="border border-blue-200 bg-blue-50/40 p-4 rounded-xl">
                          <p className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-2">Verifikasi Pembayaran (Mutasi BCA)</p>
                          <div className="flex flex-col gap-3">
                            <div className="flex items-center justify-between text-xs mb-2">
                              <span className="text-slate-600">Total Tagihan:</span>
                              <span className="font-bold text-blue-800">Rp {fmt(inv.rawRecord?.riwayatPembayaran?.[0]?.jumlah || 0)}</span>
                              {inv.rawRecord?.riwayatPembayaran?.[0]?.status_konfirmasi === 'Menunggu Konfirmasi Finance' && (
                                <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded ml-2 whitespace-nowrap">
                                  Menunggu Konfirmasi
                                </span>
                              )}
                            </div>
                            {inv.rawRecord?.riwayatPembayaran?.[0]?.catatan?.includes('tidak sesuai') && (
                              <div className="bg-rose-100 text-rose-700 p-2 rounded text-xs font-semibold mb-2 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded ml-2 whitespace-nowrap overflow-hidden text-ellipsis flex-1">
                                  Terdapat catatan: {inv.rawRecord.riwayatPembayaran[0].catatan}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-3 flex-wrap">
                              <div className="flex items-center gap-1.5 flex-1 min-w-[200px]">
                                <span className="text-xs font-semibold text-slate-500">Rp</span>
                                <Input
                                  autoFocus
                                  value={inputBayar}
                                  onChange={(e) => setInputBayar(e.target.value)}
                                  placeholder="Nominal Aktual Transfer (Contoh: 12000000)"
                                  className="h-9 text-xs bg-white"
                                />
                              </div>
                              <Button size="sm" className="h-9 px-3 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => verifikasiBayar(inv)}>
                                <ShieldCheck className="w-3.5 h-3.5" />
                                <span>Verifikasi Valid</span>
                              </Button>
                            </div>
                          </div>
                        </Card>
                      )}

                      {/* === DETAILED ACCORDION (PACKAGING, FORMULA, LEGALITAS) === */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        
                        {/* LEFT: PRODUCTS LISTING (with exact formula HPP breakdowns) */}
                        <div className="space-y-4">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Breakdown Produk & Formula Racikan</p>
                          
                          {isLegalitasOnly(inv) ? (
                            <div className="p-4 bg-amber-50/70 border border-amber-200 rounded-xl text-amber-900 text-xs flex items-start gap-3 shadow-2xs">
                              <div className="w-9 h-9 rounded-lg bg-amber-200/60 flex items-center justify-center shrink-0 mt-0.5 border border-amber-300">
                                <ShieldCheck className="w-5 h-5 text-amber-800" />
                              </div>
                              <div>
                                <p className="font-bold text-sm text-amber-950">Invoice Layanan Legalitas & Sertifikasi Saja</p>
                                <p className="text-[11px] text-amber-800 mt-1 leading-relaxed">
                                  Invoice ini khusus untuk pengurusan legalitas ({((inv.legalitas || inv.rawRecord?.legalitas || []).map(l => l.nama)).join(', ') || 'Halal / BPOM'}) tanpa pesanan produk fisik. Tidak memerlukan SPK pabrik, peracikan bibit, maupun kemasan botol.
                                </p>
                              </div>
                            </div>
                          ) : (
                            (inv.products || []).map((p, idx) => {
                            return (
                              <Card key={idx} className="border border-slate-200/80 shadow-sm rounded-xl overflow-hidden bg-white">
                                <CardContent className="p-4 space-y-3">
                                  <div className="flex justify-between items-start border-b border-slate-100 pb-2">
                                    <div>
                                      <p className="font-bold text-sm text-slate-800">{p.nama_product}</p>
                                      <p className="text-[10px] font-semibold text-[#8C6208]">Volume: {p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || 0}ml · Qty: {p.qty || 1} pcs</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Harga Jual</p>
                                      <p className="font-black text-xs text-slate-800">Rp {fmt(p.harga_jual_per_pcs || p.total_harga_jual / (p.qty || 1))}/pcs</p>
                                    </div>
                                  </div>

                                  {/* Breakdown Modal vs Margin (Mirip Kalkulator) */}
                                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-xs space-y-3">
                                    {/* Detailed Item List */}
                                    <div className="space-y-2 pb-3 border-b border-slate-200">
                                      <p className="font-bold text-slate-700 text-[11px] uppercase tracking-wider">Rincian Item & Formula</p>
                                      
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="bg-white p-2.5 rounded border border-slate-100 shadow-sm">
                                          <p className="font-bold text-slate-600 mb-1.5 border-b border-slate-50 pb-1">Kemasan / Packaging</p>
                                          {p.is_discovery_set ? (
                                            <div className="text-[10px] text-slate-500">
                                              <p className="italic mb-1 text-slate-400">Total sub-variant Kemasan:</p>
                                              <ul className="space-y-1">
                                                {(p.items || []).map((it, idx) => {
                                                  const iBotol = botolList.find(b => b.id === it.botol_id);
                                                  const iTutup = tutupList.find(t => t.id === it.tutup_id);
                                                  const iSpray = sprayList.find(s => s.id === it.spray_id);
                                                  const iFullset = fullsetList?.find(f => f.id === it.fullset_id);
                                                  const mBotol = (it.botol_dari_client || !iBotol) ? 0 : iBotol.harga_rupiah;
                                                  const mTutup = (it.tutup_dari_client || !iTutup) ? 0 : iTutup.harga_rupiah;
                                                  const mSpray = (it.spray_dari_client || !iSpray) ? 0 : iSpray.harga_rupiah;
                                                  const mFullset = iFullset ? iFullset.harga_rupiah : 0;
                                                  const subModal = it.is_fullset ? mFullset : (mBotol + mTutup + mSpray);
                                                  return (
                                                    <li key={idx} className="flex justify-between border-b border-slate-50 pb-0.5 last:border-0 last:pb-0">
                                                      <span className="truncate pr-2">- {it.nama_variant || `Variant ${idx+1}`}</span>
                                                      <span className="shrink-0 font-medium whitespace-nowrap">Modal: Rp {fmt(subModal)}</span>
                                                    </li>
                                                  );
                                                })}
                                              </ul>
                                            </div>
                                          ) : p.is_fullset || p.hpp_kemasan !== undefined ? (
                                            <ul className="text-[10px] space-y-1 text-slate-500">
                                              <li className="flex justify-between">
                                                <span>Fullset: {p.fullset_nama || p.botol_nama || 'Kemasan Fullset / Sample'}</span>
                                                <span className="font-medium">Rp {fmt(p.fullset_harga_beli || p.hpp_kemasan || 0)}</span>
                                              </li>
                                            </ul>
                                          ) : (
                                            <ul className="text-[10px] space-y-1 text-slate-500">
                                              <li className="flex justify-between">
                                                <span>Botol: {p.botol_nama === 'Dari Client' ? 'Botol Dari Client' : (p.botol_nama || 'Botol Dari Client')}</span>
                                                <span className="font-medium">Rp {fmt(p.botol_harga || p.botol_harga_beli)}</span>
                                              </li>
                                              <li className="flex justify-between">
                                                <span>Tutup: {p.tutup_nama === 'Dari Client' ? 'Tutup Dari Client' : (p.tutup_nama || 'Tutup Dari Client')}</span>
                                                <span className="font-medium">Rp {fmt(p.tutup_harga || p.tutup_harga_beli)}</span>
                                              </li>
                                              <li className="flex justify-between">
                                                <span>Spray: {p.spray_nama === 'Dari Client' ? 'Spray Dari Client' : (p.spray_nama || 'Spray Dari Client')}</span>
                                                <span className="font-medium">Rp {fmt(p.spray_harga || p.spray_harga_beli)}</span>
                                              </li>
                                            </ul>
                                          )}
                                        </div>

                                        <div className="bg-white p-2.5 rounded border border-slate-100 shadow-sm">
                                          {p.is_discovery_set ? (
                                            <>
                                              <p className="font-bold text-slate-600 mb-1.5 border-b border-slate-50 pb-1">Formula Cairan (Multi-variant)</p>
                                              <div className="text-[10px] text-slate-500">
                                                <ul className="space-y-1">
                                                  {(p.items || []).map((it, idx) => {
                                                    const regexMatch = it.nama_variant?.match(/\((\d+)ml\)/);
                                                    const fallbackMl = regexMatch ? parseFloat(regexMatch[1]) : 0;
                                                    const volVariant = it.custom_ukuran_aktual_ml !== undefined ? it.custom_ukuran_aktual_ml : (it.botol_ukuran_aktual || it.botol_ukuran_label || it.botol_ukuran_client || it.ukuran_botol_ml || fallbackMl);
                                                    
                                                    const itModalRacikan = (it.racikan || []).reduce((sum, r) => {
                                                      const hSatuan = (r.satuan || 'kg').toLowerCase();
                                                      const hBeliSat = r.harga_per_satuan || r.hpp_bahan || 0;
                                                      const hBeliPerKecil = (hSatuan === 'kg' || hSatuan === 'liter' || hSatuan === 'g' || hSatuan === 'gram') ? (hBeliSat / 1000) : hBeliSat;
                                                      const volMl = ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * volVariant;
                                                      return sum + (hBeliPerKecil * volMl);
                                                    }, 0);
                                                    return (
                                                      <li key={idx} className="flex justify-between border-b border-slate-50 pb-0.5 last:border-0 last:pb-0">
                                                        <span className="truncate pr-2">- {it.nama_variant || `Variant ${idx+1}`}</span>
                                                        <span className="shrink-0 font-medium whitespace-nowrap">Modal: Rp {fmt(itModalRacikan)}</span>
                                                      </li>
                                                    );
                                                  })}
                                                </ul>
                                              </div>
                                            </>
                                          ) : (() => {
                                            const volume = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || p.volume_ml || 0;
                                            return (
                                              <>
                                                <p className="font-bold text-slate-600 mb-1.5 border-b border-slate-50 pb-1">Formula Cairan ({volume} ml)</p>
                                                <ul className="text-[10px] space-y-1 text-slate-500">
                                                  {(p.racikan || []).map((r, ri) => {
                                                    const volPerPcs = volume * ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100);
                                                    const bMatch = bahanList.find(x => (x.nama || '').trim().toLowerCase() === (r.bahan_nama || r.nama_bahan || '').trim().toLowerCase());
                                                    const hBeliSat = r.harga_per_satuan || r.hpp_bahan || (bMatch?.harga_beli || bMatch?.harga_satuan || bMatch?.harga_rupiah || bMatch?.harga || 0);
                                                    const hBeliPerKecil = hBeliSat / 1000;
                                                    const volMl = r.volume_ml || volPerPcs;
                                                    const isDariClient = r.dari_client === true || r.vendor === 'Client';
                                                    const calculatedHpp = isDariClient ? 0 : (hBeliPerKecil * volMl);
                                                    const vendorDisplay = r.vendor || bMatch?.vendor || '';
                                                    const namaDisplay = r.bahan_nama || r.nama_bahan || (isDariClient ? `${r.kategori || 'Bahan'} Dari Client` : 'Bahan');
                                                    return (
                                                      <li key={ri} className="flex justify-between pb-1 border-b border-slate-50 last:border-0 last:pb-0">
                                                        <span>{vendorDisplay ? `[${vendorDisplay}] ` : ''}{namaDisplay} {isDariClient ? '(Dari Client) ' : ''}<span className="text-slate-400 ml-0.5">({+(parseFloat(String(r.persentase).replace(',', '.')) || 0).toFixed(2)}% - {+(volMl).toFixed(2)}ml)</span></span>
                                                        <span className="font-medium">Rp {fmt(calculatedHpp)}</span>
                                                      </li>
                                                    );
                                                  })}
                                                </ul>
                                              </>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                      
                                      {/* Komponen Tambahan Box */}
                                      {(p.komponen_tambahan || []).length > 0 && (
                                        <div className="bg-white p-2.5 rounded border border-slate-100 shadow-sm mt-4">
                                          <p className="font-bold text-slate-600 mb-1.5 border-b border-slate-50 pb-1">Item Tambahan / Aksesoris</p>
                                          <ul className="text-[10px] space-y-1 text-slate-500">
                                            {p.komponen_tambahan.map((k, ki) => {
                                              const name = k.item_nama || k.nama_item || k.nama;
                                              const found = itemTambahanList.find(i => i.nama === name);
                                              const modalValue = (k.harga && k.harga > 0) ? k.harga : (!k.dari_client && found ? found.harga_rupiah || 0 : 0);
                                              return (
                                              <li key={ki} className="flex justify-between border-b border-slate-50 pb-0.5 last:border-0 last:pb-0">
                                                <span>- {name || 'Item Tambahan'} {k.qty > 1 ? `(x${k.qty})` : ''} {k.dari_client ? '(Dari Client)' : ''}</span>
                                                <span className="font-medium">Modal: Rp {fmt(modalValue * (k.qty || 1))}</span>
                                              </li>
                                            )})}
                                          </ul>
                                        </div>
                                      )}
                                    </div>

                                    {/* Cost Summary */}
                                    <div className="space-y-1">
                                    {(() => {
                                      const fallbackFullset = p.is_fullset ? fullsetList.find(f => f.id === p.fullset_id) : null;
                                      const getHrgJual = (item) => item?.harga_jual || ((item?.harga_rupiah || 0) * 1.5);
                                      
                                      let modalPackaging = p.is_fullset || p.hpp_kemasan !== undefined ? (p.fullset_harga_beli || p.hpp_kemasan || fallbackFullset?.harga_rupiah || 0) : ((p.botol_harga_beli || 0) + (p.tutup_harga_beli || 0) + (p.spray_harga_beli || 0));
                                      let jualPackaging = p.is_fullset || p.hpp_kemasan !== undefined ? (p.fullset_harga_jual || getHrgJual(fallbackFullset) || (modalPackaging * 1.5)) : ((p.botol_harga || (p.botol_harga_beli * 1.5) || 0) + (p.tutup_harga || (p.tutup_harga_beli * 1.5) || 0) + (p.spray_harga || (p.spray_harga_beli * 1.5) || 0));
                                      
                                      const volume = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || p.volume_ml || 0;
                                      
                                      let modalRacikan = (p.racikan || []).reduce((s, r) => {
                                        const hBeliSat = r.harga_per_satuan || 0;
                                        const hBeliPerKecil = hBeliSat / 1000;
                                        const volMl = r.volume_ml || (((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * volume);
                                        return s + (hBeliPerKecil * volMl);
                                      }, 0);
                                      
                                      let jualRacikan = (p.racikan || []).reduce((s, r) => {
                                        const hJualSat = r.harga_jual_satuan !== undefined ? r.harga_jual_satuan : 0;
                                        const hJualKecil = r.custom_harga_per_unit || (hJualSat ? hJualSat / 1000 : (r.harga_per_satuan * 1.3 / 1000));
                                        const volMl = r.volume_ml || (((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * volume);
                                        return s + (hJualKecil * volMl);
                                      }, 0);
                                      
                                      // Override for Discovery Set
                                      if (p.is_discovery_set && p.items) {
                                        modalPackaging = 0;
                                        jualPackaging = 0;
                                        modalRacikan = 0;
                                        jualRacikan = 0;
                                        p.items.forEach(it => {
                                          const iBotol = botolList.find(b => b.id === it.botol_id);
                                          const iTutup = tutupList.find(t => t.id === it.tutup_id);
                                          const iSpray = sprayList.find(s => s.id === it.spray_id);
                                          const iFullset = fullsetList?.find(f => f.id === it.fullset_id);
                                          
                                          const mBotol = (it.botol_dari_client || !iBotol) ? 0 : iBotol.harga_rupiah;
                                          const mTutup = (it.tutup_dari_client || !iTutup) ? 0 : iTutup.harga_rupiah;
                                          const mSpray = (it.spray_dari_client || !iSpray) ? 0 : iSpray.harga_rupiah;
                                          const mFullset = iFullset ? iFullset.harga_rupiah : 0;
                                          
                                          const jBotol = (it.botol_dari_client || !iBotol) ? 0 : (it.botol_harga_jual !== undefined ? it.botol_harga_jual : (iBotol.harga_jual || mBotol * 1.5));
                                          const jTutup = (it.tutup_dari_client || !iTutup) ? 0 : (it.tutup_harga_jual !== undefined ? it.tutup_harga_jual : (iTutup.harga_jual || mTutup * 1.5));
                                          const jSpray = (it.spray_dari_client || !iSpray) ? 0 : (it.spray_harga_jual !== undefined ? it.spray_harga_jual : (iSpray.harga_jual || mSpray * 1.5));
                                          const jFullset = iFullset ? (it.fullset_harga_jual !== undefined ? it.fullset_harga_jual : (iFullset.harga_jual || mFullset * 1.5)) : 0;

                                          modalPackaging += it.is_fullset ? mFullset : (mBotol + mTutup + mSpray);
                                          jualPackaging += it.is_fullset ? jFullset : (jBotol + jTutup + jSpray);
                                          
                                          const regexMatch = it.nama_variant?.match(/\((\d+)ml\)/);
                                          const fallbackMl = regexMatch ? parseFloat(regexMatch[1]) : 0;
                                          const volVariant = it.custom_ukuran_aktual_ml !== undefined ? it.custom_ukuran_aktual_ml : (it.botol_ukuran_aktual || it.botol_ukuran_label || it.botol_ukuran_client || it.ukuran_botol_ml || fallbackMl);
                                          
                                          (it.racikan || []).forEach(r => {
                                            const hSatuan = (r.satuan || 'kg').toLowerCase();
                                            const hBeliSat = r.harga_per_satuan || r.hpp_bahan || 0;
                                            const hBeliPerKecil = (hSatuan === 'kg' || hSatuan === 'liter' || hSatuan === 'g' || hSatuan === 'gram') ? (hBeliSat / 1000) : hBeliSat;
                                            const volMl = ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * volVariant;
                                            modalRacikan += (hBeliPerKecil * volMl);
                                            
                                            const hJualSat = r.harga_jual_satuan !== undefined ? r.harga_jual_satuan : (hBeliSat * 1.3);
                                            const hJualKecil = r.custom_harga_per_unit || ((hSatuan === 'kg' || hSatuan === 'liter' || hSatuan === 'g' || hSatuan === 'gram') ? (hJualSat / 1000) : hJualSat);
                                            jualRacikan += (hJualKecil * volMl);
                                          });
                                        });
                                      }
                                      
                                      let modalKomponen = (p.komponen_tambahan || []).reduce((s, k) => {
                                        const name = k.item_nama || k.nama_item || k.nama;
                                        const found = itemTambahanList.find(i => i.nama === name);
                                        const modalValue = (k.harga && k.harga > 0) ? k.harga : (!k.dari_client && found ? found.harga_rupiah || 0 : 0);
                                        return s + (modalValue * (k.qty || 1));
                                      }, 0);
                                      let jualKomponen = (p.komponen_tambahan || []).reduce((s, k) => {
                                        const name = k.item_nama || k.nama_item || k.nama;
                                        const found = itemTambahanList.find(i => i.nama === name);
                                        const jualValue = (k.harga_jual && k.harga_jual > 0) ? k.harga_jual : (!k.dari_client && found ? found.harga_jual || 0 : 0);
                                        return s + (jualValue * (k.qty || 1));
                                      }, 0);
                                      
                                      const jasaNominal = p.jasa_nominal || p.final_jasa_nominal || 0;
                                      const calculatedJualTotal = jualPackaging + jualRacikan + jualKomponen + jasaNominal;
                                      
                                      // Margin Harga Jual Akhir = selisih harga user vs harga sistem
                                      const finalHargaJual = p.harga_jual_per_pcs || (p.total_harga_jual ? p.total_harga_jual / (p.qty || 1) : calculatedJualTotal);
                                      const marginHargaJualAkhir = finalHargaJual - calculatedJualTotal;
                                      
                                      // Total margin per pcs = semua margin + jasa + margin harga jual akhir
                                      const totalMarginPerPcs = (jualPackaging - modalPackaging) + (jualRacikan - modalRacikan) + (jualKomponen - modalKomponen) + jasaNominal + marginHargaJualAkhir;
                                      
                                      return (
                                        <>
                                          <div className="flex justify-between items-center text-slate-500">
                                            <span>Modal Kemasan (Packaging)</span>
                                            <span>Rp {fmt(modalPackaging)}</span>
                                          </div>
                                          {(jualPackaging - modalPackaging) > 0 && (
                                            <div className="flex justify-between items-center text-emerald-600 font-medium">
                                              <span>{p.is_fullset ? 'Margin Jual Fullset' : 'Margin Jual Kemasan'}</span>
                                              <span>+Rp {fmt(jualPackaging - modalPackaging)}</span>
                                            </div>
                                          )}
                                          <div className="flex justify-between items-center text-slate-500 mt-1">
                                            <span>Modal Formula Cairan ({volume}ml)</span>
                                            <span>Rp {fmt(modalRacikan)}</span>
                                          </div>
                                          {(jualRacikan - modalRacikan) > 0 && (
                                            <div className="flex justify-between items-center text-emerald-600 font-medium">
                                              <span>Margin Jual Cairan</span>
                                              <span>+Rp {fmt(jualRacikan - modalRacikan)}</span>
                                            </div>
                                          )}
                                          <div className="flex justify-between items-center text-slate-500 mt-1">
                                            <span>Modal Kelengkapan Tambahan</span>
                                            <span>Rp {fmt(modalKomponen)}</span>
                                          </div>
                                          {(jualKomponen - modalKomponen) > 0 && (
                                            <div className="flex justify-between items-center text-emerald-600 font-medium">
                                              <span>Margin Kelengkapan Tambahan</span>
                                              <span>+Rp {fmt(jualKomponen - modalKomponen)}</span>
                                            </div>
                                          )}
                                          {Math.round(marginHargaJualAkhir) !== 0 && (
                                            <div className={`flex justify-between items-center font-medium mt-1 ${marginHargaJualAkhir >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                              <span>Penyesuaian Harga Final (Diskon/Markup)</span>
                                              <span>{marginHargaJualAkhir >= 0 ? '+' : '-'}Rp {fmt(Math.abs(marginHargaJualAkhir))}</span>
                                            </div>
                                          )}
                                          {jasaNominal > 0 && (
                                            <div className="flex justify-between items-center text-emerald-600 font-medium mt-1">
                                              <span>Jasa Produksi (Margin)</span>
                                              <span>+Rp {fmt(jasaNominal)}</span>
                                            </div>
                                          )}
                                          <div className="flex justify-between items-center pt-2 font-bold text-slate-800 border-t border-slate-200 mt-2">
                                            <span>Total Harga Sistem (per pcs)</span>
                                            <span>Rp {fmt(calculatedJualTotal)}</span>
                                          </div>
                                          <div className="flex justify-between items-center text-emerald-700 font-bold">
                                            <span>Total Margin (per pcs)</span>
                                            <span>{totalMarginPerPcs >= 0 ? '+' : '-'}Rp {fmt(Math.abs(totalMarginPerPcs))}</span>
                                          </div>
                                        </>
                                      );
                                    })()}
                                  </div>
                                  </div>

                                </CardContent>
                              </Card>
                            );
                          }))}
                        </div>

                        {/* RIGHT: LEGALITY, SHIPPING, & MARGIN ANALYTICS */}
                        <div className="space-y-4">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Analisa Pemasukan & Margin Keuangan</p>

                          {/* Legalitas section */}
                          {(inv.legalitas || []).length > 0 && (
                            <Card className="border border-blue-200 bg-blue-50/20 shadow-sm rounded-xl p-4">
                              <p className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                                <ShieldCheck className="w-4 h-4" />
                                Rincian Biaya Legalitas
                              </p>
                              <div className="space-y-2">
                                {(inv.legalitas || []).map((l, li) => (
                                  <div key={li} className="flex justify-between items-center bg-white border border-blue-100 rounded-lg p-2.5 text-xs">
                                    <div>
                                      <p className="font-bold text-blue-900">{l.nama}{(l.qty_variant || 1) > 1 ? ` (${l.qty_variant} Varian)` : ' (Per Varian)'}</p>
                                      <p className="text-[10px] text-slate-400">Modal HPP: Rp {fmt(l.harga_beli)}{(l.qty_variant || 1) > 1 ? ` × ${l.qty_variant} = Rp ${fmt(l.harga_beli * (l.qty_variant || 1))}` : ''}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-bold text-blue-700">Rp {fmt(l.harga_jual * (l.qty_variant || 1))}</p>
                                      <p className="text-[9px] text-green-700 font-bold">Margin: +Rp {fmt((l.harga_jual - l.harga_beli) * (l.qty_variant || 1))}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </Card>
                          )}

                          {/* Payment history list */}
                          <Card className="border border-slate-200/80 shadow-sm rounded-xl p-4 bg-white">
                            <div className="flex justify-between items-start mb-4">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Mutasi Kas / Uang Masuk</p>
                              <div className="text-right">
                                <p className="text-[10px] font-bold text-slate-400 mb-0.5 tracking-wider">PIUTANG (SISA)</p>
                                <p className="text-sm font-black text-[#E85D04]">Rp {fmt(inv.totalTagihan - inv.jumlahDibayar)}</p>
                              </div>
                            </div>
                            {(inv.riwayatPembayaran || []).length === 0 ? (
                              <p className="text-xs text-slate-400 italic py-2 text-center">Belum ada mutasi dana masuk.</p>
                            ) : (
                              <div className="space-y-2 max-h-[150px] overflow-y-auto pr-1">
                                {(inv.riwayatPembayaran || []).map((rb, ri) => (
                                  <div key={ri} className="flex flex-col gap-1.5 bg-green-50/60 border border-green-100 rounded-lg px-3 py-2 text-xs">
                                    <div className="flex justify-between items-start mt-1">
                                      <p className="text-[10px] text-slate-500 max-w-[80%] leading-relaxed">
                                        Diinput oleh <span className="font-semibold text-slate-500">{rb.diinput_oleh}</span> ke Rekening <span className="font-semibold text-slate-500">{rb.bank_tujuan || 'BCA'}</span>
                                        {rb.catatan && <><br/>Catatan: {rb.catatan}</>}
                                      </p>
                                      <div className="text-right">
                                        <p className="text-xs font-bold text-slate-800 whitespace-nowrap">+ Rp {fmt(rb.jumlah)}</p>
                                        <div className="flex items-center gap-1 justify-end mt-1">
                                          {rb.status_konfirmasi === 'Menunggu Konfirmasi Finance' && (
                                            <span className="text-[9px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded inline-block">Menunggu Konfirmasi</span>
                                          )}
                                          <button 
                                            onClick={(e) => cancelPayment(inv, ri, e)}
                                            className="p-1 hover:bg-rose-100 text-rose-500 rounded"
                                            title="Hapus Riwayat Pembayaran"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                    <span className="text-[9px] font-bold text-green-700 mt-1">{formatTanggalIndo(rb.tanggal_bayar)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </Card>

                          {/* Komisi history list */}
                          {inv.rawRecord?.komisiList && inv.rawRecord.komisiList.length > 0 && (
                            <Card className="border border-amber-200/80 shadow-sm rounded-xl p-4 bg-amber-50 mt-4">
                              <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-2.5 flex items-center gap-1">
                                <Banknote className="w-3.5 h-3.5" /> Pencatatan Komisi Keluar
                              </p>
                              <div className="space-y-2 max-h-[150px] overflow-y-auto pr-1">
                                {inv.rawRecord.komisiList.map((km, ki) => (
                                  <div key={ki} className="flex justify-between items-center bg-white border border-amber-100 rounded-lg px-3 py-2 text-xs shadow-sm">
                                    <div className="flex flex-col gap-1">
                                      <p className="text-[10px] text-slate-500 leading-relaxed">
                                        Ditransfer ke: <span className="font-semibold text-slate-700">{km.atas_nama}</span> ({km.bank_tujuan})<br/>
                                        Diinput oleh <span className="font-semibold text-slate-500">{km.diinput_oleh}</span> dari <span className="font-semibold text-slate-500">{km.bank_asal}</span>
                                        {km.catatan && <><br/>Catatan: {km.catatan}</>}
                                      </p>
                                    </div>
                                    <div className="text-right flex flex-col items-end">
                                      <p className="text-xs font-bold text-rose-600 whitespace-nowrap">- Rp {fmt(km.nominal)}</p>
                                      <span className="text-[9px] font-bold text-amber-600 mt-1">{formatTanggalIndo(km.tanggal)}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </Card>
                          )}

                          {/* Dynamic Margin box */}
                          {(() => {
                            let totalMarginKemasan = 0;
                            let totalMarginCairan = 0;
                            let totalMarginKomponen = 0;
                            let totalJasa = 0;
                            let totalMarginLegalitas = 0;
                            let totalMarginHargaJualAkhir = 0;

                            (inv.products || []).forEach(p => {
                              const fallbackFullset = p.is_fullset ? fullsetList.find(f => f.id === p.fullset_id) : null;
                              const getHrgJual = (item) => item?.harga_jual || ((item?.harga_rupiah || 0) * 1.5);
                              const qty = p.qty || 1;
                              const modalPackaging = p.is_fullset ? (p.fullset_harga_beli || fallbackFullset?.harga_rupiah || 0) : ((p.botol_harga_beli || 0) + (p.tutup_harga_beli || 0) + (p.spray_harga_beli || 0));
                              const jualPackaging = p.is_fullset ? (p.fullset_harga_jual || getHrgJual(fallbackFullset) || (modalPackaging * 1.5)) : ((p.botol_harga || (p.botol_harga_beli * 1.5) || 0) + (p.tutup_harga || (p.tutup_harga_beli * 1.5) || 0) + (p.spray_harga || (p.spray_harga_beli * 1.5) || 0));
                              totalMarginKemasan += (jualPackaging - modalPackaging) * qty;
                              
                              const volume = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || p.volume_ml || 0;
                              // Modal racikan = harga BELI (cost) per pcs
                              const modalRacikan = (p.racikan || []).reduce((s, r) => {
                                const volPerPcs = volume * ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100);
                                const costPerUnit = r.harga_per_satuan || 0;
                                return s + volPerPcs * (costPerUnit / 1000);
                              }, 0);
                              // Jual racikan = harga JUAL per pcs (dihitung ulang agar konsisten dengan ml)
                              const jualRacikan = (p.racikan || []).reduce((s, r) => {
                                const jualPerUnit = r.harga_jual_satuan || (r.harga_per_satuan * 1.3) || 0;
                                const volPerPcs = volume * ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100);
                                return s + volPerPcs * (jualPerUnit / 1000);
                              }, 0);
                              totalMarginCairan += (jualRacikan - modalRacikan) * qty;
                              
                              const modalKomponen = (p.komponen_tambahan || []).reduce((sum, k) => sum + (k.harga || 0), 0);
                              const jualKomponen = (p.komponen_tambahan || []).reduce((sum, k) => sum + (k.harga_jual || 0), 0);
                              totalMarginKomponen += (jualKomponen - modalKomponen) * qty;

                              const jasaNominal = p.jasa_nominal || p.final_jasa_nominal || 0;
                              totalJasa += jasaNominal * qty;

                              // Margin Harga Jual Akhir = selisih harga user vs harga sistem
                              const calculatedJualTotal = jualPackaging + jualRacikan + jualKomponen + jasaNominal;
                              const finalHargaJual = p.harga_jual_per_pcs || (p.total_harga_jual ? p.total_harga_jual / qty : calculatedJualTotal);
                              totalMarginHargaJualAkhir += (finalHargaJual - calculatedJualTotal) * qty;
                            });

                            (inv.legalitas || []).forEach(l => {
                              const qtyVar = l.qty_variant || 1;
                              totalMarginLegalitas += ((l.harga_jual - l.harga_beli) || 0) * qtyVar;
                            });

                            // Total margin = total jual dikurangi (total HPP murni tanpa jasa). Ini menjamin hasilnya selalu 100% sama dengan atas invoice.
                            const totalJasaKeseluruhan = (inv.products || []).reduce((sum, p) => sum + ((p.jasa_nominal || p.final_jasa_nominal || 0) * (p.qty || 1)), 0);
                            const totalKomisi = (inv.rawRecord?.komisiList || []).reduce((sum, k) => sum + (Number(k.nominal) || 0), 0);
                            const hppTanpaJasa = inv.totalHpp - totalJasaKeseluruhan + totalKomisi;
                            const totalMarginKeseluruhan = inv.totalJual - hppTanpaJasa;
                            
                            // Adjust Margin Harga Jual Akhir to absorb any rounding/fallback differences so the breakdown sums up perfectly to totalMarginKeseluruhan
                            const currentSumWithoutAkhir = totalMarginKemasan + totalMarginCairan + totalMarginKomponen + totalJasa + totalMarginLegalitas - totalKomisi;
                            totalMarginHargaJualAkhir = totalMarginKeseluruhan - currentSumWithoutAkhir;

                            const modalBersih = inv.totalJual - totalMarginKeseluruhan;
                            const rawMarginPO = inv.totalJual > 0 ? (totalMarginKeseluruhan / inv.totalJual) * 100 : 0;
                            const marginPersenPO = Number.isFinite(rawMarginPO) ? rawMarginPO : 0;

                            const getAsaPoRecommendation = (marginPersen, marginNominal) => {
                              const role = currentMember?.role || 'default';
                              const userName = asaNickname || (currentMember?.full_name ? currentMember.full_name.split(' ')[0] : null);
                              const isOwner = role === 'owner' || role === 'developer';
                              
                              let sapaan = userName ? (asaGaya === 'formal' ? `Mohon perhatian ${userName}, ` : `Halo ${userName}! `) : (isOwner ? 'Halo pak! ' : 'Halo kak! ');
                              
                              let c = '';
                              const isHugeNominal = marginNominal >= 10000000;
                              const isGoodNominal = marginNominal >= 3000000;
                              const mp = (Number.isFinite(marginPersen) ? marginPersen : 0).toFixed(1);

                              if (marginNominal < 0) {
                                c = asaGaya === 'formal' ? `PO ini mencatat kerugian sebesar Rp ${fmt(Math.abs(marginNominal))}. Mohon dievaluasi kembali.` : `PO ini minus Rp ${fmt(Math.abs(marginNominal))}, kok bisa sampai boncos begini ya? Harusnya nggak di-approve nih.`;
                              } else if (inv.status === 'BELUM DIBAYAR' || inv.status === 'MENUNGGU PEMBAYARAN') {
                                c = asaGaya === 'formal' ? `Invoice ini belum lunas. Mohon untuk di-follow up agar cashflow tetap terjaga.` : `Invoice ini belum dibayar nih. Jangan lupa di-follow up terus ya biar cepet cair! 💸`;
                              } else if (isHugeNominal) {
                                c = asaGaya === 'formal' ? `Meskipun persentase margin ${mp}%, keuntungan nominal mencapai Rp ${fmt(marginNominal)} yang sangat sehat secara finansial.` : `Biarpun secara persentase marginnya ${mp}%, tapi cuannya tembus Rp ${fmt(marginNominal)} lho! Ini luar biasa sehat buat cashflow kita, gass!`;
                              } else if (isGoodNominal && marginPersen < 30) {
                                c = asaGaya === 'formal' ? `Persentase margin ${mp}%, namun keuntungan nominal Rp ${fmt(marginNominal)} sudah cukup memadai.` : `Margin persennya emang cuma ${mp}%, tapi nominal cuan Rp ${fmt(marginNominal)} udah lumayan oke kok buat nutup operasional.`;
                              } else if (marginPersen < 10) {
                                c = asaGaya === 'formal' ? `Total margin PO hanya ${mp}%. Sangat minim untuk menutupi biaya operasional. Perlu evaluasi struktur harga.` : `Total margin PO cuma ${mp}%. Tipis banget buat nutup biaya operasional. Perlu evaluasi harga lagi.`;
                              } else if (marginPersen < 30) {
                                c = asaGaya === 'formal' ? `Margin PO ${mp}% cukup memadai, namun disarankan untuk menaikkan nilai ini di masa mendatang demi keamanan finansial.` : `Margin PO ${mp}% lumayan, tapi kalau bisa di PO selanjutnya di-push dikit lagi harganya biar lebih aman.`;
                              } else if (marginPersen < 80) {
                                const mp = (Number.isFinite(marginPersen) ? marginPersen : 0).toFixed(1);
                                c = asaGaya === 'formal' ? `Margin PO ${mp}% berada di tingkat yang sangat sehat. Arus kas terkendali dengan baik.` : `Margin PO ${mp}% ini udah sehat banget. Cuan aman, cashflow lancar. Good job!`;
                              } else if (marginPersen < 150) {
                                const mp = (Number.isFinite(marginPersen) ? marginPersen : 0).toFixed(1);
                                c = asaGaya === 'formal' ? `Margin PO ${mp}% sangat menguntungkan. Pastikan standar layanan dipertahankan untuk menjamin loyalitas klien.` : `Margin PO ${mp}% ini premium banget. Pastiin delivery dan pelayanannya sepadan ya biar client repeat order.`;
                              } else {
                                const mp = (Number.isFinite(marginPersen) ? marginPersen : 0).toFixed(1);
                                c = asaGaya === 'formal' ? `Margin PO mencapai ${mp}%. Ini adalah tingkat profitabilitas yang sangat istimewa.` : `Wow, margin PO sampai ${mp}%! Ini cuannya luar biasa. Pertahankan kualitasnya!`;
                              }
                              
                              return `${sapaan} ${c}`;
                            };

                            return (
                              <div className={`p-4 rounded-xl border ${
                                inv.status === 'LUNAS' 
                                  ? 'bg-gradient-to-br from-emerald-50 to-green-50 border-green-200 text-green-800' 
                                  : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 text-amber-800'
                              }`}>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-xs font-bold uppercase tracking-wider">Potensi Keuntungan Bersih</span>
                                  <span className="font-black text-lg">Rp {fmt(totalMarginKeseluruhan)}</span>
                                </div>
                                <p className="text-[10px] text-slate-500 mb-3 border-b border-black/5 pb-2">Total seluruh margin (Kemasan + Cairan + Komponen + Harga Jual Akhir + Jasa Produksi + Legalitas).</p>

                                <div className="space-y-1.5 text-xs text-slate-700 mb-4 border-b border-black/5 pb-3">
                                  {Math.round(totalMarginKemasan) !== 0 && (
                                    <div className="flex justify-between items-center">
                                      <span>Margin Kemasan (Total PO)</span>
                                      <span className={`font-semibold ${totalMarginKemasan >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{totalMarginKemasan >= 0 ? '' : '-'}Rp {fmt(Math.abs(totalMarginKemasan))}</span>
                                    </div>
                                  )}
                                  {Math.round(totalMarginCairan) !== 0 && (
                                    <div className="flex justify-between items-center">
                                      <span>Margin Formula Cairan (Total PO)</span>
                                      <span className={`font-semibold ${totalMarginCairan >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{totalMarginCairan >= 0 ? '' : '-'}Rp {fmt(Math.abs(totalMarginCairan))}</span>
                                    </div>
                                  )}
                                  {Math.round(totalMarginKomponen) !== 0 && (
                                    <div className="flex justify-between items-center">
                                      <span>Margin Kelengkapan (Total PO)</span>
                                      <span className={`font-semibold ${totalMarginKomponen >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{totalMarginKomponen >= 0 ? '' : '-'}Rp {fmt(Math.abs(totalMarginKomponen))}</span>
                                    </div>
                                  )}
                                  {Math.round(totalMarginHargaJualAkhir) !== 0 && (
                                    <div className="flex justify-between items-center">
                                      <span>Penyesuaian Harga Final (Diskon/Markup PO)</span>
                                      <span className={`font-semibold ${totalMarginHargaJualAkhir >= 0 ? 'text-blue-700' : 'text-red-600'}`}>{totalMarginHargaJualAkhir >= 0 ? '' : '-'}Rp {fmt(Math.abs(totalMarginHargaJualAkhir))}</span>
                                    </div>
                                  )}
                                  {totalKomisi > 0 && (
                                    <div className="flex justify-between items-center">
                                      <span>Komisi Keluar (Penambahan HPP)</span>
                                      <span className="font-semibold text-red-600">-Rp {fmt(totalKomisi)}</span>
                                    </div>
                                  )}
                                  {totalJasa > 0 && (
                                    <div className="flex justify-between items-center">
                                      <span>Jasa Produksi (Margin)</span>
                                      <span className="font-semibold text-emerald-700">Rp {fmt(totalJasa)}</span>
                                    </div>
                                  )}
                                  {Math.round(totalMarginLegalitas) !== 0 && (
                                    <div className="flex justify-between items-center">
                                      <span>Margin Legalitas / Lainnya</span>
                                      <span className={`font-semibold ${totalMarginLegalitas >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{totalMarginLegalitas >= 0 ? '' : '-'}Rp {fmt(Math.abs(totalMarginLegalitas))}</span>
                                    </div>
                                  )}
                                </div>

                                {(() => {
                                  const prodJ = (inv.products || inv.items || []).reduce((s, p) => s + (p.total_harga_jual || p.subtotal || 0), 0);
                                  const legJ = (inv.legalitas || []).reduce((s, l) => s + (l.harga_jual * (l.qty_variant || 1)), 0);
                                  const nominalPPN = inv.rawRecord?.ppn_mode === 'exclude' ? ((prodJ + legJ) * (parseFloat(inv.rawRecord.ppn_percentage) || 0) / 100) : 0;
                                  const shippingCost = inv.shippingCost || 0;
                                  const shippingCostLabel = inv.shippingCostLabel || 'Biaya Pengiriman';
                                  if (nominalPPN > 0 || shippingCost > 0) {
                                    return (
                                      <div className="bg-indigo-50/40 p-3 rounded-xl border border-indigo-100 mb-4 shadow-sm">
                                        <p className="text-[10px] font-bold text-indigo-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                          <FileText className="w-3.5 h-3.5" /> Ringkasan Tagihan (Exclude PPN)
                                        </p>
                                        <div className="space-y-1.5 text-xs text-indigo-900">
                                          <div className="flex justify-between items-center">
                                            <span>Harga Murni (Sblm PPN)</span>
                                            <span className="font-semibold">Rp {fmt(prodJ + legJ)}</span>
                                          </div>
                                          {shippingCost > 0 && (
                                            <div className="flex justify-between items-center text-slate-700">
                                              <span>{shippingCostLabel}</span>
                                              <span className="font-semibold">+Rp {fmt(shippingCost)}</span>
                                            </div>
                                          )}
                                          {nominalPPN > 0 && (
                                            <div className="flex justify-between items-center text-rose-600">
                                              <span>Tambahan PPN {inv.rawRecord?.ppn_percentage || 0}%</span>
                                              <span className="font-semibold">+Rp {fmt(nominalPPN)}</span>
                                            </div>
                                          )}
                                          <div className="flex justify-between items-center pt-1.5 border-t border-indigo-200/60 font-bold text-indigo-950 mt-1">
                                            <span>Total Tagihan Akhir</span>
                                            <span>Rp {fmt(prodJ + legJ + shippingCost + nominalPPN)}</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}


                                <div className="grid grid-cols-2 gap-2 text-center text-xs mb-4">
                                  <div className="bg-white/80 p-2 rounded-lg border border-slate-100">
                                    <p className="text-[9px] text-slate-400 font-bold uppercase">Sudah Dibayar (Dana)</p>
                                    <p className="font-extrabold text-green-700">Rp {fmt(inv.jumlahDibayar)}</p>
                                  </div>
                                  <div className="bg-white/80 p-2 rounded-lg border border-slate-100">
                                    <p className="text-[9px] text-slate-400 font-bold uppercase">Sisa Piutang</p>
                                    <p className="font-extrabold text-rose-600">Rp {fmt(inv.totalTagihan - inv.jumlahDibayar)}</p>
                                  </div>
                                </div>


                              </div>
                            );
                          })()}

                        </div>

                      </div>

                      {/* ASA AI Recommendation for PO - FULL WIDTH */}
                      {(() => {
                        const totalJasaASA = (inv.products || []).reduce((sum, p) => sum + ((p.jasa_nominal || p.final_jasa_nominal || 0) * (p.qty || 1)), 0);
                        const totalKomisiASA = (inv.rawRecord?.komisiList || []).reduce((sum, k) => sum + (Number(k.nominal) || 0), 0);
                        const hppTanpaJasaASA = inv.totalHpp - totalJasaASA + totalKomisiASA;
                        const totalMarginKeseluruhan = inv.totalJual - hppTanpaJasaASA;
                        const rawMarginPO2 = inv.totalJual > 0 ? (totalMarginKeseluruhan / inv.totalJual) * 100 : 0;
                        const marginPersenPO = Number.isFinite(rawMarginPO2) ? rawMarginPO2 : 0;
                        const role = currentMember?.role || 'default';
                        const userName = asaNickname || (currentMember?.full_name ? currentMember.full_name.split(' ')[0] : null);
                        const isOwner = role === 'owner' || role === 'developer';
                        
                        let sapaan = userName ? (asaGaya === 'formal' ? `Mohon perhatian ${userName}, ` : `Halo ${userName}! `) : (isOwner ? 'Halo pak! ' : 'Halo kak! ');
                        
                        let c = '';
                        const isHugeNominal = totalMarginKeseluruhan >= 10000000;
                        const isGoodNominal = totalMarginKeseluruhan >= 3000000;

                        if (totalMarginKeseluruhan < 0) {
                          c = asaGaya === 'formal' ? `PO ini mencatat kerugian sebesar Rp ${fmt(Math.abs(totalMarginKeseluruhan))}. Mohon dievaluasi kembali.` : `PO ini minus Rp ${fmt(Math.abs(totalMarginKeseluruhan))}, kok bisa sampai boncos begini ya? Harusnya nggak di-approve nih.`;
                        } else if (inv.status === 'BELUM DIBAYAR' || inv.status === 'MENUNGGU PEMBAYARAN') {
                          c = asaGaya === 'formal' ? `Invoice ini belum lunas. Mohon untuk di-follow up agar cashflow tetap terjaga.` : `Invoice ini belum dibayar nih. Jangan lupa di-follow up terus ya biar cepet cair! 💸`;
                        } else if (isHugeNominal) {
                          c = asaGaya === 'formal' ? `Meskipun persentase margin ${marginPersenPO.toFixed(1)}%, keuntungan nominal mencapai Rp ${fmt(totalMarginKeseluruhan)} yang sangat memuaskan secara finansial.` : `Biarpun secara persentase marginnya ${marginPersenPO.toFixed(1)}%, tapi nilai nominal cuannya tembus Rp ${fmt(totalMarginKeseluruhan)}! Ini luar biasa sehat, pertahankan!`;
                        } else if (isGoodNominal && marginPersenPO < 35) {
                          c = asaGaya === 'formal' ? `Persentase margin ${marginPersenPO.toFixed(1)}%, namun keuntungan nominal Rp ${fmt(totalMarginKeseluruhan)} sudah cukup memadai.` : `Margin persennya emang cuma ${marginPersenPO.toFixed(1)}%, tapi nominal cuan Rp ${fmt(totalMarginKeseluruhan)} udah lumayan banget buat nutup operasional.`;
                        } else if (marginPersenPO < 20) {
                          c = asaGaya === 'formal' ? `Total margin PO hanya ${marginPersenPO.toFixed(1)}%. Sangat minim untuk menutupi biaya operasional. Perlu evaluasi struktur harga.` : `Total margin PO cuma ${marginPersenPO.toFixed(1)}%. Tipis banget buat nutup biaya operasional. Perlu evaluasi harga lagi.`;
                        } else if (marginPersenPO < 35) {
                          c = asaGaya === 'formal' ? `Margin PO ${marginPersenPO.toFixed(1)}% masih di bawah standar 35%, disarankan untuk menaikkan nilai ini di masa mendatang demi keamanan finansial.` : `Margin PO ${marginPersenPO.toFixed(1)}% lumayan, tapi standar sehat itu 35-50%. Kalau bisa di PO selanjutnya di-push dikit lagi harganya biar lebih aman.`;
                        } else if (marginPersenPO < 50) {
                          c = asaGaya === 'formal' ? `Margin PO ${marginPersenPO.toFixed(1)}% berada di tingkat yang sangat sehat. Arus kas terkendali dengan baik.` : `Margin PO ${marginPersenPO.toFixed(1)}% ini udah masuk standar yang sehat (35-50%). Cuan aman, cashflow lancar. Good job!`;
                        } else if (marginPersenPO < 65) {
                          c = asaGaya === 'formal' ? `Margin PO ${marginPersenPO.toFixed(1)}% sangat menguntungkan. Pastikan standar layanan dipertahankan untuk menjamin loyalitas klien.` : `Margin PO ${marginPersenPO.toFixed(1)}% ini premium banget. Pastiin delivery dan pelayanannya sepadan ya biar client repeat order.`;
                        } else {
                          c = asaGaya === 'formal' ? `Margin PO mencapai ${marginPersenPO.toFixed(1)}%. Ini adalah tingkat profitabilitas yang sangat istimewa.` : `Wow, margin PO sampai ${marginPersenPO.toFixed(1)}%! Ini cuannya luar biasa. Pertahankan kualitasnya!`;
                        }
                        
                        return (
                          <div className="bg-white/80 border border-amber-200/50 rounded-xl p-3 shadow-sm mt-4">
                            <div className="flex items-center gap-2 text-[#8C6208] mb-1.5 font-bold text-[11px]">
                              <div className="w-4 h-4 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8C6208] flex items-center justify-center shrink-0">
                                <Sparkles className="w-2.5 h-2.5 text-white" />
                              </div>
                              ASA (AI) Rekomendasi Keseluruhan
                            </div>
                            <p className="text-[12px] text-slate-700 leading-relaxed italic border-l-2 border-[#D4AF37] pl-3 py-1">
                              "{sapaan}{c}"
                            </p>
                          </div>
                        );
                      })()}

                      </ErrorBoundary>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {/* Pagination Controls */}
        {filtered.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex flex-wrap items-center justify-between gap-4 print:hidden">
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
              <div className="w-[80px]">
                <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                  <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-white border-slate-200/80 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 25, 50, 100].map(n => (
                      <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center gap-1.5">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-white hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
              >
                &lt;
              </button>
              
              {/* Simple page numbers */}
              {(() => {
                const totalPages = Math.ceil(filtered.length / itemsPerPage);
                const pages = [];
                for(let i=1; i<=totalPages; i++) {
                  if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                          currentPage === i 
                            ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                            : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  } else if (i === currentPage - 2 || i === currentPage + 2) {
                    pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                  }
                }
                return pages;
              })()}
              
              <button 
                onClick={() => setCurrentPage(p => Math.min(Math.ceil(filtered.length / itemsPerPage), p + 1))}
                disabled={currentPage >= Math.ceil(filtered.length / itemsPerPage)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-[#F9F5EC] hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
              >
                &gt;
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
      
      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      {/* INVOICE PREVIEW & CETAK MODAL */}
      {selectedInvoice && (
        <div className="hidden print:block font-sans text-black p-0 text-[12px] leading-relaxed w-full">
          <style type="text/css">{`
            @media print {
              @page { size: A4 portrait; margin: 10mm; }
              html, body, #root { height: auto !important; min-height: 0 !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; }
              body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background: white !important; }
              .fixed, div[class*="fixed"], .no-print, [class*="AsaAssistantWidget"], [class*="FloatingWidgets"], aside, nav { display: none !important; }
            }
          `}</style>
          
          <div id="invoice-print-area" className="bg-white p-0 max-w-none w-full mx-auto border-none shadow-none">
            
            {/* Company Header */}
            <div className="flex justify-between items-start mb-4">
              <div className="flex gap-4 items-center">
                <img src="/logo.png" alt="Logo" className="w-14 h-14 object-contain" />
                <div>
                  <h2 className="text-base font-black uppercase text-[#302621] tracking-tight">{companySettings.nama_perusahaan || 'PT. Satala Dermatech Essential'}</h2>
                  <p className="text-[9px] text-slate-500 max-w-[420px]">
                    {companySettings.alamat_perusahaan || 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <h1 className="text-xl font-black text-slate-800 tracking-wider">INVOICE</h1>
                <span className="inline-block mt-1 border border-green-300 text-green-700 font-bold px-2 py-0.5 rounded text-[10px]">
                  {(() => {
                    if (selectedInvoice.status === 'LUNAS') return 'LUNAS';
                    if (selectedInvoice.totalJual > 0 && selectedInvoice.jumlahDibayar > 0) {
                      const pct = Math.round((selectedInvoice.jumlahDibayar / selectedInvoice.totalJual) * 100);
                      return `${pct}%`;
                    }
                    return selectedInvoice.status.includes('DP') ? selectedInvoice.status.replace('DP ', '') : selectedInvoice.status;
                  })()}
                </span>
              </div>
            </div>

            {/* Divider line in Gold */}
            <div className="border-b-[3px] border-[#D4AF37] mb-4"></div>

            {/* Meta Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="space-y-1 text-xs">
                <div className="flex">
                  <span className="w-24 text-slate-500">Nomor</span>
                  <span className="w-2">:</span>
                  <span className="font-bold text-slate-800">{selectedInvoice.id}</span>
                </div>
                <div className="flex">
                  <span className="w-24 text-slate-500">Tanggal</span>
                  <span className="w-2">:</span>
                  <span className="text-slate-800">{formatTanggalIndo(selectedInvoice.tanggal)}</span>
                </div>
                {selectedInvoice.tanggal_jatuh_tempo && (
                  <div className="flex">
                    <span className="w-24 text-slate-500">Jatuh Tempo</span>
                    <span className="w-2">:</span>
                    <span className="text-rose-600 font-bold">{formatTanggalIndo(selectedInvoice.tanggal_jatuh_tempo)}</span>
                  </div>
                )}
                <div className="flex">
                  <span className="w-24 text-slate-500">Payment Term</span>
                  <span className="w-2">:</span>
                  <span className="font-medium text-slate-800 uppercase">{selectedInvoice.paymentTerms || selectedInvoice.rawRecord?.paymentTerms || 'CASH'}</span>
                </div>
                {selectedInvoice.bank_tujuan && (
                  <div className="flex">
                    <span className="w-24 text-slate-500">Pembayaran Ke</span>
                    <span className="w-2">:</span>
                    <span className="font-medium text-slate-800 uppercase">BANK {selectedInvoice.bank_tujuan}</span>
                  </div>
                )}
              </div>

              <div className="border border-slate-200 bg-slate-50/50 p-2.5 rounded-lg text-xs">
                <p className="font-bold text-slate-800 mb-0.5">Kepada:</p>
                <p className="font-bold text-[#8C6208] text-xs">{(selectedInvoice.client || '').split(' / ')[0].trim()}</p>
                <p className="text-[9px] text-slate-500 mt-1 leading-tight">
                  Pembelian produk OEM / Maklon Satala Dermatech
                </p>
                <p className="text-[9px] text-slate-500">Di Tempat.</p>
              </div>
            </div>

            <div className="mb-4 text-xs">
              <p className="font-bold text-slate-700">Dengan hormat,</p>
              <p className="text-slate-500 text-[10px] mt-0.5">
                Berdasarkan kesepakatan bersama, dengan ini kami mengajukan invoice tersebut sebagai berikut :
              </p>
            </div>

            <table className="w-full text-left border-collapse mb-4 text-[10px]">
              <thead>
                <tr className="bg-[#302621] text-[#D4AF37]">
                  <th className="py-2 px-3 font-bold w-8 text-center rounded-tl-md">No</th>
                  <th className="py-2 px-3 font-bold">Nama Produk / Layanan</th>
                  <th className="py-2 px-3 font-bold">Keterangan</th>
                  <th className="py-2 px-3 font-bold text-center w-12">Qty</th>
                  <th className="py-2 px-3 font-bold text-right w-28">Harga/Pcs</th>
                  <th className="py-2 px-3 font-bold text-right w-28 rounded-tr-md">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {(selectedInvoice.products || []).map((item, idx) => {
                  const qty = item.qty || 1;
                  const subtotal = item.total_harga_jual || (item.harga_jual_per_pcs ? item.harga_jual_per_pcs * qty : 0) || 0;
                  const price = item.harga_jual_per_pcs || (subtotal / qty) || 0;
                  return (
                    <tr key={`p-${idx}`} className="border-b border-slate-100 even:bg-[#F9F5EC] hover:bg-slate-50/50">
                      <td className="py-1 px-2 text-center text-slate-500">{idx + 1}</td>
                      <td className="py-1 px-2 font-semibold text-slate-800">{item.nama_product || item.nama}</td>
                      <td className="py-1 px-2 text-slate-500 text-[9px]">
                        {(() => {
                          const vol = item.botol_ukuran_aktual || item.botol_ukuran_label || item.botol_ukuran_client || item.ukuran_botol_ml || item.volume_ml || item.ukuran || item.ukuran_ml;
                          const volText = vol ? vol : '-';
                          return `Volume ${volText}` + (String(volText).toLowerCase().includes('ml') ? '' : ' ml');
                        })()}
                      </td>
                      <td className="py-1 px-2 text-center font-medium">{qty}</td>
                      <td className="py-1 px-2 text-right">Rp {fmt(price)}</td>
                      <td className="py-1 px-2 text-right font-semibold text-slate-800">Rp {fmt(subtotal)}</td>
                    </tr>
                  );
                })}

                {(() => {
                  const flattenedLegalitas = [];
                  (selectedInvoice.legalitas || []).forEach((leg) => {
                    const hargaJual = Number(leg.harga_jual !== undefined && leg.harga_jual !== '' ? leg.harga_jual : (leg.nominal || 0));
                    const qty = leg.qty_variant || (Array.isArray(leg.selected_variants) && leg.selected_variants.length > 0 ? leg.selected_variants.length : 1);
                    flattenedLegalitas.push({
                      nama: leg.nama,
                      keterangan: 'Layanan Legalitas Maklon',
                      qty: qty,
                      harga: hargaJual,
                      subtotal: hargaJual * qty,
                    });
                  });

                  const startIdx = (selectedInvoice.products || []).length;
                  return flattenedLegalitas.map((item, idx) => (
                    <tr key={`l-${idx}`} className="border-b border-slate-100 even:bg-[#F9F5EC] hover:bg-slate-50/50">
                      <td className="py-1 px-2 text-center text-slate-500">{startIdx + idx + 1}</td>
                      <td className="py-1 px-2 font-semibold text-slate-800">{item.nama}</td>
                      <td className="py-1 px-2 text-slate-500 text-[9px]">{item.keterangan}</td>
                      <td className="py-1 px-2 text-center font-medium">{item.qty}</td>
                      <td className="py-1 px-2 text-right">Rp {fmt(item.harga)}</td>
                      <td className="py-1 px-2 text-right font-semibold text-slate-800">Rp {fmt(item.subtotal)}</td>
                    </tr>
                  ));
                })()}

                {selectedInvoice.shippingCost > 0 && (
                  <tr className="border-b border-slate-100 even:bg-[#F9F5EC] hover:bg-slate-50/50">
                    <td className="py-1 px-2 text-center text-slate-500">
                      {(selectedInvoice.products || []).length + (selectedInvoice.legalitas || []).length + 1}
                    </td>
                    <td className="py-1 px-2 font-semibold text-slate-800">{selectedInvoice.shippingCostLabel || 'Biaya Pengiriman'}</td>
                    <td className="py-1 px-2 text-slate-500 text-[9px]">Biaya Tambahan / Ekspedisi</td>
                    <td className="py-1 px-2 text-center font-medium">1</td>
                    <td className="py-1 px-2 text-right">Rp {fmt(selectedInvoice.shippingCost)}</td>
                    <td className="py-1 px-2 text-right font-semibold text-slate-800">Rp {fmt(selectedInvoice.shippingCost)}</td>
                  </tr>
                )}
                

                {selectedInvoice.rawRecord?.ppn_mode === 'exclude' && (
                  <>
                    <tr className="bg-[#F9F5EC]">
                      <td colSpan="4" className="py-2 px-3"></td>
                      <td className="py-2 px-3 font-bold text-slate-700 text-right">Subtotal</td>
                      <td className="py-2 px-3 font-bold text-slate-800 text-right">
                        {(() => {
                          const prodJ = (selectedInvoice.products || selectedInvoice.items || []).reduce((s, p) => s + (p.total_harga_jual || p.subtotal || 0), 0);
                          const legJ = (selectedInvoice.legalitas || []).reduce((s, l) => s + (l.harga_jual * (l.qty_variant || 1)), 0);
                          const ship = selectedInvoice.shippingCost || 0;
                          return `Rp ${fmt(prodJ + legJ + ship)}`;
                        })()}
                      </td>
                    </tr>
                    <tr className="bg-[#F9F5EC]">
                      <td colSpan="4" className="py-2 px-3"></td>
                      <td className="py-2 px-3 font-bold text-slate-700 text-right">PPN {selectedInvoice.rawRecord?.ppn_percentage || 0}%</td>
                      <td className="py-2 px-3 font-bold text-slate-800 text-right">
                        {(() => {
                          const prodJ = (selectedInvoice.products || selectedInvoice.items || []).reduce((s, p) => s + (p.total_harga_jual || p.subtotal || 0), 0);
                          const legJ = (selectedInvoice.legalitas || []).reduce((s, l) => s + (l.harga_jual * (l.qty_variant || 1)), 0);
                          const nominal = (prodJ + legJ) * (parseFloat(selectedInvoice.rawRecord?.ppn_percentage) || 0) / 100;
                          return `Rp ${fmt(nominal)}`;
                        })()}
                      </td>
                    </tr>
                  </>
                )}
                <tr className="bg-[#F9F5EC]">
                  <td colSpan="4" className="py-2 px-3 border-t border-[#D4AF37]/30"></td>
                  <td className="py-2 px-3 font-bold text-slate-700 text-right border-t border-[#D4AF37]/30">Total Harga</td>
                  <td className="py-2 px-3 font-black text-[#8C6208] text-right border-t border-[#D4AF37]/30">Rp {fmt(selectedInvoice.totalTagihan)}</td>
                </tr>
                {selectedInvoice.customDp && Number(selectedInvoice.customDp) > 0 ? (
                  <tr className="bg-emerald-50/60 border-t border-slate-100">
                    <td colSpan="4" className="py-2 px-3"></td>
                    <td className="py-2 px-3 font-bold text-emerald-700 text-right">Down Payment (DP) {selectedInvoice.customDp}%</td>
                    <td className="py-2 px-3 font-bold text-emerald-700 text-right">Rp {fmt(selectedInvoice.totalTagihan * (Number(selectedInvoice.customDp) / 100))}</td>
                  </tr>
                ) : null}

                {selectedInvoice.jumlahDibayar > 0 && (
                  <>
                    <tr className="bg-emerald-50/60 border-t border-slate-100">
                      <td colSpan="4" className="py-2 px-3"></td>
                      <td className="py-2 px-3 font-bold text-emerald-700 text-right">Total Terbayar</td>
                      <td className="py-2 px-3 font-bold text-emerald-700 text-right">Rp {fmt(selectedInvoice.jumlahDibayar)}</td>
                    </tr>
                    {selectedInvoice.totalTagihan - selectedInvoice.jumlahDibayar > 0 && (
                      <tr className="bg-amber-50/60">
                        <td colSpan="4" className="py-2 px-3"></td>
                        <td className="py-2 px-3 font-bold text-amber-700 text-right">Sisa Tagihan</td>
                        <td className="py-2 px-3 font-bold text-amber-700 text-right">Rp {fmt(selectedInvoice.totalTagihan - selectedInvoice.jumlahDibayar)}</td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>

            <div className="mb-6 bg-slate-50/50 p-2.5 rounded-lg border border-slate-100 text-xs">
              <p className="font-bold text-slate-700">Terbilang:</p>
              <p className="text-[10px] italic text-[#8C6208] font-medium mt-0.5">
                {(() => {
                  const sisa = selectedInvoice.jumlahDibayar > 0 ? (selectedInvoice.totalTagihan - selectedInvoice.jumlahDibayar) : selectedInvoice.totalTagihan;
                  return sisa <= 0 ? 'Lunas' : terbilang(sisa);
                })()}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 items-start mt-6 text-xs break-inside-avoid print-avoid-break">
              <div>
                <p className="font-bold text-slate-700 mb-1">Noted:</p>
                <p className="text-slate-500 text-[10px] mb-3">Pembayaran Harap Mencantumkan Detail Invoice</p>

                {selectedInvoice.customNote && (
                  <div className="mb-3 p-2 bg-[#F9F5EC]/50 border border-[#E6D5B8] rounded">
                    <p className="font-bold text-[#8C6208] text-[10px] mb-0.5">Catatan Khusus:</p>
                    <p className="text-slate-800 font-medium text-[10px] whitespace-pre-wrap">{selectedInvoice.customNote}</p>
                  </div>
                )}

                <div className="bg-slate-50 border border-slate-100 p-2.5 rounded-lg space-y-1 text-[9px] max-w-[280px]">
                  {(() => {
                    const bankKey = selectedInvoice.rawRecord?.paymentMethod || 'bca';
                    const bankAcc = (companySettings.bank_accounts && companySettings.bank_accounts[bankKey]) || {
                      nama_bank: 'BCA',
                      name: companySettings.nama_perusahaan || 'PT. Satala Dermatech Essential',
                      number: '190283018'
                    };
                    return (
                      <>
                        <div className="flex">
                          <span className="w-20 text-slate-500">Alamat Transfer</span>
                          <span>: -</span>
                        </div>
                        <div className="flex">
                          <span className="w-20 text-slate-500">Account</span>
                          <span className="font-bold text-slate-700">: {bankAcc.name || companySettings.nama_perusahaan || 'PT. Satala Dermatech Essential'}</span>
                        </div>
                        <div className="flex">
                          <span className="w-20 text-slate-500">Bank</span>
                          <span className="font-bold text-slate-700">: {(bankAcc.nama_bank || bankKey).toUpperCase()}</span>
                        </div>
                        <div className="flex">
                          <span className="w-20 text-slate-500">No Rekening</span>
                          <span className="font-bold text-slate-700">: {bankAcc.number || '190283018'}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              <div className="text-right flex flex-col items-end pr-4">
                <p className="text-[10px] text-slate-500 mb-1">Bogor, {formatTanggalIndo(selectedInvoice.tanggal)}</p>
                
                <div className="relative w-48 h-20 my-1 flex justify-center items-center">
                  {companySettings.stempel_url ? (
                    <img src={companySettings.stempel_url} alt="Stempel" className="absolute left-6 -top-1 pointer-events-none w-20 h-20 object-contain opacity-80" />
                  ) : (
                    <div className="absolute left-6 -top-1 pointer-events-none scale-[0.8]">
                      <svg width="90" height="90" viewBox="0 0 100 100" className="opacity-80">
                        <circle cx="50" cy="50" r="45" fill="none" stroke="#D4AF37" strokeWidth="2" strokeDasharray="3 2" />
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#D4AF37" strokeWidth="1" />
                        <text x="50" y="45" fill="#D4AF37" fontSize="8" fontWeight="bold" textAnchor="middle">PT. SATALA</text>
                        <text x="50" y="55" fill="#D4AF37" fontSize="8" fontWeight="bold" textAnchor="middle">DERMATECH</text>
                        <text x="50" y="65" fill="#D4AF37" fontSize="6" fontWeight="bold" textAnchor="middle">★ BOGOR ★</text>
                        <path d="M 45 35 C 45 30, 55 30, 55 35 C 55 40, 45 42, 45 48 C 45 53, 55 53, 55 48" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round" />
                      </svg>
                    </div>
                  )}

                  {companySettings.ttd_url ? (
                    <img src={companySettings.ttd_url} alt="Tanda Tangan" className="w-24 h-16 object-contain relative z-10" />
                  ) : (
                    <span className="font-serif italic text-2xl text-blue-900/60 font-semibold tracking-wider relative z-10 rotate-[-4deg]" style={{ fontFamily: "'Brush Script MT', cursive" }}>
                      {companySettings.nama_direktur || 'Umar Syarif'}
                    </span>
                  )}
                </div>

                <div className="text-center w-48 border-t border-slate-200 pt-1">
                  <p className="font-bold text-slate-800 text-[10px]">{companySettings.nama_direktur || 'Umar Syarif'}</p>
                  <p className="text-slate-400 text-[8px] uppercase tracking-wider mt-0.5">Direktur</p>
                </div>
              </div>
            </div>

            <div className="border-b-[2px] border-[#D4AF37] mt-8"></div>
          </div>
        </div>
      )}

      {/* ================= MODAL ASA PRINT INVOICE ================= */}
      {asaPrintModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60  animate-in fade-in duration-200 print:hidden">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-[#E6D5B8] animate-in zoom-in-95 duration-200 flex flex-col">
            <div className="bg-gradient-to-r from-[#F9F5EC] to-white p-6 relative border-b border-[#E6D5B8] flex items-start gap-4">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-sm shrink-0 bg-amber-50">
                <img src="/asa-profile.png" alt="ASA" className="w-full h-full object-cover" onError={(e) => { e.target.src = 'https://ui-avatars.com/api/?name=Asa&background=0D8ABC&color=fff'; }} />
              </div>
              <div className="flex-1 mt-1">
                <h3 className="font-black text-slate-800 text-lg flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  Cetak Invoice
                </h3>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Halo kak! Mau cetak invoice buat <b>{asaPrintModal.client?.split(' / ')[0]}</b> nih. Ada pesan khusus nggak buat dimasukin ke invoicenya?
                </p>
              </div>
              <button onClick={() => setAsaPrintModal(null)} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Minta Down Payment (DP) %</label>
                  <div className="flex items-center gap-3">
                    <div className="relative w-24">
                      <input 
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Misal: 50"
                        value={asaPrintDp}
                        onChange={(e) => setAsaPrintDp(e.target.value)}
                        className="w-full text-sm p-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-[#8C6208] pr-6"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">%</span>
                    </div>
                    {asaPrintDp && Number(asaPrintDp) > 0 && (
                      <div className="flex-1 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 flex items-center justify-between">
                        <span className="text-[10px] font-medium text-emerald-700">Nominal DP ({asaPrintDp}%):</span>
                        <span className="text-xs font-bold text-emerald-800">Rp {fmt(asaPrintModal.totalJual * (Number(asaPrintDp) / 100))}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Catatan Khusus (Opsional)</label>
                  <textarea
                    className="w-full text-sm p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-[#8C6208] focus:ring-1 focus:ring-[#8C6208]/50 resize-none h-24"
                    placeholder="Misal: Pembayaran bisa ditransfer ke rekening BNI..."
                    value={asaPrintNote}
                    onChange={(e) => setAsaPrintNote(e.target.value)}
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-2 justify-end">
              <button 
                onClick={() => {
                  setAsaPrintNote('');
                  setAsaPrintDp('');
                  setSelectedInvoice(asaPrintModal);
                  setAsaPrintModal(null);
                  setTimeout(() => {
                    const oldTitle = document.title;
                    document.title = asaPrintModal.id || 'Invoice';
                    window.print();
                    document.title = oldTitle;
                  }, 300);
                }}
                className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-200 bg-slate-100 rounded-lg transition-colors"
              >
                Skip & Cetak Langsung
              </button>
              <button 
                onClick={() => {
                  setSelectedInvoice({...asaPrintModal, customNote: asaPrintNote, customDp: asaPrintDp});
                  setAsaPrintModal(null);
                  setTimeout(() => {
                    const oldTitle = document.title;
                    document.title = asaPrintModal.id || 'Invoice';
                    window.print();
                    document.title = oldTitle;
                  }, 300);
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-[#8C6208] hover:bg-[#6A4A06] rounded-lg transition-colors shadow-sm"
              >
                Simpan & Cetak
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL TEMPO ================= */}
      {tempoModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60  animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-[#E6D5B8] animate-in zoom-in-95 duration-200">
            <div className="bg-gradient-to-r from-[#F9F5EC] to-white p-6 flex flex-col items-center text-center relative border-b border-[#E6D5B8]">
              <button onClick={() => setTempoModal(null)} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"><X className="w-5 h-5" /></button>
              <div className="w-20 h-20 bg-white rounded-full border-4 border-[#E6D5B8] shadow-sm flex items-center justify-center mb-3">
                <Sparkles className="w-10 h-10 text-[#8C6208]" />
              </div>
              <h2 className="text-xl font-black text-[#302621]">Set Tagihan Jadi Tempo</h2>
              <p className="text-sm text-[#8C6208] mt-2 font-medium">
                {asaGaya === 'santai' 
                  ? `Tentukan tanggal barang sampai dan jatuh temponya ya, ${asaNickname}. Oh iya, jangan lupa buat nagih invoice ini kalau udah jatuh tempo nanti! 😎`
                  : asaGaya === 'formal'
                  ? `Mohon tentukan tanggal barang tiba dan tanggal jatuh tempo, ${asaNickname}. Harap pastikan untuk melakukan penagihan tepat pada saat tanggal jatuh tempo tiba.`
                  : `Tentukan tanggal barang sampai dan jatuh temponya ya, ${asaNickname}. Dan pastikan jangan lupa untuk menagihnya saat sudah jatuh tempo nanti! 😉`}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Kapan barang sampai?</label>
                <CustomDatePicker value={tempoModal.tglSampai} onChange={date => setTempoModal({...tempoModal, tglSampai: date})} className="h-12 border-rose-200 focus-visible:ring-rose-500" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Kapan jatuh temponya?</label>
                <CustomDatePicker value={tempoModal.tglJatuhTempo} onChange={date => setTempoModal({...tempoModal, tglJatuhTempo: date})} className="h-12 border-rose-200 focus-visible:ring-rose-500" />
              </div>
              
              <div className="pt-4 flex gap-3">
                <Button variant="outline" className="flex-1 h-12 rounded-xl text-[#302621] border-[#E6D5B8] hover:bg-[#F9F5EC]" onClick={() => setTempoModal(null)}>Batal</Button>
                <Button 
                  className="flex-1 h-12 rounded-xl bg-[#8C6208] hover:bg-[#725006] text-white font-bold shadow-lg shadow-[#8C6208]/20"
                  onClick={() => {
                    if(!tempoModal.tglSampai || !tempoModal.tglJatuhTempo) {
                      toast.error('Isi kedua tanggal ya!');
                      return;
                    }
                    updatePO(tempoModal.invId, {
                      is_tempo: true,
                      statusBayar: 'TEMPO',
                      tanggal_barang_sampai: tempoModal.tglSampai,
                      tanggal_jatuh_tempo: tempoModal.tglJatuhTempo,
                      paymentMethod: 'cuan jalan terus'
                    });
                    toast.success('Invoice berhasil diset Tempo & masuk Piutang!');
                    setTempoModal(null);
                  }}
                >Simpan Tempo</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Asa Dialog Overlay */}
      {asaDialog?.show && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-200 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-[#E6D5B8] animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh] my-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent p-4 sm:p-5 border-b border-[#E6D5B8]/40 flex gap-4 items-center shrink-0">
              <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center shrink-0 border border-[#E6D5B8]">
                <Sparkles className="w-6 h-6 text-[#8C6208]" />
              </div>
              <div className="min-w-0">
                <h3 className="font-black text-[#302621] text-lg">Notifikasi Asa AI</h3>
                <p className="text-[10px] text-[#8C6208] font-bold uppercase tracking-wider">Deteksi Duplikat Varian</p>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 overscroll-contain">
              <div className="flex flex-col items-center justify-center text-center mb-5 shrink-0">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden border-4 border-white shadow-lg mb-3 bg-slate-100">
                  <img src="/asa-profile.png" alt="ASA" className="w-full h-full object-cover" onError={(e) => { e.target.src = 'https://ui-avatars.com/api/?name=Asa&background=0D8ABC&color=fff'; }} />
                </div>
                <h3 className="text-lg font-black text-[#302621]">ASA AI</h3>
              </div>
              
              <div className="bg-[#FFFDF9] border border-[#E6D5B8]/50 p-4 rounded-xl shadow-inner mb-5 relative min-h-[80px] flex items-center justify-center">
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-[#FFFDF9] border-t border-l border-[#E6D5B8]/50 rotate-45"></div>
                {isAiLoading ? (
                  <div className="flex items-center gap-2 text-[#7A695B] font-medium text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> ASA sedang memproses...
                  </div>
                ) : (
                  <p className="text-sm text-[#302621] text-center font-medium leading-relaxed relative z-10 w-full">
                    "{aiMessage}"
                  </p>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex gap-2 items-start shadow-inner">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <p>Pilih <b>Overwrite</b> untuk mengganti data lama. Pilih <b>Batal (Skip)</b> untuk melewati varian yang duplikat saja.</p>
              </div>
            </div>
            {/* Footer */}
            <div className="p-4 bg-slate-50 flex justify-end gap-2 border-t border-slate-100 shrink-0">
              <Button
                variant="outline"
                className="text-slate-600 bg-white hover:bg-slate-50 font-bold border-slate-200 rounded-xl"
                onClick={() => {
                  if (asaDialog.newProducts.length === 0) {
                    toast.error('Semua varian dilewati (tidak ada data baru yang disimpan).');
                  } else {
                    toast.success(`Varian duplikat dilewati. Menyimpan ${asaDialog.newProducts.length} varian baru...`);
                    simpanKeBrandDbMut.mutate({ ...asaDialog.safeInv, products: asaDialog.newProducts, _duplicateAction: 'skip' });
                  }
                  setAsaDialog(null);
                }}
              >
                Batal (Skip)
              </Button>
              <Button
                className="bg-[#8C6208] text-white hover:bg-[#725007] font-bold rounded-xl"
                onClick={() => {
                  toast.success(`Mempersiapkan penimpaan (overwrite) untuk varian ${asaDialog.duplicateVarian.join(', ')}...`);
                  simpanKeBrandDbMut.mutate({ ...asaDialog.safeInv, _duplicateAction: 'overwrite' });
                  setAsaDialog(null);
                }}
              >
                <Database className="w-4 h-4 mr-1.5" /> Overwrite Data
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* SURAT JALAN PRINT MODAL */}
      {showSuratJalanFor && (
        <SuratJalanPrint
          order={showSuratJalanFor}
          shipment={showSuratJalanShipment}
          companySettings={companySettings}
          onClose={() => {
            setShowSuratJalanFor(null);
            setShowSuratJalanShipment(null);
          }}
        />
      )}

      {/* ASA MULTI SURAT JALAN MODAL */}
      {pendingAsaSuratJalan && (
        <Dialog open={!!pendingAsaSuratJalan} onOpenChange={(open) => !open && setPendingAsaSuratJalan(null)}>
          <DialogContent className="max-w-md bg-white border-2 border-[#D4AF37]/30 rounded-2xl overflow-hidden p-0">
            <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent p-5 border-b border-[#E6D5B8]/40 flex gap-4 items-start">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8C6208] flex items-center justify-center shrink-0 shadow-lg relative">
                <Sparkles className="w-6 h-6 text-white" />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
              </div>
              <div>
                <h3 className="font-black text-[#302621] text-lg">Konfirmasi Asa AI</h3>
                <p className="text-sm text-[#302621]/80 leading-relaxed mt-1">
                  {asaGaya === 'gaul' ? `Halo ${asaNickname}! Asa lihat ada lebih dari 1 Surat Jalan untuk pesanan ini nih.`
                  : asaGaya === 'formal' ? `Halo ${asaNickname}. Terdapat lebih dari 1 riwayat pengiriman (Surat Jalan) untuk pesanan ini.`
                  : `Halo ${asaNickname}! Terdapat lebih dari 1 riwayat pengiriman (Surat Jalan) untuk pesanan ini.`}
                </p>
                <p className="text-sm text-[#302621]/80 leading-relaxed mt-2 font-medium">
                  {asaGaya === 'gaul' ? `${asaNickname} mau cetak Surat Jalan yang mana?`
                  : asaGaya === 'formal' ? `Surat Jalan mana yang ingin Anda cetak, ${asaNickname}?`
                  : `Silakan pilih Surat Jalan mana yang ingin dicetak ya.`}
                </p>
              </div>
            </div>
            
            <div className="p-5 space-y-3 bg-slate-50 max-h-[60vh] overflow-y-auto">
              {pendingAsaSuratJalan.shipments.map((shipment, index) => {
                const totalItems = (shipment.items || []).reduce((sum, i) => sum + (Number(i.qty_akhir) || 0), 0);
                const shipDate = shipment.date || pendingAsaSuratJalan.shipped_at ? new Date(shipment.date || pendingAsaSuratJalan.shipped_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Tanggal tidak diketahui';
                
                return (
                <Button 
                  key={index}
                  variant="outline" 
                  className="w-full justify-start h-auto py-3 px-4 bg-white border-[#E6D5B8] hover:bg-[#F9F5EC] hover:border-[#8C6208] transition-all text-left group flex flex-col items-start gap-1.5"
                  onClick={() => {
                     setShowSuratJalanFor(pendingAsaSuratJalan);
                     setShowSuratJalanShipment(shipment);
                     setPendingAsaSuratJalan(null);
                  }}
                >
                  <div className="flex items-center justify-between w-full font-bold text-[#302621] group-hover:text-[#8C6208] transition-colors">
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-[#8C6208]" /> 
                      <span>Cetak Destinasi {index + 1}</span>
                    </div>
                    <span className="text-[10px] bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full whitespace-nowrap">{totalItems} pcs</span>
                  </div>
                  <div className="text-xs text-slate-500 pl-6 w-full">
                    <span className="font-semibold text-slate-700">{shipDate}</span> • {shipment.shipping_via} - {shipment.resi || 'Resi belum diinput'}
                    <div className="mt-1 line-clamp-1">{shipment.alamat_pengiriman?.alamat_lengkap || shipment.shipping_details || 'Alamat tidak ditulis'}</div>
                  </div>
                </Button>
              )})}
              
              <Button 
                variant="outline" 
                className="w-full justify-start h-auto py-3 px-4 bg-[#F9F5EC] border-[#8C6208] hover:bg-[#8C6208] hover:text-white transition-all text-left group flex items-center gap-2"
                onClick={() => {
                   setShowSuratJalanFor(pendingAsaSuratJalan);
                   setShowSuratJalanShipment(null); // Print all
                   setPendingAsaSuratJalan(null);
                }}
              >
                <FileText className="w-4 h-4 text-[#8C6208] group-hover:text-white" /> 
                <span className="font-bold text-[#8C6208] group-hover:text-white">Cetak Semua Surat Jalan</span>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ASA UPDATE PO MODAL */}
      {pendingAsaPoUpdate && (
        <Dialog open={!!pendingAsaPoUpdate} onOpenChange={(open) => !open && setPendingAsaPoUpdate(null)}>
          <DialogContent className="max-w-md bg-white border-2 border-[#D4AF37]/30 rounded-2xl overflow-hidden p-0">
            <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent p-5 border-b border-[#E6D5B8]/40 flex gap-4 items-start">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8C6208] flex items-center justify-center shrink-0 shadow-lg relative">
                <Sparkles className="w-6 h-6 text-white" />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
              </div>
              <div>
                <h3 className="font-black text-[#302621] text-lg">Konfirmasi Asa AI</h3>
                <p className="text-sm text-[#302621]/80 leading-relaxed mt-1">
                  Halo {asaNickname}! Asa lihat kamu baru saja memperbarui pesanan. PO ini sedang berjalan di tahap <span className="font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{pendingAsaPoUpdate.invoice.statusProduksi}</span>.
                </p>
                <p className="text-sm text-[#302621]/80 leading-relaxed mt-2">
                  Varian yang baru ditambahkan ini mau ditaruh di tahap mana?
                </p>
              </div>
            </div>
            
            <div className="p-5 space-y-3 bg-slate-50">
              <Button 
                variant="outline" 
                className="w-full justify-start h-14 bg-white border-[#E6D5B8] hover:bg-[#F9F5EC] hover:border-[#8C6208] transition-all text-left group"
                onClick={async () => {
                   const { invoice, poData } = pendingAsaPoUpdate;
                   // Use raw production status
                   let currentStatus = 'antrian_produksi';
                   const normalizeCode = (str) => String(str || '').replace(/^(PO-|INV-|SPH-)/i, '').trim().toLowerCase();
                   const realPO = productionOrders.find(p => 
                     p.id === invoice.id ||
                     p.order_number === invoice.id || 
                     (p.notes && p.notes.includes(invoice.id)) || 
                     (p.order_number && String(p.order_number).trim() === String(invoice.id).trim()) ||
                     (p.order_number && invoice.id && normalizeCode(p.order_number) === normalizeCode(invoice.id))
                   );
                   if (realPO) currentStatus = realPO.status || 'antrian_produksi';
                   
                   const targetOrderNumber = realPO?.order_number || (invoice.id || '').replace(/^INV-/i, 'PO-') || invoice.id;
                   const updatedData = { ...poData, status: currentStatus, id: realPO?.id, order_number: targetOrderNumber };
                   if (updatedData.products) {
                     updatedData.products = updatedData.products.map(p => ({ ...p, status: currentStatus }));
                   }
                   if (updatedData.components) {
                      updatedData.components = updatedData.components.map(c => ({ ...c, status: currentStatus }));
                   }
                   
                   const tid = toast.loading('Memproses update...');
                   try {
                     if (!updatedData.id) {
                       await createPOMutation.mutateAsync(updatedData);
                     } else {
                       await updatePOMutation.mutateAsync(updatedData);
                     }
                     await base44.entities.ClientPO.update(invoice.id, { has_pending_po_update: false });
                     queryClient.invalidateQueries({ queryKey: ['client-pos'] });
                     queryClient.invalidateQueries({ queryKey: ['production-orders'] });
                     setPendingAsaPoUpdate(null);
                     toast.success('PO berhasil diupdate tanpa mereset status!', { id: tid });
                   } catch (e) {
                     toast.error('Gagal update PO: ' + e.message, { id: tid });
                   }
                }}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform">
                    <CheckCircle className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-[#302621] text-sm">Ikut Status Saat Ini</p>
                    <p className="text-[10px] text-slate-500">Lanjutkan varian baru ini di tahap {pendingAsaPoUpdate.invoice.statusProduksi}</p>
                  </div>
                </div>
              </Button>

              <Button 
                variant="outline" 
                className="w-full justify-start h-14 bg-white border-slate-200 hover:bg-slate-50 transition-all text-left group"
                onClick={async () => {
                   const { invoice, poData } = pendingAsaPoUpdate;
                   const normalizeCode = (str) => String(str || '').replace(/^(PO-|INV-|SPH-)/i, '').trim().toLowerCase();
                   const realPO = productionOrders.find(p => 
                     p.id === invoice.id ||
                     p.order_number === invoice.id || 
                     (p.notes && p.notes.includes(invoice.id)) || 
                     (p.order_number && String(p.order_number).trim() === String(invoice.id).trim()) ||
                     (p.order_number && invoice.id && normalizeCode(p.order_number) === normalizeCode(invoice.id))
                   );
                   
                   const targetOrderNumber = realPO?.order_number || (invoice.id || '').replace(/^INV-/i, 'PO-') || invoice.id;
                   const updatedData = { ...poData, status: 'antrian_produksi', id: realPO?.id, order_number: targetOrderNumber };
                   if (updatedData.products) {
                     updatedData.products = updatedData.products.map(p => ({ ...p, status: 'antrian_produksi' }));
                   }
                   if (updatedData.components) {
                      updatedData.components = updatedData.components.map(c => ({ ...c, status: 'antrian_produksi' }));
                   }
                   
                   const tid = toast.loading('Memproses update...');
                   try {
                     if (!updatedData.id) {
                       await createPOMutation.mutateAsync(updatedData);
                     } else {
                       await updatePOMutation.mutateAsync(updatedData);
                     }
                     await base44.entities.ClientPO.update(invoice.id, { has_pending_po_update: false });
                     queryClient.invalidateQueries({ queryKey: ['client-pos'] });
                     queryClient.invalidateQueries({ queryKey: ['production-orders'] });
                     setPendingAsaPoUpdate(null);
                     toast.success('PO berhasil diupdate dan direset ke Antrian Produksi!', { id: tid });
                   } catch (e) {
                     toast.error('Gagal update PO: ' + e.message, { id: tid });
                   }
                }}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 group-hover:scale-110 transition-transform">
                    <RotateCcw className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-[#302621] text-sm">Mulai Dari Awal</p>
                    <p className="text-[10px] text-slate-500">Reset status seluruh PO ini ke Antrian Produksi</p>
                  </div>
                </div>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* SAMPLE FORM POPUP */}
      {sampleFormData && (
        <SampleForm
          item={sampleFormData}
          onClose={() => setSampleFormData(null)}
          onSaved={() => {
            setSampleFormData(null);
            toast.success('Sample berhasil dibuat!');
          }}
        />
      )}

      {/* Modal Input Komisi */}
      {showKomisiModal && (
        <div className="fixed inset-0 bg-slate-900/40  z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden border border-slate-100">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div>
                <h3 className="font-bold text-base text-slate-800 flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-rose-600" />
                  Input Komisi Invoice
                </h3>
                <p className="text-[10px] text-slate-500 mt-0.5">Komisi menambah HPP dan memotong margin bersih.</p>
              </div>
              <button onClick={() => setShowKomisiModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={saveKomisi}>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Invoice / Order</Label>
                    <Input value={komisiForm.invName} readOnly className="h-9 bg-slate-50 text-xs text-slate-500 cursor-not-allowed" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nama PIC</Label>
                    <Input value={komisiForm.picName} onChange={e => setKomisiForm({...komisiForm, picName: e.target.value})} className="h-9 text-xs" placeholder="Nama PIC" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Sumber Dana (Dari Bank)</Label>
                    <Select value={komisiForm.bankAsal} onValueChange={v => setKomisiForm({...komisiForm, bankAsal: v})}>
                      <SelectTrigger className="h-9 bg-white text-xs">
                        <SelectValue placeholder="Pilih Bank" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {Object.keys(bankAccounts).map(key => (
                          <SelectItem key={key} value={key}>
                            {bankAccounts?.[key]?.name || key.toUpperCase()} ({(bankAccounts?.[key]?.nama_bank || key).toUpperCase()})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nominal Komisi (Rp)</Label>
                    <Input type="number" required value={komisiForm.nominal} onChange={e => setKomisiForm({...komisiForm, nominal: e.target.value})} className="h-9 text-xs font-bold" placeholder="Misal: 500000" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Bank Tujuan (Penerima)</Label>
                  <Input required value={komisiForm.bankTujuan} onChange={e => setKomisiForm({...komisiForm, bankTujuan: e.target.value})} className="h-9 text-xs" placeholder="Misal: BCA / BRI / Mandiri" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Atas Nama (Penerima)</Label>
                  <Input required value={komisiForm.atasNama} onChange={e => setKomisiForm({...komisiForm, atasNama: e.target.value})} className="h-9 text-xs" placeholder="Misal: Ahmad Zaky" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Keterangan / Catatan</Label>
                  <Input value={komisiForm.catatan} onChange={e => setKomisiForm({...komisiForm, catatan: e.target.value})} className="h-9 text-xs" placeholder="Opsional" />
                </div>
              </div>
              <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowKomisiModal(false)} className="h-9 text-xs">Batal</Button>
                <Button type="submit" disabled={isSavingKomisi} className="h-9 text-xs bg-rose-600 hover:bg-rose-700 text-white gap-2">
                  <Banknote className="w-3.5 h-3.5" />
                  {isSavingKomisi ? 'Menyimpan...' : 'Simpan Komisi'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}


      {spkSelectedInvoice && (
        <SpkPrintModal
          isOpen={showSpkModal}
          onClose={() => {
            setShowSpkModal(false);
            setSpkSelectedInvoice(null);
          }}
          invoice={spkSelectedInvoice}
          onPrintMulti={(selectedProducts, showNilai, spkDate) => {
            generateSpkPdf({
              invoiceCode: spkSelectedInvoice.id,
              record: { ...spkSelectedInvoice, spk_date: spkDate },
              products: selectedProducts,
              companySettings: companySettings || {},
              showNilai
            });
          }}
          onPrintSeparate={(selectedProducts, showNilai, spkDate) => {
            selectedProducts.forEach((prod, index) => {
              setTimeout(() => {
                generateSpkPdf({
                  invoiceCode: spkSelectedInvoice.id,
                  record: { ...spkSelectedInvoice, spk_date: spkDate },
                  products: [prod],
                  companySettings: companySettings || {},
                  showNilai
                });
              }, index * 500);
            });
          }}
        />
      )}

      {/* ASA Stock Deduction Preview Modal */}
      {asaStockModal && (
        <AsaStockDeductionModal
          open={!!asaStockModal}
          onOpenChange={(isOpen) => !isOpen && setAsaStockModal(null)}
          title={asaStockModal.title}
          taskName={asaStockModal.taskName}
          poNumber={asaStockModal.poNumber}
          brandName={asaStockModal.brandName}
          activityType={asaStockModal.activityType}
          previewData={asaStockModal.previewData}
          confirmLabel={asaStockModal.confirmLabel || "Konfirmasi & Selesaikan"}
          onConfirm={asaStockModal.onConfirm}
        />
      )}

    </div>
  );
}
